import {
  ActiveModeSchema,
  CharacterIdSchema,
  CreatorPatchSchema,
  MODE_LIMITS,
  PROTOCOL_VERSION,
  SUPPORTED_ARTIFACT_VERSIONS,
  EXT_RULES,
  type ActiveMode,
  type AdminSummary,
  type CatalogEntry,
  type Challenge,
  type ChallengeRanking,
  type CreatorRanking,
  type ErrorCode,
  type MatchResult,
  type Replay,
  type MapView,
  type MapVersionView,
  type MapArtifact,
  type CreatorChallengeView,
  type LibraryEntry,
  type ShareView,
  type Rect,
  type Profile,
  type RoomPhase,
  type RoomSnapshot,
  type RoundResult,
  type SceneProjection,
} from "@urlcade/contracts";
import { getLocale, setLocale, tr } from "../i18n/tr";
import { MultiplayerService, type RoomHandlers } from "../services/multiplayer";
import { LocalSound } from "../services/sound";
import { getBestLocalScore, readLocalScores, saveLocalScore, clearLocalScores, type LocalScore } from "../services/local-scores";
import { readPreferences, writePreferences, type Preferences, type VisualQuality } from "../services/preferences";
import { loadCatalog, loadMap, MapLoadError, type LoadedMap } from "../services/maps";
import { config, multiplayerDisabledMessage } from "../services/config";
import { escapeHtml, formatDate, formatTime, initials } from "./html";
import { icon, runnerArt } from "./icons";
import { GameSession, type GameHud, type LocalRushResult } from "../game/game-session";
import { ConversionApiError, ConversionService, normalizeUserUrl, type ConversionStatusDto } from "../services/conversions";
import { ProductApi, ProductApiError, type DiscoverPage } from "../services/product-api";
import { loadAuthorizedMapVersion, loadAuthorizedRoomMapVersion } from "../services/private-map-assets";
import { characterAssets, characterFor } from "../game/character-assets";

type Screen = "home" | "maps" | "create" | "join" | "lobby" | "game" | "local_result" | "match_result" | "settings" | "history" | "challenge" | "creator_challenge" | "challenge_create" | "practice_starting" | "build" | "map_detail" | "map_edit" | "library" | "discover" | "profile" | "admin";
type LoadState = "idle" | "loading" | "ready" | "error";

interface AppState {
  screen: Screen;
  maps: CatalogEntry[];
  mapsLoad: LoadState;
  mapsError: string;
  selectedHash: string;
  selectedMode: ActiveMode;
  createMode: ActiveMode;
  createMapHash: string;
  loadedMap: LoadedMap | null;
  assetLoad: LoadState;
  assetError: string;
  busy: boolean;
  formError: string;
  gameActionFeedback: string;
  notice: string;
  roomCode: string;
  snapshot: RoomSnapshot | null;
  latestScene: SceneProjection | null;
  roomPhase: RoomPhase | null;
  roundResult: RoundResult | null;
  matchResult: MatchResult | null;
  connectionLost: boolean;
  reconnectMessage: string;
  hintRegions: number[];
  localResult: LocalRushResult | null;
  localVersionId: string;
  localChallengeId: string;
  localChallengeOptionId: string;
  history: MatchResult[];
  historyLoad: LoadState;
  historyError: string;
  challengeId: string;
  challenge: Challenge | null;
  challengeLoad: LoadState;
  challengeError: string;
  challengeRankings: ChallengeRanking[];
  rankingsLoad: LoadState;
  rankingsError: string;
  challengeReplay: Replay | null;
  replayLoad: LoadState;
  replayError: string;
  createChallengeMapHash: string;
  createChallengeOptionId: string;
  pendingChallengeId: string;
  practiceMode: ActiveMode | null;
  practiceRetryHash: string;
  practiceRetryVersionId: string;
  urlDraft: string;
  conversionRequestId: string;
  conversionStatus: ConversionStatusDto | null;
  conversionLoad: LoadState;
  conversionError: string;
  mapView: MapView | null;
  mapViewLoad: LoadState;
  mapViewError: string;
  profile: Profile | null;
  profileLoad: LoadState;
  profileError: string;
  libraryEntries: import("@urlcade/contracts").LibraryEntry[];
  libraryLoad: LoadState;
  libraryError: string;
  discoverPage: DiscoverPage | null;
  discoverLoad: LoadState;
  discoverError: string;
  discoverMode: ActiveMode | "";
  discoverRooms: import("@urlcade/contracts").PublicRoomView[];
  privatePreview: LoadedMap | null;
  previewVersionId: string;
  previewLoad: LoadState;
  previewError: string;
  shareView: ShareView | null;
  inviteToken: string;
  creatorChallenge: CreatorChallengeView | null;
  creatorChallengeId: string;
  creatorChallengeLoad: LoadState;
  creatorChallengeError: string;
  creatorChallengeRankings: CreatorRanking[];
  creatorRankingsLoad: LoadState;
  creatorRankingsError: string;
  creatorShare: ShareView | null;
  creatorVersionId: string;
  creatorSourceBounds: Rect | null;
  creatorPlayerCount: 1 | 2 | 3 | 4;
  ownBestReplay: Replay | null;
  ownBestVisible: boolean;
  ownBestLoad: LoadState;
  ownBestError: string;
  editRegion: Rect | null;
  mapEditMode: ActiveMode;
  libraryFavoritesOnly: boolean;
  invitePending: boolean;
  createMapVersionId: string;
  roomListed: boolean;
  roomTeamSize: 2 | 3;
  adminSummary: AdminSummary | null;
  adminLoad: LoadState;
  adminError: string;
}

const modeTitle = (mode: ActiveMode): string => ({
  scroll_rush: tr.rushLabel,
  site_hide_seek: tr.hideLabel,
  capture_logo: tr.captureLabel,
  page_brawl: tr.brawlLabel,
  browser_boss: tr.bossLabel,
})[mode];
const modeDescription = (mode: ActiveMode): string => ({
  scroll_rush: tr.rushDescription,
  site_hide_seek: tr.hideDescription,
  capture_logo: tr.captureDescription,
  page_brawl: tr.brawlDescription,
  browser_boss: tr.bossDescription,
})[mode];
const modeIcon = (mode: ActiveMode): string => ({
  scroll_rush: "finish",
  site_hide_seek: "hide",
  capture_logo: "logo",
  page_brawl: "battle",
  browser_boss: "boss",
})[mode] as "finish" | "hide" | "logo" | "battle" | "boss";
const modePlayers = (mode: ActiveMode): string => mode === "site_hide_seek" ? tr.hidePlayerCount : mode === "capture_logo" ? tr.capturePlayerCount : mode === "page_brawl" ? tr.brawlPlayerCount : mode === "browser_boss" ? tr.bossPlayerCount : tr.racePlayerCount;
const mapByHash = (maps: CatalogEntry[], hash: string): CatalogEntry | undefined => maps.find((map) => map.artifactHash === hash);
const validModes: readonly ActiveMode[] = ["scroll_rush", "site_hide_seek", "capture_logo", "page_brawl", "browser_boss"];
const adaptationReasonForKind: Readonly<Record<string, string>> = Object.freeze({
  route_support: "SOURCE_ROUTE_SUPPORTED",
  prop_appearance: "SOURCE_PROP_VARIETY_ADAPTED",
  capture_objective: "CAPTURE_OBJECTIVES_ADAPTED",
  brawl_cover: "BRAWL_COVER_ADAPTED",
  boss_objective: "BOSS_OBJECTIVES_ADAPTED",
});
function sameOriginShareUrl(value: string): string {
  const url = new URL(value, window.location.origin);
  if (url.origin !== window.location.origin) throw new Error("SHARE_URL_INVALID");
  if (url.pathname.startsWith("/map/")) url.pathname = url.pathname.replace(/^\/map\//, "/maps/");
  if (!/^\/(?:maps\/[0-9a-f-]{36}|challenge\/[A-Za-z0-9_-]{32,128})$/i.test(url.pathname)) throw new Error("SHARE_URL_INVALID");
  return url.toString();
}
const roomErrorText = (code: ErrorCode, serverMessage: string): string => {
  const messages: Record<ErrorCode, string> = {
    CONFIG_MISSING: tr.multiplayerUnavailable,
    AUTH_INVALID: tr.errorAuth,
    AUTH_EXPIRED: tr.errorAuth,
    RATE_LIMITED: tr.errorRateLimited,
    INVALID_COMMAND: tr.errorGeneric,
    FORBIDDEN: tr.errorForbidden,
    ROOM_NOT_FOUND: tr.errorRoomMissing,
    ROOM_FULL: tr.errorRoomFull,
    ROOM_ACTIVE: tr.errorRoomActive,
    DUPLICATE_SEAT: tr.errorRoomActive,
    NOT_READY: tr.errorNotReady,
    VERSION_MISMATCH: tr.errorVersionMismatch,
    ARTIFACT_MISSING: tr.assetMismatch,
    ASSET_MISSING: tr.assetMismatch,
    PERSISTENCE_FAILED: tr.resultFailed,
    MATCH_ABORTED: tr.errorMatchAborted,
    ROOM_CLOSED: tr.matchDisconnected,
  };
  return serverMessage === code || /^[A-Z_]+$/.test(serverMessage) ? messages[code] : serverMessage;
};

export class App {
  private readonly root: HTMLElement;
  private readonly multiplayer = new MultiplayerService();
  private readonly conversions = new ConversionService(() => this.multiplayer.getBearerToken());
  private readonly productApi = new ProductApi(() => this.multiplayer.getBearerToken());
  private preferences: Preferences = readPreferences();
  private readonly sound = new LocalSound(this.preferences);
  private gameSession: GameSession | null = null;
  private gameHudStatus = "";
  private gameActionFeedbackTimer: number | null = null;
  private readonly state: AppState = {
    screen: "home",
    maps: [],
    mapsLoad: "idle",
    mapsError: "",
    selectedHash: "",
    selectedMode: "scroll_rush",
    createMode: "scroll_rush",
    createMapHash: "",
    loadedMap: null,
    assetLoad: "idle",
    assetError: "",
    busy: false,
    formError: "",
    gameActionFeedback: "",
    notice: "",
    roomCode: "",
    snapshot: null,
    latestScene: null,
    roomPhase: null,
    roundResult: null,
    matchResult: null,
    connectionLost: false,
    reconnectMessage: "",
    hintRegions: [],
    localResult: null,
    localVersionId: "",
    localChallengeId: "",
    localChallengeOptionId: "",
    history: [],
    historyLoad: "idle",
    historyError: "",
    challengeId: "",
    challenge: null,
    challengeLoad: "idle",
    challengeError: "",
    challengeRankings: [],
    rankingsLoad: "idle",
    rankingsError: "",
    challengeReplay: null,
    replayLoad: "idle",
    replayError: "",
    createChallengeMapHash: "",
    createChallengeOptionId: "",
    pendingChallengeId: "",
    practiceMode: null,
    practiceRetryHash: "",
    practiceRetryVersionId: "",
    urlDraft: "",
    conversionRequestId: "",
    conversionStatus: null,
    conversionLoad: "idle",
    conversionError: "",
    mapView: null,
    mapViewLoad: "idle",
    mapViewError: "",
    profile: null,
    profileLoad: "idle",
    profileError: "",
    libraryEntries: [],
    libraryLoad: "idle",
    libraryError: "",
    discoverPage: null,
    discoverLoad: "idle",
    discoverError: "",
    discoverMode: "",
    discoverRooms: [],
    privatePreview: null,
    previewVersionId: "",
    previewLoad: "idle",
    previewError: "",
    shareView: null,
    inviteToken: "",
    creatorChallenge: null,
    creatorChallengeId: "",
    creatorChallengeLoad: "idle",
    creatorChallengeError: "",
    creatorChallengeRankings: [],
    creatorRankingsLoad: "idle",
    creatorRankingsError: "",
    creatorShare: null,
    creatorVersionId: "",
    creatorSourceBounds: null,
    creatorPlayerCount: 1,
    ownBestReplay: null,
    ownBestVisible: false,
    ownBestLoad: "idle",
    ownBestError: "",
    editRegion: null,
    mapEditMode: "scroll_rush",
    libraryFavoritesOnly: false,
    invitePending: false,
    createMapVersionId: "",
    roomListed: false,
    roomTeamSize: 2,
    adminSummary: null,
    adminLoad: "idle",
    adminError: "",
  };
  private renderedScreen: Screen | null = null;
  private lobbyMarkup = "";
  private resultMarkup = "";
  private lobbyInteractionScope = "";
  private lobbyInteractionTimer = 0;
  private lobbyPatchPending = false;
  private localTimeHandle = 0;
  private assetLoadKey = "";
  private roomAssetAbort: AbortController | null = null;
  private readySentHash = "";
  private localScoreSaved = false;
  private ownPlayerId = "";
  private hintTimeout = 0;
  private mapLoadRequest = 0;
  private challengeLoadRequest = 0;
  private rankingsRequest = 0;
  private replayRequest = 0;
  private ownBestRequest = 0;
  private creatorRankingsRequest = 0;
  private localVersionAbort: AbortController | null = null;
  private localVersionRequest = 0;
  private localeDirty = false;
  private roomRequestId = 0;
  private roomRequestAbort: AbortController | null = null;
  private mapLoadAbort: AbortController | null = null;
  private practiceReadyKey = "";
  private practiceStartKey = "";
  private conversionGeneration = 0;
  private conversionPollHandle = 0;
  private conversionSourceDraft = "";
  private privateAssetAbort: AbortController | null = null;
  private privateAssetGeneration = 0;
  private shareRedeemGeneration = 0;
  private profileDeleteBusy = false;
  private profileDeleteError = "";
  private accountGeneration = 0;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.addEventListener("pointerdown", (event) => this.beginLobbyInteraction(event));
    window.addEventListener("pointerup", () => this.finishLobbyInteraction());
    window.addEventListener("pointercancel", () => this.finishLobbyInteraction());
    this.root.addEventListener("keydown", (event) => {
      if (event.key === " " || event.key === "Enter") this.beginLobbyInteraction(event);
    });
    window.addEventListener("keyup", (event) => {
      if (event.key === " " || event.key === "Enter") this.finishLobbyInteraction();
    });
    window.addEventListener("blur", () => this.finishLobbyInteraction());
    this.root.addEventListener("click", (event) => this.handleClick(event));
    this.root.addEventListener("submit", (event) => this.handleSubmit(event));
    this.root.addEventListener("change", (event) => this.handleChange(event));
    this.root.addEventListener("input", (event) => this.handleInput(event));
    window.addEventListener("popstate", () => this.handleLocation());
    window.addEventListener("keydown", (event) => this.handleKeydown(event));
  }

  start(): void {
    setLocale(getLocale());
    this.handleLocation();
    this.render();
    void this.loadProfile(true);
    void this.refreshCatalog();
  }

  private handleLocation(): void {
    this.cancelPendingRoomRequest();
    this.cancelLocalVersionLoad();
    const url = new URL(window.location.href);
    const parts = url.pathname.split("/").filter(Boolean).map((part) => decodeURIComponent(part));
    if (parts[0] === "room" && parts[1]) {
      this.state.inviteToken = parts[1];
      this.state.roomCode = "";
      this.state.formError = "";
      this.state.screen = "join";
      this.render();
      return;
    }
    if (parts[0] === "creator" && parts[1] === "challenges" && parts[2]) {
      this.state.screen = "creator_challenge";
      this.render();
      void this.loadCreatorChallenge(parts[2]);
      return;
    }
    if (parts[0] === "challenge" && parts[1]) {
      this.state.screen = "creator_challenge";
      this.render();
      const share = url.searchParams.get("share");
      if (share) void this.resolveChallengeShare(share);
      else if (/^[0-9a-f-]{36}$/i.test(parts[1])) void this.loadCreatorChallenge(parts[1]);
      else void this.resolveChallengeShare(parts[1]);
      return;
    }
    if (parts[0] === "play" && parts[1]) {
      this.state.screen = this.multiplayer.currentConnection ? "game" : "join";
      this.state.formError = this.multiplayer.currentConnection ? "" : tr.resumeRoomRequired;
      this.render();
      return;
    }
    if (parts[0] === "build" && parts[1]) {
      this.state.screen = "build";
      if (this.state.conversionRequestId !== parts[1]) this.state.conversionStatus = null;
      this.state.conversionRequestId = parts[1];
      this.state.conversionLoad = "loading";
      this.state.conversionError = "";
      this.render();
      void this.refreshConversion(parts[1]);
      return;
    }
    if (parts[0] === "maps" && parts[1]) {
      this.state.screen = parts[2] === "edit" ? "map_edit" : "map_detail";
      this.state.mapView = this.state.mapView?.id === parts[1] ? this.state.mapView : null;
      this.state.mapViewLoad = this.state.mapView ? "ready" : "loading";
      if (parts[2] === "edit" && this.state.mapView) {
        this.state.mapEditMode = this.state.mapView.versions[0]?.mode ?? "scroll_rush";
        this.state.editRegion = null;
      }
      this.render();
      const shareToken = url.searchParams.get("share");
      if (shareToken) void this.resolveMapShare(parts[1], shareToken);
      else if (!this.state.mapView) void this.loadMapView(parts[1]);
      return;
    }
    if (parts[0] === "library") { this.state.screen = "library"; this.render(); if (this.state.libraryLoad === "idle") void this.loadLibrary(); return; }
    if (parts[0] === "discover") { this.state.screen = "discover"; this.render(); if (this.state.discoverLoad === "idle") void this.loadDiscover(); return; }
    if (parts[0] === "profile") { if (this.state.screen !== "profile") this.state.formError = ""; this.state.screen = "profile"; this.render(); if (this.state.profileLoad === "idle") void this.loadProfile(true); return; }
    if (parts[0] === "settings") { this.state.screen = "settings"; this.render(); return; }
    if (parts[0] === "admin") { this.state.screen = "admin"; this.render(); if (this.state.adminLoad === "idle") void this.loadAdminSummary(); return; }
    const room = url.searchParams.get("room") ?? url.searchParams.get("code");
    if (room) {
      this.state.challengeId = url.searchParams.get("challenge") ?? this.state.challengeId;
      this.state.formError = "";
      this.state.roomCode = room.trim().toUpperCase();
      this.state.screen = "join";
      this.render();
      return;
    }
    const challengeId = url.searchParams.get("challenge") ?? "";
    if (challengeId) {
      this.state.challengeId = challengeId;
      this.state.screen = "challenge";
      this.render();
      if (this.state.mapsLoad === "ready") void this.loadChallenge(challengeId);
      return;
    }
    if (url.pathname === "/") {
      if (this.state.screen !== "home") { this.state.screen = "home"; this.state.challengeId = ""; this.render(); }
      return;
    }
    if (parts.length === 0) { this.state.screen = "home"; this.render(); return; }
    if (this.state.screen === "join" || this.state.screen === "challenge") {
      this.state.screen = "home";
      this.state.challengeId = "";
      this.render();
    }
  }

  private render(): void {
    document.body.dataset.urlcadeScreen = this.state.screen;
    window.dispatchEvent(new Event("urlcade:screenchange"));
    if (this.renderedScreen === "game" && this.state.screen === "game") return;
    if (this.gameSession && this.state.screen !== "game") {
      this.gameSession.destroy();
      this.gameSession = null;
    }
    this.clearLobbyInteraction();
    this.lobbyMarkup = "";
    this.resultMarkup = "";
    this.renderedScreen = this.state.screen;
    const screenMarkup = this.renderScreen();
    if (this.state.screen === "lobby") this.lobbyMarkup = screenMarkup;
    if (this.state.screen === "match_result") this.resultMarkup = screenMarkup;
    const soundLabel = this.preferences.soundEnabled ? tr.soundMute : tr.soundEnable;
    this.root.innerHTML = `
      <div class="app-shell">
        <header class="site-header">
          <a class="brand" href="/" data-action="home" aria-label="${tr.brandHomeLabel}"><img class="brand-wordmark" src="/brand/urlcade-wordmark.png" alt="" /></a>
          <nav class="header-nav" aria-label="${tr.primaryNavigation}">
            <a href="/" data-action="home">${tr.homeNav}</a>
            <a href="/library" data-action="library">${tr.library}</a>
            <a href="/discover" data-action="discover">${tr.discover}</a>
            <a href="/profile" data-action="profile">${tr.profile}</a>
          </nav>
          <div class="header-tools">
            <label class="visually-hidden" for="header-locale">${tr.interfaceLanguage}</label>
            <select id="header-locale" class="header-locale" data-control="locale" aria-label="${tr.interfaceLanguage}"><option value="tr-TR" ${getLocale() === "tr-TR" ? "selected" : ""}>TR</option><option value="en-US" ${getLocale() === "en-US" ? "selected" : ""}>EN</option></select>
            <button class="icon-button" type="button" data-action="toggle-sound" aria-label="${soundLabel}" title="${soundLabel}">${icon(this.preferences.soundEnabled ? "sound" : "soundOff")}</button>
            <button class="icon-button" type="button" data-action="settings" aria-label="${tr.settings}" title="${tr.settings}">${icon("settings")}</button>
          </div>
        </header>
        <main class="main-content" id="main-content">${screenMarkup}</main>
        <footer class="site-footer"><span>© ${new Date().getFullYear()} URLCADE · ${escapeHtml(tr.tagline)}</span><a href="#settings" data-action="settings">${tr.settings}</a></footer>
        <div class="visually-hidden" id="app-announcer" role="status" aria-live="polite" aria-atomic="true"></div>
      </div>`;
    if (this.state.screen === "game") this.mountGameSession();
    if (this.state.screen === "history" && this.state.historyLoad === "idle") void this.loadHistory();
  }

  private renderScreen(): string {
    switch (this.state.screen) {
      case "home": return this.renderHome();
      case "maps": return this.renderMapSelection();
      case "create": return this.renderCreateForm();
      case "join": return this.renderJoinForm();
      case "lobby": return this.renderLobby();
      case "game": return this.renderGame();
      case "local_result": return this.renderLocalResult();
      case "match_result": return this.renderMatchResult();
      case "settings": return this.renderSettings();
      case "history": return this.renderHistory();
      case "challenge": return this.renderChallenge();
      case "creator_challenge": return this.renderCreatorChallenge();
      case "challenge_create": return this.renderChallengeCreate();
      case "practice_starting": return this.renderPracticeStarting();
      case "build": return this.renderBuildStatus();
      case "map_detail": return this.renderMapWorkspace();
      case "map_edit": return this.renderMapEdit();
      case "library": return this.renderLibrary();
      case "discover": return this.renderDiscover();
      case "profile": return this.renderProfile();
      case "admin": return this.renderAdmin();
      default: return this.renderHome();
    }
  }

  private renderBuildStatus(): string {
    const status = this.state.conversionStatus;
    const phase = status?.phase;
    const terminal = status ? ["ready", "rejected", "failed", "cancelled", "expired"].includes(status.phase) : false;
    const canCancel = Boolean(status && !terminal);
    const canRetry = Boolean(status?.error?.retryable && ["failed", "rejected"].includes(status.phase));
    return `<section class="panel-screen build-screen" aria-labelledby="build-title">
      <a class="back-link" href="/" data-action="home">${icon("arrowLeft")}${tr.homeNav}</a>
      <div class="panel-title-row"><div><span class="eyebrow">${tr.pageSnapshot}</span><h1 id="build-title">${tr.conversionTitle}</h1><p>${tr.conversionPrivateNotice}</p></div></div>
      <section class="surface conversion-progress" aria-live="polite">
        <div class="conversion-progress-heading"><span class="conversion-mark">${phase && phase !== "ready" && !terminal ? `<i class="spinner" aria-hidden="true"></i>` : icon(phase === "ready" ? "check" : "shield")}</span><div><h2>${status ? this.conversionPhaseLabel(phase ?? "queued") : tr.conversionLoading}</h2><p>${status?.displayUrl ? escapeHtml(status.displayUrl) : tr.conversionNoSourceLeak}</p></div></div>
        ${status?.progress ? `<p class="conversion-count">${tr.conversionProgress}: ${status.progress.completed} / ${status.progress.total}</p>` : ""}
        ${this.state.conversionLoad === "loading" && !status ? `<p class="status-live" role="status"><span class="loading-indicator"><i class="spinner"></i>${tr.conversionLoading}</span></p>` : ""}
        ${this.state.conversionLoad === "error" || this.state.conversionError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.conversionError || tr.conversionLoadFailed)}</p>` : ""}
        ${status?.error ? `<p class="inline-error" role="alert">${escapeHtml(this.conversionErrorText(status.error.code))}</p>` : ""}
        ${phase === "ready" && status?.mapId ? `<div class="conversion-ready"><p>${tr.conversionReadyCopy}</p><button class="button button-primary" type="button" data-action="open-converted-map" data-map-id="${escapeHtml(status.mapId)}">${icon("play")}${tr.openGameCopy}</button></div>` : ""}
        <div class="result-actions">${canCancel ? `<button class="button button-danger" type="button" data-action="cancel-conversion" ${this.state.busy ? "disabled" : ""}>${tr.cancelConversion}</button>` : ""}${canRetry ? `<button class="button button-secondary" type="button" data-action="retry-conversion" ${this.state.busy ? "disabled" : ""}>${tr.retryConversion}</button>` : ""}<button class="button button-quiet" type="button" data-action="reload-conversion" ${this.state.conversionLoad === "loading" ? "disabled" : ""}>${tr.refreshStatus}</button></div>
      </section>
    </section>`;
  }

  private renderMapDetail(): string {
    if (this.state.mapViewLoad === "loading") return `<section class="panel-screen" aria-labelledby="map-detail-title"><a class="back-link" href="/library" data-action="library">${icon("arrowLeft")}${tr.library}</a><h1 id="map-detail-title">${tr.mapDetails}</h1><p class="status-live" role="status"><i class="spinner"></i>${tr.authorizedMapLoading}</p></section>`;
    return `<section class="panel-screen" aria-labelledby="map-detail-title"><a class="back-link" href="/library" data-action="library">${icon("arrowLeft")}${tr.library}</a><h1 id="map-detail-title">${tr.mapDetails}</h1><p class="inline-error" role="alert">${escapeHtml(this.state.mapViewError || tr.mapUnavailable)}</p><button class="button button-secondary" type="button" data-action="retry-map-view">${tr.retry}</button></section>`;
  }

  private suitabilityLabel(status: "supported" | "adapted" | "unavailable"): string { return status === "supported" ? tr.suitabilitySupported : status === "adapted" ? tr.suitabilityAdapted : tr.suitabilityUnavailable; }
  private suitabilityReason(reason: string): string | undefined {
    const translated = tr[`reason_${reason}` as keyof typeof tr];
    return typeof translated === "string" ? translated : undefined;
  }
  private visibilityLabel(visibility: "private" | "unlisted" | "public"): string { return visibility === "public" ? tr.visibilityPublic : visibility === "unlisted" ? tr.visibilityUnlisted : tr.visibilityPrivate; }

  private modeVersionForSelection(map: MapView, mode: ActiveMode): MapVersionView | undefined {
    return map.versions
      .filter((version) => version.mode === mode && version.protocolVersion === PROTOCOL_VERSION && Boolean(version.rulesHash))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))[0];
  }

  private mapModeIsPlayable(map: MapView, mode: ActiveMode): boolean {
    const suitability = map.suitability.find((item) => item.mode === mode);
    return Boolean(suitability && suitability.status !== "unavailable" && suitability.availablePlayerCounts.length > 0 &&
      this.modeVersionForSelection(map, mode));
  }

  private firstPlayableMapMode(map: MapView): ActiveMode | undefined {
    const preference = [this.state.selectedMode, "scroll_rush", ...validModes] as ActiveMode[];
    return preference.find((mode, index) => preference.indexOf(mode) === index && this.mapModeIsPlayable(map, mode));
  }

  private playerCountsLabel(counts: number[], mode?: ActiveMode): string {
    if (!counts.length) return "";
    // Legacy Hide artifacts retain their signed four-player capacity metadata.
    // Admission now supports fewer hiders without changing geometry or artifact bytes.
    if (mode === "site_hide_seek" && counts.includes(4)) counts = [2, 3, 4];
    const ordered = [...new Set(counts)].sort((left, right) => left - right);
    const continuous = ordered.every((count, index) => index === 0 || count === ordered[index - 1]! + 1);
    const countText = continuous && ordered.length > 1 ? `${ordered[0]}–${ordered.at(-1)}` : ordered.join(", ");
    return `${countText} ${ordered.length === 1 && ordered[0] === 1 ? tr.playerSingle : tr.players}`;
  }

  private modeRequirementReason(mode: ActiveMode): string {
    return ({
      scroll_rush: tr.modeReasonRush,
      site_hide_seek: tr.modeReasonHide,
      capture_logo: tr.modeReasonCapture,
      page_brawl: tr.modeReasonBrawl,
      browser_boss: tr.modeReasonBoss,
    })[mode];
  }

  private modeSuitabilityExplanation(
    mode: ActiveMode,
    suitability: MapView["suitability"][number],
    version: MapVersionView | undefined,
  ): string[] {
    const artifact = version ? this.privateArtifactForVersion(version) : undefined;
    if (artifact?.schemaVersion === 4 && suitability.status !== "unavailable" && artifact.adaptationClass === "native") return [tr.modeNativeFallback];
    const exactAdaptationSummary = artifact?.schemaVersion === 4 && artifact.adaptationClass === "adapted"
      ? artifact.adaptations.filter((adaptation) => adaptationReasonForKind[adaptation.kind] === adaptation.reason).map((adaptation) => adaptation.reason)
      : suitability.adaptationSummary;
    const codeList = artifact?.schemaVersion === 4
      ? [...new Set(exactAdaptationSummary)]
      : [...new Set([...suitability.reasons, ...exactAdaptationSummary])];
    const translated = [...new Set(codeList.map((code) => this.suitabilityReason(code)).filter((message): message is string => Boolean(message)))];
    if (suitability.status === "unavailable") return translated.length ? translated.slice(0, 2) : [this.modeRequirementReason(mode)];
    if (!version) return [tr.modeVersionUnavailable];
    if (suitability.status === "adapted") return translated.length ? translated.slice(0, 2) : [tr.modeAdaptedFallback];
    return translated.slice(0, 1);
  }

  private privateArtifactForVersion(version: MapVersionView): MapArtifact | undefined {
    const artifact = this.state.privatePreview?.artifact;
    return this.state.previewVersionId === version.id && artifact?.artifactHash === version.artifactHash ? artifact : undefined;
  }

  private suitabilityForVersion(
    suitability: MapView["suitability"][number],
    version: MapVersionView | undefined,
  ): MapView["suitability"][number] {
    const artifact = version ? this.privateArtifactForVersion(version) : undefined;
    if (!artifact || artifact.schemaVersion !== 4 || suitability.status === "unavailable") return suitability;
    const adapted = artifact.adaptationClass === "adapted";
    return {
      ...suitability,
      status: adapted ? "adapted" : "supported",
      adaptationSummary: adapted
        ? [...new Set(artifact.adaptations.filter((entry) => adaptationReasonForKind[entry.kind] === entry.reason).map((entry) => entry.reason))]
        : [],
    };
  }

  private renderModeIllustration(mode: ActiveMode): string {
    const player = mode === "site_hide_seek" ? "scene-avatar-ghost"
      : mode === "page_brawl" ? "scene-avatar-bot"
      : mode === "browser_boss" ? "scene-avatar-buddy"
      : "scene-avatar-runner";
    return `<span class="mode-scene mode-scene-${mode}" aria-hidden="true">
      <span class="scene-browser-bar"><i></i><i></i><i></i><b></b></span>
      <span class="scene-ground"></span>
      <span class="scene-platform scene-platform-a"></span><span class="scene-platform scene-platform-b"></span><span class="scene-platform scene-platform-c"></span><span class="scene-platform scene-platform-d"></span>
      <span class="scene-prop scene-prop-a"></span><span class="scene-prop scene-prop-b"></span><span class="scene-prop scene-prop-c"></span>
      <span class="scene-team scene-team-red"></span><span class="scene-team scene-team-blue"></span>
      <span class="scene-breakable scene-breakable-a"></span><span class="scene-breakable scene-breakable-b"></span>
      <span class="scene-boss"></span><span class="scene-key scene-key-a"></span><span class="scene-key scene-key-b"></span><span class="scene-key scene-key-c"></span>
      <span class="scene-goal">${icon(modeIcon(mode))}</span><span class="scene-avatar ${player}"></span>
    </span>`;
  }

  private renderModeChoice(map: MapView, mode: ActiveMode, selectedMode: ActiveMode | undefined): string {
    const reportedSuitability = map.suitability.find((item) => item.mode === mode);
    if (!reportedSuitability) return "";
    const version = this.modeVersionForSelection(map, mode);
    const suitability = this.suitabilityForVersion(reportedSuitability, version);
    const selectable = reportedSuitability.status !== "unavailable" && reportedSuitability.availablePlayerCounts.length > 0 && Boolean(version);
    const shownStatus = selectable ? suitability.status : "unavailable";
    const selected = selectable && selectedMode === mode;
    const reason = this.modeSuitabilityExplanation(mode, suitability, version);
    const descriptionId = `mode-description-${mode}`;
    const reasonId = `mode-reason-${mode}`;
    const playerLine = selectable
      ? `<span class="mode-card-meta">${icon("users")}<span>${this.playerCountsLabel(suitability.availablePlayerCounts, suitability.mode)}</span></span>`
      : `<span class="mode-card-meta mode-card-meta-unavailable">${icon("shield")}<span>${tr.modeUnavailableHint}</span></span>`;
    const cardContents = `${this.renderModeIllustration(mode)}<span class="mode-card-body">
      <span class="mode-card-heading"><strong>${modeTitle(mode)}</strong><span class="mode-card-status mode-card-status-${shownStatus}">${icon(shownStatus === "unavailable" ? "close" : "check")}${this.suitabilityLabel(shownStatus)}</span></span>
      <span class="mode-card-description" id="${descriptionId}">${modeDescription(mode)}</span>
      ${playerLine}
      ${reason.length ? `<span class="mode-card-reason" id="${reasonId}">${escapeHtml(reason.join(" "))}</span>` : ""}
    </span>`;
    if (!selectable) return `<article class="mode-choice-card mode-choice-disabled">${cardContents}</article>`;
    return `<button class="mode-choice-card ${selected ? "is-selected" : ""}" type="button" data-action="select-map-mode" data-mode="${mode}" aria-pressed="${selected}" aria-describedby="${descriptionId}${reason.length ? " " + reasonId : ""}">${cardContents}</button>`;
  }

  private renderSelectedModePanel(map: MapView, mode: ActiveMode | undefined): string {
    if (!mode) {
      return `<section class="selected-mode-panel surface" aria-labelledby="selected-mode-title"><span class="eyebrow">${tr.modePreviewTitle}</span><h2 id="selected-mode-title">${tr.noPlayableMapMode}</h2><p class="selected-mode-empty-copy">${tr.noPlayableMapModeDescription}</p></section>`;
    }
    const reportedSuitability = map.suitability.find((item) => item.mode === mode);
    const version = this.modeVersionForSelection(map, mode);
    if (!reportedSuitability || !version) return "";
    const suitability = this.suitabilityForVersion(reportedSuitability, version);
    const previewReady = this.state.previewLoad === "ready" && this.state.previewVersionId === version.id &&
      this.state.privatePreview?.artifact.artifactHash === version.artifactHash;
    const previewLoading = this.state.previewLoad === "loading" && this.state.previewVersionId === version.id;
    const previewError = this.state.previewLoad === "error" && this.state.previewVersionId === version.id;
    const bestLocal = mode === "scroll_rush" && previewReady ? getBestLocalScore(this.state.privatePreview!.artifact) : null;
    const playerLabel = this.playerCountsLabel(suitability.availablePlayerCounts, suitability.mode);
    const explanation = this.modeSuitabilityExplanation(mode, suitability, version);
    const exactArtifact = this.privateArtifactForVersion(version);
    const modeNote = suitability.status === "adapted" && explanation.length
      ? `<p class="mode-adaptation-note">${icon("spark")}${escapeHtml(explanation.join(" "))}</p>`
      : exactArtifact?.schemaVersion === 4 && exactArtifact.adaptationClass === "native"
        ? `<p class="mode-adaptation-note">${icon("shield")}${escapeHtml(tr.modeNativeFallback)}</p>`
        : "";
    const previewContent = previewReady
      ? this.renderArtifactPreview(this.state.privatePreview!)
      : previewLoading
        ? `<div class="mode-preview-state" role="status"><span class="loading-indicator"><i class="spinner"></i>${tr.authorizedMapLoading}</span></div>`
        : previewError
          ? `<div class="mode-preview-state"><p class="inline-error" role="alert">${escapeHtml(this.state.previewError || tr.previewLoadFailed)}</p><button class="button button-small button-secondary" type="button" data-action="load-private-preview" data-version-id="${escapeHtml(version.id)}">${tr.modePreviewRetry}</button></div>`
          : `<div class="mode-preview-state"><p>${tr.previewPrivateNotice}</p><button class="button button-small button-secondary" type="button" data-action="load-private-preview" data-version-id="${escapeHtml(version.id)}">${tr.modePreviewRetry}</button></div>`;
    const actionDisabled = previewReady && !this.state.busy ? "" : "disabled";
    const rushAction = mode === "scroll_rush"
      ? `<button class="button button-primary" type="button" data-action="start-local-version-rush" data-version-id="${escapeHtml(version.id)}" ${actionDisabled}>${icon("play")}${tr.playSoloRush}</button>`
      : `<button class="button button-primary" type="button" data-action="start-version-practice" data-version-id="${escapeHtml(version.id)}" ${this.multiplayer.enabled ? actionDisabled : "disabled"}>${icon("play")}${tr.practiceStart}</button>`;
    const roomAction = `<button class="button button-secondary" type="button" data-action="create-version-room" data-version-id="${escapeHtml(version.id)}" ${this.multiplayer.enabled ? actionDisabled : "disabled"}>${icon("users")}${tr.createRoom}</button>`;
    const serviceNote = !this.multiplayer.enabled
      ? `<p class="mode-online-note">${tr.modeOnlineUnavailable}</p>`
      : "";
    return `<section class="selected-mode-panel surface" aria-labelledby="selected-mode-title">
      <div class="selected-mode-heading"><div><span class="eyebrow">${tr.modePreviewTitle}</span><h2 id="selected-mode-title">${modeTitle(mode)}</h2><p>${modeDescription(mode)}</p></div><span class="selected-mode-status mode-card-status mode-card-status-${suitability.status}">${icon(suitability.status === "unavailable" ? "close" : "check")}${this.suitabilityLabel(suitability.status)}</span></div>
      <div class="selected-mode-facts"><span>${tr.playerCount}<strong>${playerLabel}</strong></span><span>${tr.selectedMapVersion}<strong>${escapeHtml(version.artifactHash.slice(0, 12))}</strong></span></div>
      <div class="selected-mode-preview">${previewContent}</div>
      ${modeNote}
      <div class="selected-mode-actions">${rushAction}${roomAction}</div>
      ${bestLocal ? `<p class="mode-best-score">${tr.localBest}: <strong>${formatTime(bestLocal.elapsedMs + bestLocal.penaltyMs)}</strong></p>` : ""}
      ${serviceNote}
      <p class="hero-footnote">${icon("shield")}${tr.privateMapAssets}</p>
    </section>`;
  }

  private renderMapWorkspace(): string {
    const map = this.state.mapView;
    if (!map) return this.renderMapDetail();
    const selectedMode = this.firstPlayableMapMode(map);
    const sharePanel = this.state.shareView?.mapId === map.id ? `<section class="surface share-link-panel"><label for="map-share-url">${tr.shareLink}</label><input id="map-share-url" readonly value="${escapeHtml(this.state.shareView.url)}" /><span>${tr.shareExpires}: ${escapeHtml(this.state.shareView.expiresAt)}</span><button class="button button-small button-secondary" type="button" data-action="copy-map-share">${tr.copy}</button><button class="button button-small button-quiet" type="button" data-action="revoke-map-share" data-share-id="${escapeHtml(this.state.shareView.id)}">${tr.revokeShare}</button></section>` : "";
    const canPublish = this.state.profile?.canPublish === true && map.owner && map.visibility !== "public" && map.status === "ready";
    const modeCards = validModes.map((mode) => this.renderModeChoice(map, mode, selectedMode)).join("");
    const sourceSectionNotice = map.provenance.warnings.includes("SOURCE_SECTION_SELECTED")
      ? `<p class="inline-note">${escapeHtml(tr.sourceSectionSelected.replace("{height}", new Intl.NumberFormat(getLocale()).format(map.provenance.selectedRegion.height)))}</p>`
      : "";
    const versionHistory = map.versions.length
      ? map.versions.slice().sort((left, right) => right.createdAt.localeCompare(left.createdAt)).map((version) =>
        `<li class="map-version-history-row"><span>${modeTitle(version.mode)} <code>${escapeHtml(version.artifactHash.slice(0, 12))}</code></span><time datetime="${escapeHtml(version.createdAt)}">${escapeHtml(version.createdAt.slice(0, 10))}</time></li>`).join("")
      : `<li class="map-version-history-empty">${tr.noMapVersion}</li>`;
    return `<div class="map-workspace">
      <a class="back-link map-workspace-back" href="/library" data-action="library">${icon("arrowLeft")}${tr.library}</a>
      <header class="map-source-header">
        <span class="eyebrow">${escapeHtml(map.provenance.sourceHost)}</span>
        <h1 id="map-detail-title">${escapeHtml(map.title)}</h1>
        <p class="map-source-url"><span>${icon("location")}${tr.sourcePage}</span><code>${escapeHtml(map.provenance.displayUrl)}</code></p>
        ${sourceSectionNotice}
      </header>
      ${this.state.mapViewError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.mapViewError)}</p>` : ""}
      ${this.state.notice ? `<p class="inline-note" role="status">${escapeHtml(this.state.notice)}</p>` : ""}
      <div class="map-mode-workspace">
        <section class="map-mode-picker" aria-labelledby="mode-picker-title">
          <div class="map-mode-heading"><div><span class="eyebrow">${tr.pageSnapshot}</span><h2 id="mode-picker-title">${tr.modeChooseHeading}</h2></div><p>${tr.modeChooseDescription}</p></div>
          <div class="mode-choice-grid">${modeCards}</div>
        </section>
        ${this.renderSelectedModePanel(map, selectedMode)}
      </div>
      <details class="map-owner-tools" id="map-owner-tools" ${sharePanel || this.state.mapViewError ? "open" : ""}>
        <summary>${tr.mapToolsSummary}</summary>
        <div class="map-owner-tools-content">
          <p class="map-tools-meta">${tr.capturedAt}: ${escapeHtml(new Date(map.provenance.capturedAt).toLocaleString())} · ${tr.mapVisibility}: ${this.visibilityLabel(map.visibility)} · ${tr.snapshotImmutable} · ${escapeHtml(map.snapshotId.slice(0, 8))}</p>
          <div class="map-owner-actions">
            <button class="button button-small button-secondary" type="button" data-action="library-save-map" data-map-id="${escapeHtml(map.id)}">${map.favorite ? tr.savedToLibrary : tr.saveToLibrary}</button>
            <button class="button button-small button-secondary" type="button" data-action="create-share" data-map-id="${escapeHtml(map.id)}">${tr.sharePrivateMap}</button>
            ${canPublish ? `<button class="button button-small button-secondary" type="button" data-action="publish-map" data-map-id="${escapeHtml(map.id)}">${tr.requestPublication}</button>` : ""}
            <button class="button button-small button-quiet" type="button" data-action="recapture-map" data-map-id="${escapeHtml(map.id)}">${tr.recapturePage}</button>
            <a class="button button-small button-quiet" href="/maps/${encodeURIComponent(map.id)}/edit" data-action="map-edit">${tr.editMap}</a>
          </div>
          <section class="map-version-history" aria-labelledby="map-version-history-title"><h2 id="map-version-history-title">${tr.mapToolsVersionHistory}</h2><ul>${versionHistory}</ul></section>
          ${sharePanel}
          <section class="map-report-section"><h2>${tr.reportContent}</h2>${this.renderReportForm(map.id)}</section>
        </div>
      </details>
    </div>`;
  }

  private renderArtifactPreview(loaded: LoadedMap): string {
    const artifact = loaded.artifact;
    const focus = { x: 0, y: 0, width: artifact.world.width, height: Math.min(artifact.world.height, 900) };
    const scale = (bounds: Rect): string => `left:${((bounds.x - focus.x) / focus.width) * 100}%;top:${((bounds.y - focus.y) / focus.height) * 100}%;width:${(bounds.width / focus.width) * 100}%;height:${(bounds.height / focus.height) * 100}%`;
    const tiles = artifact.backgroundTiles.map((tile) => {
      const src = loaded.assetUrls.get(tile.assetId);
      return src ? `<img class="artifact-preview-layer" src="${escapeHtml(src)}" alt="" draggable="false" style="${scale(tile.bounds)}" />` : "";
    }).join("");
    const objects = (artifact.visualLayers ?? []).map((layer) => {
      const src = loaded.assetUrls.get(layer.assetId);
      return src ? `<img class="artifact-preview-layer artifact-preview-object" src="${escapeHtml(src)}" alt="" draggable="false" style="${scale(layer.bounds)}" />` : "";
    }).join("");
    const aspect = focus.width / focus.height;
    return `<figure class="artifact-preview-figure"><div class="artifact-preview" role="img" aria-label="${tr.pageSnapshotPreview}" style="--preview-aspect:${aspect}">${tiles}${objects}</div><figcaption>${escapeHtml(artifact.title)} · ${escapeHtml(artifact.artifactHash.slice(0, 12))} · ${tr.snapshotImmutable}</figcaption></figure>`;
  }

  private renderMapEdit(): string {
    const map = this.state.mapView;
    if (this.state.mapViewLoad === "loading") return `<section class="panel-screen"><p class="status-live" role="status"><i class="spinner"></i>${tr.authorizedMapLoading}</p></section>`;
    if (!map) return `<section class="panel-screen"><a class="back-link" href="/library" data-action="library">${icon("arrowLeft")}${tr.library}</a><h1>${tr.editMap}</h1><p class="inline-error" role="alert">${escapeHtml(this.state.mapViewError || tr.mapUnavailable)}</p></section>`;
    const mode = map.versions.some((version) => version.mode === this.state.mapEditMode) ? this.state.mapEditMode : map.versions[0]?.mode ?? "scroll_rush";
    const version = map.versions.find((entry) => entry.mode === mode);
    const candidatePreview = this.state.privatePreview;
    const loaded = version && candidatePreview && this.state.previewVersionId === version.id && this.creatorArtifactMatchesVersion(version, candidatePreview) ? candidatePreview : null;
    const sourceBounds = map.provenance.selectedRegion;
    const bounds = this.state.editRegion && this.validSourceRegion(this.state.editRegion, sourceBounds, mode)
      ? this.state.editRegion
      : loaded?.artifact.region ?? sourceBounds;
    const regionPreviewRequired = Boolean(loaded?.artifact.schemaVersion === 4 && loaded.artifact.region && !this.sameRegion(bounds, loaded.artifact.region));
    const creator = this.state.creatorChallenge;
    const preview = loaded
      ? this.renderArtifactPreview(loaded)
      : `<div class="surface preview-placeholder">${this.state.previewLoad === "loading" ? `<p class="status-live" role="status"><i class="spinner"></i>${tr.authorizedMapLoading}</p>` : `<p>${tr.previewRequiresAccess}</p><button class="button button-secondary" type="button" data-action="load-private-preview" data-version-id="${escapeHtml(version?.id ?? "")}" ${version ? "" : "disabled"}>${tr.loadAuthorizedPreview}</button>`}</div>`;
    const modeOptions = validModes.filter((candidate) => map.versions.some((item) => item.mode === candidate)).map((candidate) => `<option value="${candidate}" ${candidate === mode ? "selected" : ""}>${escapeHtml(modeTitle(candidate))}</option>`).join("");
    return `<section class="panel-screen panel-screen-wide map-editor-screen" aria-labelledby="map-edit-title">
      <a class="back-link" href="/maps/${encodeURIComponent(map.id)}" data-action="map-detail">${icon("arrowLeft")}${tr.mapDetails}</a>
      <div class="panel-title-row"><div><span class="eyebrow">${tr.creatorTools}</span><h1 id="map-edit-title">${tr.editMap}</h1><p>${tr.boundedMapEditing}</p></div></div>
      ${preview}${this.state.previewError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.previewError)}</p>` : ""}
      <form class="surface form-surface form-stack editor-form" data-form="map-edit" novalidate>
        <div class="field"><label for="edit-mode">${tr.modeLabel}</label><select id="edit-mode" name="mode" ${this.state.busy ? "disabled" : ""}>${modeOptions}</select></div>
        <input type="hidden" name="versionId" value="${escapeHtml(version?.id ?? "")}" />
        ${loaded ? this.renderCreatorSetupControls(loaded, bounds, sourceBounds) : `<fieldset class="region-fields"><legend>${tr.selectedRegion}</legend>${([["region-x", tr.regionX, bounds.x, sourceBounds.x + sourceBounds.width], ["region-y", tr.regionY, bounds.y, sourceBounds.y + sourceBounds.height], ["region-width", tr.regionWidth, bounds.width, sourceBounds.width], ["region-height", tr.regionHeight, bounds.height, mode === "scroll_rush" ? sourceBounds.height : Math.min(3600, sourceBounds.height)]] as const).map(([name, label, value, max]) => `<label class="field" for="${name}">${label}<input id="${name}" name="${name}" type="number" min="0" max="${max}" step="1" value="${Math.round(value)}" required /></label>`).join("")}</fieldset>`}
        <p class="inline-note" role="status" data-region-preview-note ${regionPreviewRequired ? "" : "hidden"}>${tr.creatorRegionPreviewOnly}</p>
        <p class="field-hint">${loaded ? `${tr.worldBounds}: ${loaded.artifact.world.width} × ${loaded.artifact.world.height}` : tr.regionNeedsPreview}</p>
        ${this.state.formError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.formError)}</p>` : ""}
        <div class="form-actions"><button class="button button-secondary" type="button" data-action="creator-region-preview" ${regionPreviewRequired && !this.state.busy ? "" : "hidden"} ${!version || !loaded || this.state.busy ? "disabled" : ""}>${tr.creatorCreateRegionPreview}</button><button class="button button-secondary" type="submit" ${!version || !loaded || this.state.busy || regionPreviewRequired ? "disabled" : ""}>${this.state.busy ? `<i class="spinner"></i>${tr.savingVariant}` : tr.createVariant}</button></div>
      </form>
      <form class="surface form-surface form-stack editor-form" data-form="creator-draft" novalidate>
        <h2>${tr.creatorChallengeDraft}</h2><p>${tr.creatorChallengePrivacy}</p><input type="hidden" name="mapVersionId" value="${escapeHtml(version?.id ?? "")}" />
        <div class="field"><label for="creator-title">${tr.challengeName}</label><input id="creator-title" name="title" type="text" minlength="2" maxlength="100" required value="${escapeHtml(creator?.title ?? "")}" /></div>
        <div class="field"><label for="creator-description">${tr.description}</label><textarea id="creator-description" name="description" maxlength="500">${escapeHtml(creator?.description ?? "")}</textarea></div>
        <div class="field"><label for="creator-visibility">${tr.mapVisibility}</label><select id="creator-visibility" name="visibility"><option value="private">${tr.visibilityPrivate}</option><option value="unlisted">${tr.visibilityUnlisted}</option><option value="public">${tr.visibilityPublic}</option></select></div>
        <div class="form-actions"><button class="button button-primary" type="submit" ${!version || !this.multiplayer.enabled || this.state.busy ? "disabled" : ""}>${tr.createCreatorDraft}</button></div>
        ${creator ? `<p class="inline-note" role="status">${tr.creatorDraftCreated} ${escapeHtml(creator.id.slice(0, 8))}</p><button class="button button-secondary" type="button" data-action="creator-share">${tr.shareChallenge}</button>` : ""}
      </form>
    </section>`;
  }

  private renderLibrary(): string {
    if (this.state.libraryLoad === "loading") return `<section class="panel-screen"><h1>${tr.library}</h1><p class="status-live" role="status"><i class="spinner"></i>${tr.libraryLoading}</p></section>`;
    const entries = this.state.libraryEntries.filter((entry) => !this.state.libraryFavoritesOnly || entry.favorite);
    const rows = entries.map((entry) => {
      const map = entry.map;
      if (!map) return `<article class="library-row"><div><h2>${tr.mapUnavailable}</h2><p>${entry.accessRevoked ? tr.accessRevoked : tr.mapExpired}</p></div></article>`;
      return `<article class="library-row"><div class="library-row-copy"><span class="eyebrow">${escapeHtml(map.provenance.sourceHost)} · ${this.visibilityLabel(map.visibility)}</span><h2><a href="/maps/${encodeURIComponent(map.id)}" data-action="open-map" data-map-id="${escapeHtml(map.id)}">${escapeHtml(map.title)}</a></h2><p>${tr.capturedAt}: ${escapeHtml(map.provenance.capturedAt.slice(0, 10))} · ${map.versions.length} ${tr.mapVersions.toLocaleLowerCase()}</p><p class="source-safe-url">${escapeHtml(map.provenance.displayUrl)}</p></div><div class="library-row-actions"><a class="button button-small button-secondary" href="/maps/${encodeURIComponent(map.id)}" data-action="open-map" data-map-id="${escapeHtml(map.id)}">${tr.openMap}</a><button class="button button-small button-quiet" type="button" data-action="favorite-map" data-map-id="${escapeHtml(map.id)}" data-favorite="${entry.favorite ? "false" : "true"}" aria-pressed="${entry.favorite}">${entry.favorite ? tr.removeFavorite : tr.addFavorite}</button></div></article>`;
    }).join("");
    return `<section class="panel-screen panel-screen-wide" aria-labelledby="library-title"><div class="panel-title-row"><div><span class="eyebrow">${tr.privateContent}</span><h1 id="library-title">${tr.library}</h1><p>${tr.libraryPrivacy}</p></div><button class="button button-secondary" type="button" data-action="reload-library">${tr.refreshStatus}</button></div><div class="toolbar-row"><button class="filter-button ${this.state.libraryFavoritesOnly ? "is-selected" : ""}" type="button" data-action="library-filter" aria-pressed="${this.state.libraryFavoritesOnly}">${tr.favoritesOnly}</button><span class="field-hint">${entries.length} ${tr.mapsCount}</span></div>${this.state.libraryError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.libraryError)}</p>` : ""}<div class="library-list">${rows || `<div class="surface empty-state"><h2>${tr.libraryEmpty}</h2><p>${tr.libraryEmptyHelp}</p><a class="button button-primary" href="/" data-action="home">${tr.newUrlCapture}</a></div>`}</div></section>`;
  }

  private renderDiscover(): string {
    if (this.state.discoverLoad === "loading") return `<section class="panel-screen"><h1>${tr.discover}</h1><p class="status-live" role="status"><i class="spinner"></i>${tr.discoverLoading}</p></section>`;
    const maps = this.state.discoverPage?.items ?? [];
    const mapRows = maps.map((map) => `<article class="discover-row"><div><span class="eyebrow">${escapeHtml(map.provenance.sourceHost)} · ${escapeHtml(modeTitle(map.versions[0]?.mode ?? "scroll_rush"))}</span><h2><a href="/maps/${encodeURIComponent(map.id)}" data-action="open-map" data-map-id="${escapeHtml(map.id)}">${escapeHtml(map.title)}</a></h2><p>${escapeHtml(map.description || tr.noDescription)}</p><span class="field-hint">${map.completedMatches} ${tr.completedMatches}</span></div><a class="button button-small button-secondary" href="/maps/${encodeURIComponent(map.id)}" data-action="open-map" data-map-id="${escapeHtml(map.id)}">${tr.openMap}</a></article>`).join("");
    const rooms = this.state.discoverRooms.map((room) => `<article class="public-room-row"><div><span class="eyebrow">${escapeHtml(modeTitle(room.mode))}</span><h3>${escapeHtml(room.title)}</h3><p>${room.players}/${room.maxPlayers} ${tr.playerCount} · ${escapeHtml(room.roomCode)}</p></div><button class="button button-small button-primary" type="button" data-action="join-public-room" data-room-code="${escapeHtml(room.roomCode)}" ${room.players >= room.maxPlayers || !this.multiplayer.enabled ? "disabled" : ""}>${tr.joinRoom}</button></article>`).join("");
    return `<section class="panel-screen panel-screen-wide" aria-labelledby="discover-title"><div class="panel-title-row"><div><span class="eyebrow">${tr.publicOnly}</span><h1 id="discover-title">${tr.discover}</h1><p>${tr.discoverPolicy}</p></div><button class="button button-secondary" type="button" data-action="reload-discover">${tr.refreshStatus}</button></div><label class="field discover-filter" for="discover-mode">${tr.filterByMode}<select id="discover-mode" name="discover-mode"><option value="">${tr.allModes}</option>${validModes.map((mode) => `<option value="${mode}" ${mode === this.state.discoverMode ? "selected" : ""}>${escapeHtml(modeTitle(mode))}</option>`).join("")}</select></label>${this.state.discoverError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.discoverError)}</p>` : ""}<section class="surface discover-list"><h2>${tr.publicMaps}</h2>${mapRows || `<p class="field-hint">${tr.discoverEmpty}</p>`}${this.state.discoverPage?.nextCursor ? `<button class="button button-quiet" type="button" data-action="discover-more">${tr.loadMore}</button>` : ""}</section><section class="surface discover-list"><h2>${tr.publicRooms}</h2>${rooms || `<p class="field-hint">${tr.noPublicRooms}</p>`}</section></section>`;
  }

  private renderProfile(): string {
    const profile = this.state.profile;
    if (this.state.profileLoad === "loading" || !profile && this.state.profileLoad === "idle") return `<section class="panel-screen"><h1>${tr.profile}</h1><p class="status-live" role="status"><i class="spinner"></i>${tr.profileLoading}</p></section>`;
    if (!profile) return `<section class="panel-screen"><h1>${tr.profile}</h1><p class="inline-error" role="alert">${escapeHtml(this.state.profileError || tr.profileUnavailable)}</p><button class="button button-secondary" type="button" data-action="reload-profile">${tr.retry}</button></section>`;
    const characters = Object.entries(characterAssets).map(([id, character]) => `<label class="character-choice"><input type="radio" name="characterId" value="${id}" ${profile.characterId === id ? "checked" : ""} /><span class="character-sheet-preview" role="img" aria-label="${escapeHtml(character.name)}" style="background-image:url('${character.url}');background-position:0 0"></span><strong>${escapeHtml(character.name)}</strong></label>`).join("");
    const accountForm = profile.isAnonymous
      ? `<form class="surface form-surface form-stack" data-form="upgrade-account" novalidate><h2>${tr.upgradeAccount}</h2><p>${tr.upgradeAccountPrivacy}</p><p class="field-hint">${tr.emailConfirmationInstructions}</p><div class="field"><label for="upgrade-email">${tr.email}</label><input id="upgrade-email" name="email" type="email" autocomplete="email" required maxlength="254" /></div>${this.state.formError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.formError)}</p>` : ""}<button class="button button-secondary" type="submit" ${this.state.busy ? "disabled" : ""}>${this.state.busy ? tr.upgrading : tr.requestEmailLink}</button></form>`
      : `<form class="surface form-surface form-stack" data-form="set-password" novalidate><h2>${tr.setAccountPassword}</h2><p>${tr.accountIdentityPreserved}</p><div class="field"><label for="upgrade-password">${tr.password}</label><input id="upgrade-password" name="password" type="password" autocomplete="new-password" minlength="10" maxlength="128" required /></div>${this.state.formError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.formError)}</p>` : ""}<button class="button button-secondary" type="submit" ${this.state.busy ? "disabled" : ""}>${this.state.busy ? tr.upgrading : tr.setAccountPassword}</button></form>`;
    return `<section class="panel-screen panel-screen-wide" aria-labelledby="profile-title"><div class="panel-title-row"><div><span class="eyebrow">${profile.isAnonymous ? tr.guestAccount : tr.permanentAccount}</span><h1 id="profile-title">${tr.profile}</h1><p>${tr.profileIdentityPrivacy}</p></div></div><form class="surface form-surface form-stack" data-form="profile" novalidate><div class="field"><label for="profile-nickname">${tr.nickname}</label><input id="profile-nickname" name="nickname" minlength="2" maxlength="24" required value="${escapeHtml(profile.nickname)}" /></div><fieldset class="character-grid"><legend>${tr.chooseCharacter}</legend>${characters}</fieldset><div class="field"><label for="profile-locale">${tr.interfaceLanguage}</label><select id="profile-locale" name="locale"><option value="tr-TR" ${profile.locale === "tr-TR" ? "selected" : ""}>Türkçe</option><option value="en-US" ${profile.locale === "en-US" ? "selected" : ""}>English</option></select></div>${this.state.profileError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.profileError)}</p>` : ""}${this.state.notice ? `<p class="inline-note" role="status">${escapeHtml(this.state.notice)}</p>` : ""}<div class="form-actions"><button class="button button-primary" type="submit">${this.state.busy ? `<i class="spinner"></i>${tr.savingProfile}` : tr.saveProfile}</button><button class="button button-secondary" type="button" data-action="history">${tr.matchHistory}</button></div></form>${accountForm}<section class="surface form-surface danger-zone" aria-labelledby="delete-account-title"><h2 id="delete-account-title">${tr.deleteAccount}</h2><p>${tr.deleteAccountWarning}</p>${this.profileDeleteError ? `<p class="inline-error" role="alert">${escapeHtml(this.profileDeleteError)}</p>` : ""}<button class="button button-small button-danger" type="button" data-action="delete-account" ${this.profileDeleteBusy || this.state.busy ? "disabled" : ""}>${this.profileDeleteBusy ? `<i class="spinner"></i>${tr.deletingAccount}` : tr.deleteAccountAction}</button></section></section>`;
  }

  private renderAdmin(): string {
    if (!this.state.profile?.isAdmin) return `<section class="panel-screen"><a class="back-link" href="/" data-action="home">${icon("arrowLeft")}${tr.homeNav}</a><h1>${tr.admin}</h1><p class="inline-note" role="status">${this.state.adminLoad === "loading" ? tr.adminLoading : tr.adminRestricted}</p></section>`;
    const summary = this.state.adminSummary;
    const jobs = summary?.jobs.map((job) => `<li class="admin-row"><span><strong>${escapeHtml(job.phase)}</strong><span>${escapeHtml(job.id)} · ${escapeHtml(job.host ?? "")}</span></span><time>${escapeHtml(job.createdAt)}</time></li>`).join("") ?? "";
    const reports = summary?.reports.map((report) => `<li class="admin-row"><span><strong>${escapeHtml(report.reason)} · ${escapeHtml(report.status)}</strong><span>${escapeHtml(report.mapId)} · ${escapeHtml(report.id)}</span></span><time>${escapeHtml(report.createdAt)}</time><button class="button button-small button-secondary" type="button" data-action="admin-report-dismiss" data-report-id="${escapeHtml(report.id)}">${tr.dismissReport}</button><button class="button button-small button-danger" type="button" data-action="admin-report-takedown" data-report-id="${escapeHtml(report.id)}">${tr.takedown}</button></li>`).join("") ?? "";
    return `<section class="panel-screen panel-screen-wide" aria-labelledby="admin-title"><div class="panel-title-row"><div><span class="eyebrow">${tr.adminTools}</span><h1 id="admin-title">${tr.admin}</h1><p>${tr.adminSafetyNote}</p></div><button class="button button-secondary" type="button" data-action="reload-admin">${tr.refreshStatus}</button></div>${this.state.adminError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.adminError)}</p>` : ""}${!summary ? `<p class="status-live" role="status"><i class="spinner"></i>${tr.adminLoading}</p>` : `<dl class="admin-usage"><div><dt>${tr.activeJobs}</dt><dd>${summary.usage.activeJobs}</dd></div><div><dt>${tr.activeRooms}</dt><dd>${summary.usage.activeRooms}</dd></div><div><dt>${tr.capturesToday}</dt><dd>${summary.usage.capturesToday}</dd></div></dl><section class="surface admin-section"><h2>${tr.recentJobs}</h2><ul class="admin-list">${jobs || `<li>${tr.noAdminJobs}</li>`}</ul></section><section class="surface admin-section"><h2>${tr.contentReports}</h2><ul class="admin-list">${reports || `<li>${tr.noReports}</li>`}</ul></section><form class="surface form-surface form-stack" data-form="admin-policy" novalidate><h2>${tr.sourcePolicy}</h2><div class="field"><label for="policy-host">${tr.sourceHost}</label><input id="policy-host" name="host" type="text" maxlength="253" required placeholder="example.com" /></div><div class="field"><label for="policy-action">${tr.policyAction}</label><select id="policy-action" name="action"><option value="block">${tr.blockHost}</option><option value="allow">${tr.allowHost}</option></select></div><div class="field"><label for="policy-reason">${tr.policyReason}</label><input id="policy-reason" name="reason" type="text" maxlength="200" required /></div><button class="button button-secondary" type="submit">${tr.savePolicy}</button></form>`}</section>`;
  }

  private renderCreatorChallenge(): string {
    const challenge = this.state.creatorChallenge;
    if (this.state.creatorChallengeLoad === "loading" || this.state.creatorChallengeLoad === "idle") return `<section class="panel-screen"><a class="back-link" href="/" data-action="home">${icon("arrowLeft")}${tr.homeNav}</a><p class="status-live" role="status"><i class="spinner"></i>${tr.challengeLoading}</p></section>`;
    if (!challenge) return `<section class="panel-screen"><a class="back-link" href="/" data-action="home">${icon("arrowLeft")}${tr.homeNav}</a><h1>${tr.challengeTitle}</h1><p class="inline-error" role="alert">${escapeHtml(this.state.creatorChallengeError || tr.challengeError)}</p><button class="button button-secondary" type="button" data-action="retry-creator-challenge">${tr.retry}</button></section>`;
    const versions = challenge.versions.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const version = versions.find((item) => item.id === this.state.creatorVersionId) ?? versions[0];
    const rows = this.state.creatorChallengeRankings.map((entry) => {
      const outcome = entry.outcome === "victory" ? tr.bossVictory : entry.outcome === "defeat" ? tr.bossDefeat : entry.outcome === "red" ? tr.redTeamWon : entry.outcome === "blue" ? tr.blueTeamWon : entry.outcome === "draw" ? tr.matchDraw : "";
      const detail = entry.elapsedMs === null ? `${entry.score} ${tr.points}${outcome ? ` · ${outcome}` : ""}` : `${formatTime(entry.elapsedMs + entry.penaltyMs)} · +${formatTime(entry.penaltyMs)} ${tr.penalty}${outcome ? ` · ${outcome}` : ""}`;
      const ghost = entry.replayId
        ? `<button class="button button-small button-secondary" type="button" data-action="creator-ghost" data-replay-id="${entry.replayId}" aria-pressed="${this.state.challengeReplay?.id === entry.replayId}">${this.state.challengeReplay?.id === entry.replayId ? tr.ghostActive : tr.challengeGhost}</button>`
        : `<span class="field-hint">${tr.ghostReplayUnavailable}</span>`;
      return `<li class="history-item"><span><strong>${entry.rank}. ${escapeHtml(entry.nickname)}</strong><span>${detail}</span></span>${ghost}</li>`;
    }).join("");
    const playerCountPicker = version?.mode === "browser_boss"
      ? `<label class="field creator-player-count"><span>${tr.playerCount}</span><select data-control="creator-player-count" aria-label="${tr.playerCount}">${[1, 2, 3, 4].map((count) => `<option value="${count}" ${count === this.state.creatorPlayerCount ? "selected" : ""}>${count}</option>`).join("")}</select></label>`
      : "";
    const selectedPreview = version && this.state.privatePreview && this.state.previewVersionId === version.id && this.creatorArtifactMatchesVersion(version, this.state.privatePreview)
      ? this.state.privatePreview
      : null;
    const canEdit = challenge.owner && version && selectedPreview && this.state.creatorSourceBounds !== null;
    const editor = challenge.owner && version ? `<form class="surface form-surface form-stack editor-form" data-form="creator-version" novalidate><h2>${tr.saveNewVersion}</h2><p>${tr.creatorPatchBounded}</p>${canEdit ? this.renderCreatorSetupControls(selectedPreview!, selectedPreview!.artifact.region, this.state.creatorSourceBounds!) : !this.state.creatorSourceBounds ? `<p class="inline-error" role="alert">${tr.sourceBoundsUnavailable}</p>` : `<p class="status-live" role="status">${tr.previewRequiresAccess}</p><button class="button button-secondary" type="button" data-action="load-private-preview" data-version-id="${escapeHtml(version.id)}">${tr.loadAuthorizedPreview}</button>`}<p class="inline-note" role="status" data-region-preview-note hidden>${tr.creatorRegionPreviewRequired} <a href="/maps/${encodeURIComponent(challenge.mapId)}/edit" data-action="edit-map-id" data-map-id="${escapeHtml(challenge.mapId)}">${tr.editMap}</a></p>${this.state.creatorChallengeError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.creatorChallengeError)}</p>` : ""}<button class="button button-secondary" type="submit" ${!canEdit || this.state.busy ? "disabled" : ""}>${this.state.busy ? `<i class="spinner"></i>${tr.savingVariant}` : tr.saveNewVersion}</button></form>` : "";
    return `<section class="panel-screen" aria-labelledby="creator-challenge-title"><a class="back-link" href="/library" data-action="library">${icon("arrowLeft")}${tr.library}</a><div class="panel-title-row"><div><span class="eyebrow">${this.visibilityLabel(challenge.visibility)}</span><h1 id="creator-challenge-title">${escapeHtml(challenge.title)}</h1><p>${escapeHtml(challenge.description || tr.noDescription)}</p></div></div>${challenge.removed ? `<p class="inline-error" role="alert">${tr.challengeRemoved}</p>` : `<div class="surface challenge-detail"><label class="field">${tr.challengeVersion}<select name="creator-version" data-control="creator-version" ${this.state.busy ? "disabled" : ""}>${versions.map((item) => `<option value="${item.id}" ${item.id === version?.id ? "selected" : ""}>${escapeHtml(modeTitle(item.mode))} · ${escapeHtml(item.artifactHash.slice(0, 12))} · ${escapeHtml(item.createdAt.slice(0, 10))}</option>`).join("")}</select></label>${playerCountPicker}<div class="result-actions"><button class="button button-secondary" type="button" data-action="creator-share">${tr.shareChallenge}</button><button class="button button-primary" type="button" data-action="play-creator-challenge" ${version && this.multiplayer.enabled ? "" : "disabled"}>${icon("play")}${tr.challengeStart}</button></div>${this.state.creatorShare ? `<div class="share-link-panel"><label for="creator-share-url">${tr.shareLink}</label><input id="creator-share-url" readonly value="${escapeHtml(this.state.creatorShare.url)}" /><button class="button button-small button-secondary" type="button" data-action="copy-creator-share">${tr.copy}</button><button class="button button-small button-quiet" type="button" data-action="revoke-creator-share" data-share-id="${this.state.creatorShare.id}">${tr.revokeShare}</button></div>` : ""}</div>${editor}<section class="surface challenge-ranking"><div class="section-heading"><h2>${tr.challengeLeaderboard}</h2>${this.state.creatorRankingsLoad === "loading" ? `<span class="status-live" role="status"><i class="spinner"></i>${tr.challengeLoading}</span>` : ""}</div>${this.state.creatorRankingsError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.creatorRankingsError)}</p>` : this.state.creatorRankingsLoad === "error" ? `<button class="button button-small button-secondary" type="button" data-action="reload-creator-rankings">${tr.retry}</button>` : rows ? `<ol class="history-list">${rows}</ol>` : this.state.creatorRankingsLoad === "ready" ? `<p class="field-hint">${tr.challengeNoRecords}</p>` : ""}${this.state.replayLoad === "loading" ? `<p class="status-live" role="status"><i class="spinner"></i>${tr.ghostReplayLoading}</p>` : this.state.replayError ? `<p class="inline-note" role="status">${escapeHtml(this.state.replayError)}</p>` : ""}</section>`}</section>`;
  }

  private renderReportForm(mapId: string): string {
    return `<form class="report-form" data-form="report" novalidate><input type="hidden" name="mapId" value="${escapeHtml(mapId)}" /><label class="field">${tr.reportReason}<select name="reason"><option value="privacy">${tr.reportPrivacy}</option><option value="rights">${tr.reportRights}</option><option value="unsafe">${tr.reportUnsafe}</option><option value="abuse">${tr.reportAbuse}</option><option value="other">${tr.other}</option></select></label><label class="field">${tr.reportDetails}<textarea name="details" maxlength="1000"></textarea></label><button class="button button-small button-secondary" type="submit">${tr.submitReport}</button></form>`;
  }

  private conversionPhaseLabel(phase: string): string {
    const labels: Record<string, string> = { queued: tr.phaseQueued, policy_check: tr.phasePolicyCheck, capturing: tr.phaseCapturing, extracting: tr.phaseExtracting, compiling: tr.phaseCompiling, packaging: tr.phasePackaging, ready: tr.phaseReady, rejected: tr.phaseRejected, failed: tr.phaseFailed, cancelled: tr.phaseCancelled, expired: tr.phaseExpired };
    return labels[phase] ?? tr.conversionLoading;
  }

  private conversionErrorText(code: string): string {
    const errors: Record<string, string> = {
      INVALID_URL: tr.urlInvalid, UNSUPPORTED_SCHEME: tr.urlSchemeUnsupported, DISALLOWED_TARGET: tr.urlTargetBlocked,
      URL_CONTAINS_SECRET: tr.urlSecretBlocked, SOURCE_POLICY_BLOCKED: tr.urlPolicyBlocked, AUTH_REQUIRED_BY_SOURCE: tr.urlAuthRequired,
      BOT_CHALLENGE: tr.urlChallengeBlocked, UNSUPPORTED_CONTENT_TYPE: tr.urlContentUnsupported, CAPTURE_TIMEOUT: tr.urlCaptureTimeout,
      CAPTURE_LIMIT_REACHED: tr.urlCaptureLimit, CAPTURE_UNSTABLE: tr.urlCaptureUnstable, NO_PLAYABLE_MODE: tr.urlNoModes,
      MODE_NOT_SUITABLE: tr.urlNoModes, QUOTA_EXCEEDED: tr.urlQuota, QUEUE_AT_CAPACITY: tr.urlQueueFull,
      CAPTURE_CONFIGURATION_REQUIRED: tr.captureNotConfigured, ACCESS_REVOKED: tr.accessRevoked, SOURCE_REMOVED: tr.sourceRemoved,
      JOB_CANCELLED: tr.phaseCancelled, PERSISTENCE_PENDING: tr.statusPending, ASSET_MISSING: tr.assetMissingError,
      VERSION_MISMATCH: tr.versionMismatch, ROOM_FULL: tr.errorRoomFull, PLAYER_COUNT_MISMATCH: tr.errorNotReady,
      AUTH_INVALID: tr.errorAuth, FORBIDDEN: tr.errorForbidden, NOT_FOUND: tr.errorRoomMissing, RATE_LIMITED: tr.errorRateLimited,
      CONFLICT: tr.errorVersionMismatch,
      ACCOUNT_EMAIL_INVALID: tr.accountEmailInvalid, ACCOUNT_PASSWORD_INVALID: tr.accountPasswordInvalid,
      ACCOUNT_EMAIL_NOT_CONFIRMED: tr.accountEmailNotConfirmed, ACCOUNT_UPGRADE_FAILED: tr.accountUpgradeFailed,
      AUTH_UNAVAILABLE: tr.serverNotConfigured, AUTH_SESSION_CHANGED: tr.accountUpgradeFailed,
    };
    return errors[code] ?? tr.conversionGenericError;
  }

  private renderPracticeStarting(): string {
    const mode = this.state.practiceMode;
    const version = this.state.practiceRetryVersionId
      ? this.state.mapView?.versions.find((entry) => entry.id === this.state.practiceRetryVersionId)
      : undefined;
    const map = this.state.maps.find((entry) => entry.artifactHash === this.state.practiceRetryHash);
    const mapTitle = version && this.state.mapView ? this.state.mapView.title : map?.title;
    const loadingText = this.state.assetLoad === "loading" ? tr.practiceVerifyingMap : tr.practiceConnecting;
    const retryTarget = this.state.practiceRetryVersionId
      ? `data-version-id="${escapeHtml(this.state.practiceRetryVersionId)}" data-artifact-hash="${escapeHtml(this.state.practiceRetryHash)}"`
      : this.state.practiceRetryHash ? `data-artifact-hash="${escapeHtml(this.state.practiceRetryHash)}"` : "";
    return `<section class="panel-screen" aria-labelledby="practice-start-title"><button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.cancel}</button><div class="surface form-surface practice-start-panel"><span class="eyebrow">${tr.practiceUnranked}</span><h1 id="practice-start-title">${mode ? escapeHtml(modeTitle(mode)) : tr.practiceStart}</h1><p>${mapTitle ? escapeHtml(mapTitle) : tr.practicePreparing}</p>${this.state.formError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.formError)}</p>` : `<p class="status-live" role="status"><span class="loading-indicator"><i class="spinner"></i>${loadingText}</span></p>`}<p class="inline-note">${tr.practicePrivacy}</p><div class="form-actions">${this.state.formError && mode && retryTarget ? `<button class="button button-primary" type="button" data-action="practice-again" data-mode="${mode}" ${retryTarget}>${tr.retry}</button>` : ""}<button class="button button-quiet" type="button" data-action="home">${tr.cancel}</button></div></div></section>`;
  }

  private renderChallengeCreate(): string {
    return `<section class="panel-screen" aria-labelledby="challenge-create-title"><button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.homeNav}</button><div class="surface empty-state create-version-required"><span class="eyebrow">${tr.challengeTitle}</span><h1 id="challenge-create-title">${tr.challengeCreate}</h1><p>${tr.creatorChallengeNeedsVersion}</p><button class="button button-primary" type="button" data-action="focus-url">${icon("location")}${tr.newUrlCapture}</button></div></section>`;
  }

  private renderChallenge(): string {
    const challenge = this.state.challenge;
    if (this.state.challengeLoad === "loading" || this.state.challengeLoad === "idle") return `<section class="panel-screen" aria-labelledby="challenge-title"><button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.homeNav}</button><div class="loading-panel" role="status"><i class="spinner"></i><span>${tr.challengeLoading}</span></div></section>`;
    if (!challenge) return `<section class="panel-screen" aria-labelledby="challenge-title"><button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.homeNav}</button><h1 id="challenge-title">${tr.challengeTitle}</h1><p class="inline-error" role="alert">${escapeHtml(this.state.challengeError || tr.challengeError)}</p><button class="button button-secondary" type="button" data-action="reload-challenge">${tr.retry}</button></section>`;
    const artifact = this.state.loadedMap?.artifact;
    const compatible = Boolean(artifact && artifact.artifactHash === challenge.artifactHash && artifact.simulationVersion === challenge.simulationVersion && artifact.protocolVersion === challenge.protocolVersion && (SUPPORTED_ARTIFACT_VERSIONS as readonly string[]).includes(challenge.protocolVersion) && artifact.setup.mode === "scroll_rush" && artifact.setup.challengeOptions?.some((option) => option.id === challenge.optionId));
    const route = artifact?.setup.mode === "scroll_rush" ? artifact.setup.challengeOptions?.find((option) => option.id === challenge.optionId) : undefined;
    const rows = this.state.challengeRankings.map((entry) => `<li class="history-item challenge-rank-row"><span><strong>${entry.rank}. ${escapeHtml(entry.nickname)}</strong><span>${formatTime(entry.elapsedMs + entry.penaltyMs)} · ${tr.penalty.toLocaleLowerCase("tr-TR")} ${formatTime(entry.penaltyMs)}</span></span><button class="button button-small button-quiet" type="button" data-action="challenge-ghost" data-replay-id="${entry.replayId}" ${compatible ? "" : "disabled"}>${this.state.challengeReplay?.id === entry.replayId ? tr.ghostActive : tr.challengeGhost}</button></li>`).join("");
    const createLoading = this.state.assetLoad === "loading";
    return `<section class="panel-screen" aria-labelledby="challenge-title">
      <button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.homeNav}</button>
      <div class="panel-title-row"><div><span class="eyebrow">${tr.challengeTitle}</span><h1 id="challenge-title">${escapeHtml(challenge.title)}</h1><p>${route ? `${tr.challengeOption}: ${escapeHtml(route.title)}` : tr.challengeLoading}</p></div><span class="challenge-mark">${icon("challenge")}</span></div>
      <section class="surface challenge-detail">
        <div class="challenge-facts"><div><span>${tr.challengeMap}</span><strong>${escapeHtml(artifact?.title ?? challenge.artifactHash.slice(0, 12))}</strong></div><div><span>${tr.mapVersion}</span><strong>${escapeHtml(challenge.simulationVersion)}</strong></div><div><span>${tr.challengeOption}</span><strong>${escapeHtml(challenge.optionId)}</strong></div></div>
        ${this.state.challengeError ? `<p class="inline-note" role="status">${escapeHtml(this.state.challengeError)}</p>` : ""}
        ${!compatible ? `<p class="inline-note" role="status">${tr.challengeVersionUnavailable}</p>` : this.state.challengeReplay ? `<p class="inline-success" role="status">${tr.ghostReplayVersion} ${escapeHtml(this.state.challengeReplay.nickname)}</p>` : `<p class="inline-note">${tr.challengeGhostHint}</p>`}
        ${this.state.replayLoad === "loading" ? `<p class="status-live" role="status"><i class="spinner"></i>${tr.ghostReplayLoading}</p>` : this.state.replayError ? `<p class="inline-note" role="status">${escapeHtml(this.state.replayError)}</p>` : ""}
        <div class="result-actions"><button class="button button-secondary" type="button" data-action="copy-challenge">${icon("copy")}${tr.challengeShare}</button><button class="button button-primary" type="button" data-action="challenge-practice" ${compatible && !createLoading ? "" : "disabled"}>${icon("play")}${tr.practiceUnrankedAction}</button></div>
      </section>
      <section class="surface challenge-ranking" aria-labelledby="challenge-rankings-title"><div class="challenge-ranking-heading"><div><span class="eyebrow">${tr.serverRecords}</span><h2 id="challenge-rankings-title">${tr.challengeLeaderboard}</h2></div><button class="button button-small button-quiet" type="button" data-action="reload-rankings" ${this.state.rankingsLoad === "loading" ? "disabled" : ""}>${tr.retry}</button></div>
        ${this.state.rankingsLoad === "loading" ? `<p class="status-live" role="status"><i class="spinner"></i>${tr.challengeLoading}</p>` : this.state.rankingsError ? `<p class="inline-note" role="status">${escapeHtml(this.state.rankingsError)}</p>` : rows ? `<ol class="history-list">${rows}</ol>` : `<p class="field-hint">${tr.challengeNoRecords}</p>`}
        <p class="field-hint">${tr.challengeServerRecordOnly}</p>
      </section>
    </section>`;
  }

  private renderHome(): string {
    const localRows = this.state.maps.filter((map) => map.mode === "scroll_rush").length;
    const unavailable = !this.multiplayer.enabled;
    const availableModes = validModes;
    const modeRows = availableModes.map((mode) => {
      const count = this.state.maps.filter((map) => map.mode === mode).length;
      const detail = modePlayers(mode);
      const action = mode === "scroll_rush"
        ? `<button class="button button-small button-secondary" type="button" data-action="local" ${count === 0 ? "disabled" : ""}>${tr.goToCourse}${icon("arrowRight")}</button>`
        : `<button class="button button-small button-secondary" type="button" data-action="focus-url" ${unavailable ? "disabled" : ""}>${tr.newUrlCapture}${icon("arrowRight")}</button>`;
      const solo = `<button class="button button-small button-secondary" type="button" data-action="practice-mode" data-mode="${mode}" ${unavailable || count === 0 || this.state.busy ? "disabled" : ""}>${this.state.busy && this.state.practiceMode === mode ? `<i class="spinner"></i>${tr.practiceStarting}` : tr.practiceStart}</button>`;
      return `<article class="mode-row mode-row-${mode.replaceAll("_", "-")}"><span class="mode-glyph">${icon(modeIcon(mode))}</span><div class="mode-copy"><h3>${modeTitle(mode)}</h3><p>${modeDescription(mode)}</p><span class="mode-meta">${detail}${count > 1 ? ` · ${count} ${tr.mapsReady}` : ""}</span></div><div class="mode-actions">${action}${solo}</div></article>`;
    }).join("");
    return `
      <section class="home-grid" aria-labelledby="hero-title">
        <div class="hero-copy">
          <span class="eyebrow">${tr.homeEyebrow}</span>
          <h1 id="hero-title">${escapeHtml(tr.heroTitle)}</h1>
          <p>${escapeHtml(tr.heroDescription)}</p>
          <form class="url-conversion-form" data-form="conversion" novalidate>
            <label for="source-url">${tr.urlInputLabel}</label>
            <div class="url-input-wrap"><span class="url-input-icon">${icon("location")}</span><input id="source-url" name="url" type="url" inputmode="url" autocomplete="url" autocapitalize="none" spellcheck="false" maxlength="4096" required placeholder="https://example.com/your-page" value="${escapeHtml(this.state.urlDraft)}" aria-describedby="source-url-help" ${this.state.busy ? "disabled" : ""} /><button class="button button-primary url-convert-button" type="submit" ${!this.multiplayer.enabled || this.state.busy ? "disabled" : ""}>${this.state.busy ? `<i class="spinner"></i>${tr.conversionSubmitting}` : `${tr.convertThisPage}${icon("arrowRight")}`}</button></div>
            <span id="source-url-help" class="field-hint">${tr.urlInputHelp}</span>
            ${this.state.formError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.formError)}</p>` : ""}
            ${!this.multiplayer.enabled ? `<p class="inline-note" role="status">${escapeHtml(multiplayerDisabledMessage())} ${tr.captureNotConfigured}</p>` : ""}
            <p class="hero-footnote">${icon("shield")}${tr.urlPrivacyNote}</p>
          </form>
          <div class="hero-secondary-actions"><button class="button button-small button-quiet" type="button" data-action="local" ${localRows === 0 ? "disabled" : ""}>${icon("play")}${tr.localPlay}</button><button class="button button-small button-quiet" type="button" data-action="join" ${unavailable ? "disabled" : ""}>${tr.joinRoom}${icon("arrowRight")}</button><a href="/library" data-action="library">${tr.library}</a></div>
          ${this.state.mapsLoad === "error" ? `<p class="inline-error" role="status">${escapeHtml(this.state.mapsError)} <button class="quiet-button" type="button" data-action="retry-maps">${tr.retry}</button></p>` : ""}
        </div>
        <div class="page-art" aria-hidden="true">
          <div class="art-grid"></div>
          <div class="art-window">
            <div class="art-window-top"><i class="art-dot"></i><i class="art-dot"></i><i class="art-dot"></i><span class="art-address">${icon("shield")} ${tr.heroArtworkLabel}</span></div>
            <div class="art-page">
              <div class="art-nav"><span class="art-nav-mark">U</span><span class="art-nav-links"><i>${tr.artNavRace}</i><i>${tr.artNavHide}</i><i>${tr.artNavTogether}</i></span><span aria-hidden="true">✦</span></div>
              <div class="art-copy"><strong>${escapeHtml(tr.artPageTitle)}</strong><span></span><span></span><div class="art-chip">${tr.checkpointProgress}</div></div>
              <img class="home-character-keyart" src="/brand/character-keyart.png" alt="" />
              <img class="art-brand-icon" src="/brand/urlcade-app-icon.png" alt="" />
              <div class="art-cards"><div class="art-card"><div class="art-card-mark"></div><div class="art-card-line"></div><div class="art-card-line"></div></div><div class="art-card"><div class="art-card-mark"></div><div class="art-card-line"></div><div class="art-card-line"></div></div><div class="art-card"><div class="art-card-mark"></div><div class="art-card-line"></div><div class="art-card-line"></div></div></div>
            </div>
          </div>
          <div class="art-route"></div><div class="art-runner">${runnerArt()}</div>
          <div class="art-note"><span>01 / 02</span> &nbsp; ${tr.routeToFinish}</div>
        </div>
      </section>
      <section class="mode-section" id="modes" aria-labelledby="modes-title">
        <div class="section-heading"><div><span class="eyebrow">${tr.allFiveModes}</span><h2 id="modes-title">${tr.chooseHowToPlay}</h2></div><p>${tr.modesDescription}</p></div>
        <div class="mode-list">${this.state.mapsLoad === "loading" && availableModes.length === 0 ? `<p class="field-hint">${tr.loadingMaps}</p>` : modeRows || `<p class="field-hint">${tr.noMapsDescription}</p>`}</div>
        ${unavailable ? `<p class="mode-service-note" role="status">${escapeHtml(multiplayerDisabledMessage())} ${tr.modeOnlineRequired}</p>` : ""}
      </section>`;
  }

  private renderMapSelection(): string {
    const maps = this.state.maps.filter((map) => map.mode === this.state.selectedMode);
    const listContent = this.state.mapsLoad === "loading"
      ? `<div class="map-load-error"><span class="loading-indicator"><i class="spinner"></i>${tr.loadingMaps}</span></div>`
      : this.state.mapsLoad === "error"
        ? `<div class="map-load-error"><h2>${tr.loadFailed}</h2><p>${escapeHtml(this.state.mapsError)}</p><button class="button button-secondary" type="button" data-action="retry-maps">${tr.retry}</button></div>`
        : maps.length === 0
          ? `<div class="map-load-error"><h2>${tr.noMaps}</h2><p>${tr.noMapsDescription}</p></div>`
          : maps.map((map) => this.renderMapCard(map, true)).join("");
    return `<section class="panel-screen panel-screen-wide" aria-labelledby="map-title">
      <button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.homeNav}</button>
      <div class="panel-title-row"><div><span class="eyebrow">${tr.localLabel}</span><h1 id="map-title">${tr.chooseMap}</h1><p>${tr.chooseMapDescription}</p></div><button class="button button-primary" type="button" data-action="start-local" ${!mapByHash(this.state.maps, this.state.selectedHash) || this.state.busy ? "disabled" : ""}>${this.state.busy ? `<i class="spinner"></i>${tr.loadingAsset}` : `${icon("play")}${tr.startLocal}`}</button></div>
      <div class="map-grid" aria-label="${tr.scrollRushMaps}">${listContent}</div>
      ${this.state.assetError ? `<p class="inline-error disabled-callout" role="alert">${escapeHtml(this.state.assetError)}</p>` : ""}
      ${this.state.assetLoad === "loading" ? `<p class="status-live" role="status"><span class="loading-indicator"><i class="spinner"></i>${tr.mapDigestLoading}</span></p>` : ""}
      <p class="hero-footnote">${icon("shield")}${tr.privacyNote}</p>
    </section>`;
  }

  private renderMapCard(map: CatalogEntry, selectable: boolean): string {
    const fakeMap = { artifactHash: map.artifactHash };
    const best = map.mode === "scroll_rush" ? getBestLocalScore(fakeMap) : null;
    const selected = map.artifactHash === this.state.selectedHash;
    const localThumb = this.state.loadedMap?.artifact.artifactHash === map.artifactHash
      ? this.state.loadedMap.assetUrls.get(this.state.loadedMap.artifact.backgroundTiles[0]?.assetId ?? "")
      : undefined;
    return `<button class="map-card" type="button" data-map-hash="${escapeHtml(map.artifactHash)}" aria-pressed="${selected}" ${selectable ? "" : "disabled"}>
      <span class="map-preview ${map.mode === "site_hide_seek" ? "hide-map" : ""}" aria-hidden="true">${localThumb ? `<img class="map-preview-image" src="${escapeHtml(localThumb)}" alt="" />` : ""}<i class="map-preview-lines"></i><i class="map-preview-figure"></i></span>
      <span class="map-card-info"><strong>${escapeHtml(map.title)}</strong><span class="map-card-meta"><span>${modeTitle(map.mode)} · ${map.artifactHash.slice(0, 8)}</span>${best ? `<span class="map-card-time">${formatTime(best.elapsedMs + best.penaltyMs)}</span>` : ""}</span></span>
    </button>`;
  }

  private renderCreateForm(): string {
    if (!this.state.mapView) return `<section class="panel-screen" aria-labelledby="create-title"><button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.homeNav}</button><div class="surface empty-state create-version-required"><span class="eyebrow">${tr.privateRoom}</span><h1 id="create-title">${tr.createTitle}</h1><p>${tr.createVersionRequired}</p><button class="button button-primary" type="button" data-action="focus-url">${icon("location")}${tr.newUrlCapture}</button></div></section>`;
    const availableMaps = this.state.maps.filter((map) => map.mode === this.state.createMode);
    if (!availableMaps.some((map) => map.artifactHash === this.state.createMapHash)) this.state.createMapHash = availableMaps[0]?.artifactHash ?? "";
    const options = availableMaps.map((map) => `<option value="${escapeHtml(map.artifactHash)}" ${map.artifactHash === this.state.createMapHash ? "selected" : ""}>${escapeHtml(map.title)}</option>`).join("");
    const versionOptions = this.state.mapView.versions.filter((version) => version.mode === this.state.createMode && version.protocolVersion === PROTOCOL_VERSION && Boolean(version.rulesHash));
    const selectedVersion = versionOptions.find((version) => version.id === this.state.createMapVersionId) ?? versionOptions[0];
    if (selectedVersion && !this.state.createMapVersionId) this.state.createMapVersionId = selectedVersion.id;
    const canSubmit = this.multiplayer.enabled && Boolean(selectedVersion && this.state.assetLoad === "ready") && !this.state.busy;
    const limits = MODE_LIMITS[this.state.createMode];
    const challenge = Boolean(this.state.pendingChallengeId);
    const availableModes = this.state.mapView
      ? validModes.filter((mode) => this.state.mapView?.versions.some((version) => version.mode === mode))
      : [];
    const versioned = Boolean(this.state.mapView);
    const visibilityPublic = selectedVersion && this.state.mapView?.visibility === "public" && !this.state.mapView.owner;
    const returnToMapDetail = Boolean(this.state.mapView && window.location.pathname === `/maps/${encodeURIComponent(this.state.mapView.id)}`);
    return `<section class="panel-screen" aria-labelledby="create-title">
      <button class="back-link" type="button" data-action="${returnToMapDetail ? "map-detail" : "home"}">${icon("arrowLeft")}${returnToMapDetail ? tr.mapDetails : tr.homeNav}</button>
      <div class="panel-title-row"><div><span class="eyebrow">${challenge ? tr.challengeTitle : tr.privateRoom}</span><h1 id="create-title">${challenge ? tr.challengeStart : tr.createTitle}</h1><p>${challenge ? tr.challengeRoomDescription : tr.createDescription}</p></div></div>
      <form class="surface form-surface form-stack" data-form="create" novalidate>
        <div class="field"><label for="nickname">${tr.nickname}</label><input id="nickname" name="nickname" type="text" minlength="2" maxlength="24" autocomplete="nickname" required value="${escapeHtml(this.state.profile?.nickname ?? "")}" placeholder="${tr.exampleNickname}" aria-describedby="nickname-hint" /><span class="field-hint" id="nickname-hint">${tr.nicknameInputHint}</span></div>
        <div class="field"><label for="create-mode">${tr.gameMode}</label><select id="create-mode" name="mode" ${challenge ? "disabled" : ""}>${availableModes.map((mode) => `<option value="${mode}" ${mode === this.state.createMode ? "selected" : ""}>${modeTitle(mode)} · ${modePlayers(mode)}</option>`).join("")}</select><span class="field-hint">${tr.playerLimitHint.replace("{min}", String(limits.min)).replace("{max}", String(limits.max))}</span></div>
        <div class="field"><label for="create-map">${tr.mapLabel}</label><select id="create-map" name="mapVersionId" ${versionOptions.length === 0 || challenge ? "disabled" : ""}>${versionOptions.map((version) => `<option value="${version.id}" ${version.id === selectedVersion?.id ? "selected" : ""}>${escapeHtml(version.artifactHash.slice(0, 12))} · ${escapeHtml(version.createdAt.slice(0, 10))}</option>`).join("") || `<option value="">${tr.noMapVersion}</option>`}</select></div>
        ${this.state.createMode === "capture_logo" ? `<div class="field"><label for="team-size">${tr.teamSize}</label><select id="team-size" name="teamSize"><option value="2" ${this.state.roomTeamSize === 2 ? "selected" : ""}>2 × 2</option><option value="3" ${this.state.roomTeamSize === 3 ? "selected" : ""}>3 × 3</option></select></div>` : ""}
        ${visibilityPublic ? `<label class="check-row"><input type="checkbox" name="listed" ${this.state.roomListed ? "checked" : ""} /><span>${tr.listPublicRoom}</span></label>` : ""}
        ${challenge ? `<p class="inline-note">${escapeHtml(this.state.challenge?.title ?? tr.challengeTitle)} · ${escapeHtml(this.state.createMapHash.slice(0, 12))}</p>` : ""}
        ${this.state.formError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.formError)}</p>` : ""}
        ${!this.multiplayer.enabled ? `<p class="inline-note">${escapeHtml(multiplayerDisabledMessage())} ${tr.multiplayerCreateDisabled}</p>` : ""}
        ${versionOptions.length === 0 ? `<p class="inline-note">${tr.noMapForMode}</p>` : ""}
        <div class="form-actions"><button class="button button-quiet" type="button" data-action="${returnToMapDetail ? "map-detail" : "home"}">${tr.cancel}</button><button class="button button-primary" type="submit" ${canSubmit ? "" : "disabled"}>${this.state.busy ? `<i class="spinner"></i>${tr.networkConnecting}` : `${icon("users")}${tr.createRoom}`}</button></div>
      </form>
    </section>`;
  }

  private renderJoinForm(): string {
    const canSubmit = this.multiplayer.enabled && !this.state.busy;
    return `<section class="panel-screen" aria-labelledby="join-title">
      <button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.homeNav}</button>
      <div class="panel-title-row"><div><span class="eyebrow">${tr.inviteLink}</span><h1 id="join-title">${tr.joinTitle}</h1><p>${tr.joinDescription}</p></div></div>
      <form class="surface form-surface form-stack" data-form="join" novalidate>
        <div class="field"><label for="nickname">${tr.nickname}</label><input id="nickname" name="nickname" type="text" minlength="2" maxlength="24" autocomplete="nickname" required value="${escapeHtml(this.state.profile?.nickname ?? "")}" placeholder="${tr.exampleNickname}" /></div>
        ${this.state.inviteToken ? `<p class="invite-private-note">${tr.inviteAccessNote}</p><input type="hidden" name="inviteToken" value="${escapeHtml(this.state.inviteToken)}" />` : `<div class="field"><label for="room-code">${tr.roomCode}</label><input id="room-code" name="roomCode" type="text" inputmode="text" maxlength="8" autocomplete="off" autocapitalize="characters" pattern="[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{8}" required placeholder="8 KARAKTER" value="${escapeHtml(this.state.roomCode)}" aria-describedby="room-code-hint"/><span class="field-hint" id="room-code-hint">${tr.roomCodeHint}</span></div>`}
        ${this.state.formError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.formError)}</p>` : ""}
        ${!this.multiplayer.enabled ? `<p class="inline-note">${escapeHtml(multiplayerDisabledMessage())} ${tr.multiplayerJoinDisabled}</p>` : ""}
        <div class="form-actions"><button class="button button-quiet" type="button" data-action="home">${tr.cancel}</button><button class="button button-primary" type="submit" ${canSubmit ? "" : "disabled"}>${this.state.busy ? `<i class="spinner"></i>${tr.networkConnecting}` : `${tr.continue}${icon("arrowRight")}`}</button></div>
      </form>
    </section>`;
  }

  private renderLobby(): string {
    const snapshot = this.state.snapshot;
    if (!snapshot) return `<section class="panel-screen"><p class="inline-note" role="status"><i class="spinner"></i> ${tr.lobbyWaiting}</p></section>`;
    const host = Boolean(this.ownPlayerId) && snapshot.hostId === this.ownPlayerId;
    const self = snapshot.players.find((player) => player.id === this.ownPlayerId);
    const ready = Boolean(self?.ready);
    const limits = snapshot.playerLimits ?? MODE_LIMITS[snapshot.mode];
    const playerCountAllowed = snapshot.players.length >= limits.min && snapshot.players.length <= limits.max;
    const allReady = playerCountAllowed && snapshot.players.every((player) => player.ready && player.connected);
    const loaded = this.state.loadedMap?.artifact.artifactHash === snapshot.artifactHash &&
      (!snapshot.rulesHash || this.state.loadedMap.artifact.rulesHash === snapshot.rulesHash) && this.state.assetLoad === "ready";
    const canStart = host && allReady && loaded && !this.state.busy;
    const chosenMap = this.state.maps.find((map) => map.artifactHash === snapshot.artifactHash);
    const roomMapTitle = this.state.mapView?.id === snapshot.mapId ? this.state.mapView.title
      : this.state.loadedMap?.artifact.artifactHash === snapshot.artifactHash ? this.state.loadedMap.artifact.title
      : chosenMap?.title ?? tr.mapLabel;
    const inviteUrl = this.state.inviteToken ? new URL(`/room/${encodeURIComponent(this.state.inviteToken)}`, window.location.origin).toString() : "";
    const playerRows = snapshot.players.map((player) => `<li class="player-row"><span class="player-avatar ${player.team ? `team-${player.team}` : ""}" aria-hidden="true">${escapeHtml(initials(player.nickname))}</span><span class="player-name">${escapeHtml(player.nickname)}${player.id === snapshot.hostId ? `<span class="player-sub"> · ${tr.host}</span>` : ""}${player.team ? `<span class="player-sub team-label team-${player.team}"> · ${player.team === "red" ? tr.redTeam : tr.blueTeam}</span>` : ""}<span class="player-sub">${player.connected ? tr.connected : tr.waitingConnection}</span></span><span class="player-state ${player.ready ? "ready" : ""}">${player.ready ? tr.ready : tr.waiting}</span></li>`).join("");
    const teamSize = snapshot.teamSize ?? 2;
    const teamPicker = snapshot.mode === "capture_logo" && !snapshot.practice
      ? `<fieldset class="team-picker" ${snapshot.phase !== "lobby" || this.state.connectionLost ? "disabled" : ""}><legend>${tr.chooseTeam}</legend>${(["red", "blue"] as const).map((team) => {
        const count = snapshot.players.filter((player) => player.team === team).length;
        const selected = self?.team === team;
        const full = count >= teamSize && !selected;
        const label = team === "red" ? tr.redTeam : tr.blueTeam;
        return `<button class="team-choice team-choice-${team} ${selected ? "is-selected" : ""}" type="button" data-action="select-team" data-team="${team}" aria-pressed="${selected}" ${full ? "disabled" : ""}><span>${label}</span><strong>${count}/${teamSize}</strong>${full ? `<small>${tr.teamFull}</small>` : ""}</button>`;
      }).join("")}</fieldset>`
      : "";
    const ownedRoomVersions = (this.state.mapView?.id === snapshot.mapId ? this.state.mapView.versions : [])
      .filter((version) => version.protocolVersion === PROTOCOL_VERSION && Boolean(version.rulesHash));
    const roomModes = [...new Set(ownedRoomVersions.map((version) => version.mode))];
    const modeOptions = roomModes.map((mode) => `<option value="${mode}" ${mode === snapshot.mode ? "selected" : ""}>${modeTitle(mode)} · ${modePlayers(mode)}</option>`).join("");
    const modeVersions = ownedRoomVersions.filter((version) => version.mode === snapshot.mode);
    const mapOptions = modeVersions.map((version) => `<option value="${version.id}" ${version.id === snapshot.mapVersionId ? "selected" : ""}>${escapeHtml(version.artifactHash.slice(0, 12))} · ${escapeHtml(version.createdAt.slice(0, 10))}</option>`).join("");
    const status = this.state.formError
      ? `<p class="inline-error" role="alert">${escapeHtml(this.state.formError)}</p>`
      : this.state.connectionLost
      ? `<p class="inline-note" role="status">${escapeHtml(this.state.reconnectMessage || tr.matchDisconnected)}</p>`
      : this.state.assetLoad === "loading"
        ? `<p class="status-live" role="status"><span class="loading-indicator"><i class="spinner"></i>${tr.loadingAsset}…</span></p>`
        : this.state.assetError
          ? `<p class="inline-error" role="alert">${escapeHtml(this.state.assetError)}</p>`
          : this.state.notice
            ? `<p class="inline-success" role="status">${escapeHtml(this.state.notice)}</p>`
            : `<p class="lobby-status">${loaded ? tr.loadedReady : tr.assetsNeedChecking}</p>`;
    const challengeRoom = Boolean(snapshot.challengeId);
    const practiceRoom = Boolean(snapshot.practice);
    const soloRushChallengeRoom = snapshot.mode === "scroll_rush" && limits.min === 1 && Boolean(snapshot.challengeVersionId || challengeRoom);
    const objective = practiceRoom ? tr.practiceLobby : soloRushChallengeRoom ? tr.rushChallengeSoloLobby : snapshot.mode === "capture_logo" ? tr.captureObjective : snapshot.mode === "page_brawl" ? tr.brawlObjective : snapshot.mode === "browser_boss" ? tr.bossObjective : snapshot.mode === "site_hide_seek" ? tr.hideNeedsFourHelp : tr.rushNeedsTwoHelp;
    return `<section class="panel-screen panel-screen-wide" aria-labelledby="lobby-page-title">
      <button class="back-link" type="button" data-action="leave-room">${icon("arrowLeft")}${tr.leaveRoom}</button>
      <div class="panel-title-row"><div><span class="eyebrow">${escapeHtml(modeTitle(snapshot.mode))}${practiceRoom ? ` · ${tr.practiceUnranked}` : ""}</span><h1 id="lobby-page-title">${tr.lobbyTitle}</h1><p>${practiceRoom ? tr.practiceLobby : tr.mapVersionPinned}</p></div>${practiceRoom ? "" : `<span class="room-code" aria-label="Oda kodu ${escapeHtml(snapshot.roomCode)}">${escapeHtml(snapshot.roomCode)}</span>`}</div>
      <div class="lobby-layout">
        <section class="surface lobby-main" aria-labelledby="players-title"><div class="lobby-title"><div><h2 id="players-title">${tr.roomPlayers} <span class="player-sub">${snapshot.players.length}/${limits.max}</span></h2><p>${objective}</p></div>${practiceRoom ? "" : `<button class="button button-small button-secondary" type="button" data-action="copy-invite">${icon("copy")}${tr.copyInvite}</button>`}</div>${teamPicker}<ul class="player-list">${playerRows || `<li class="player-row"><span class="loading-indicator"><i class="spinner"></i>${tr.lobbyWaiting}</span></li>`}</ul>
          <div class="lobby-actions">${loaded && !practiceRoom ? `<button class="button ${ready ? "button-secondary" : "button-primary"}" type="button" data-action="toggle-ready">${icon(ready ? "check" : "play")}${ready ? tr.notReady : tr.ready}</button>` : !loaded ? `<button class="button button-primary" type="button" disabled>${icon("shield")}${this.state.assetLoad === "loading" ? tr.mapPreparing : tr.mapMustLoad}</button>` : ""}${host && !practiceRoom ? `<button class="button button-secondary" type="button" data-action="host-start" ${canStart ? "" : "disabled"}>${icon("play")}${tr.startGame}</button>` : ""}<button class="button button-quiet" type="button" data-action="leave-room">${tr.leaveRoom}</button></div>
          ${status}
          ${!practiceRoom && inviteUrl ? `<div class="field"><label for="lobby-invite-url">${tr.inviteLink}</label><input id="lobby-invite-url" data-control="invite-url" type="text" readonly value="${escapeHtml(inviteUrl)}" autocomplete="off" spellcheck="false" /></div>` : ""}
        </section>
        <aside class="surface lobby-side" aria-label="${tr.playArea}"><h3>${tr.playArea}</h3>${host && !challengeRoom && !practiceRoom && ownedRoomVersions.length ? `<div class="host-controls"><div class="field"><label for="lobby-mode">${tr.gameMode}</label><select id="lobby-mode" data-control="host-mode">${modeOptions}</select></div><div class="field"><label for="lobby-map">${tr.mapLabel}</label><select id="lobby-map" data-control="host-map">${mapOptions}</select></div></div>` : challengeRoom ? `<p class="inline-note">${tr.challengeHostOnly}</p>` : practiceRoom ? `<p class="inline-note">${tr.practicePrivacy}</p>` : host && !practiceRoom ? `<p class="inline-note">${tr.mapVersionPinned}</p>` : ""}<div class="lobby-map"><div class="lobby-map-thumb" aria-hidden="true">${this.state.loadedMap?.artifact.artifactHash === snapshot.artifactHash ? `<img src="${escapeHtml(this.state.loadedMap.assetUrls.get(this.state.loadedMap.artifact.backgroundTiles[0]?.assetId ?? "") ?? "")}" alt=""/>` : ""}</div><div><strong>${escapeHtml(roomMapTitle)}</strong><span>${escapeHtml(snapshot.artifactHash.slice(0, 12))}${practiceRoom ? "" : ` · ${modePlayers(snapshot.mode)}`}${snapshot.challengeVersionId || snapshot.challengeId ? ` · ${tr.challengeTitle}` : ""}${practiceRoom ? ` · ${tr.practiceUnranked}` : ""}</span></div></div>${this.state.assetLoad === "error" ? `<button class="button button-small button-secondary" type="button" data-action="retry-asset">${tr.retry}</button>` : ""}<p class="lobby-status">${tr.founder}: ${escapeHtml(snapshot.players.find((player) => player.id === snapshot.hostId)?.nickname ?? "—")}</p></aside>
      </div>
      <p class="orientation-hint"><span>${tr.portraitMode}</span></p>
    </section>`;
  }

  private renderGame(): string {
    const snapshot = this.state.snapshot;
    const mode = snapshot?.mode ?? "scroll_rush";
    const title = modeTitle(mode);
    const phase = this.state.roomPhase ?? snapshot?.phase ?? "racing";
    const ownBestControl = this.canShowOwnBestControl(snapshot)
      ? `<div class="game-ghost-control"><button class="button button-small button-secondary" type="button" data-action="own-best-ghost" aria-pressed="${this.state.ownBestVisible}" ${this.state.ownBestLoad === "loading" || this.state.assetLoad !== "ready" ? "disabled" : ""}>${this.state.ownBestLoad === "loading" ? tr.bestGhostLoading : this.state.ownBestVisible ? tr.hideBestGhost : tr.showBestGhost}</button><span class="field-hint" role="status">${escapeHtml(this.state.ownBestError || (this.state.ownBestVisible ? this.state.ownBestReplay?.nickname ?? "" : ""))}</span></div>`
      : "";
    const practiceRole = this.state.latestScene?.self?.role;
    const rolePicker = snapshot?.practice && mode === "site_hide_seek"
      ? `<div class="practice-role-picker" role="group" aria-label="${tr.practiceRoleLabel}"><span>${tr.practiceRoleLabel}</span><button class="button button-small ${practiceRole === "seeker" ? "button-primary" : "button-secondary"}" type="button" data-action="practice-seeker" aria-pressed="${practiceRole === "seeker"}" ${phase === "searching" ? "" : "disabled"}>${tr.practiceSeeker}</button><button class="button button-small ${practiceRole === "hider" ? "button-primary" : "button-secondary"}" type="button" data-action="practice-hider" aria-pressed="${practiceRole === "hider"}" ${phase === "searching" ? "" : "disabled"}>${tr.practiceHider}</button></div>`
      : "";
    const movementControls = `<div class="control-cluster" aria-label="${tr.movementControls}"><button class="touch-control" type="button" data-game-input="left" aria-label="${tr.moveLeft}" aria-pressed="false">←</button><button class="touch-control" type="button" data-game-input="right" aria-label="${tr.moveRight}" aria-pressed="false">→</button></div>`;
    const jumpControl = `<button class="touch-control" type="button" data-game-input="jump" aria-label="${tr.jump}">${icon("jump")}</button>`;
    const controls = mode === "scroll_rush"
      ? `${movementControls}<div class="control-cluster">${jumpControl}<button class="touch-control" type="button" data-game-input="dash" aria-label="${tr.dash}">${icon("dash")}</button></div>`
      : mode === "site_hide_seek"
        ? `${movementControls}<div class="control-cluster">${jumpControl}<button class="touch-control" type="button" data-action="select-prop" aria-label="${tr.selectProp}" ${this.hideControlAttributes("select_prop")}>E</button><button class="touch-control" type="button" data-action="toggle-disguise" aria-label="${tr.transform}" ${this.hideControlAttributes("toggle_disguise")}>F</button><button class="touch-control" type="button" data-action="scan" aria-label="${tr.scan}" ${this.hideControlAttributes("scan")}>${icon("magnify")}</button></div>`
        : mode === "capture_logo"
          ? `${movementControls}<div class="control-cluster">${jumpControl}<button class="touch-control" type="button" data-game-input="dash" aria-label="${tr.dash}">${icon("dash")}</button><button class="touch-control" type="button" data-action="push" aria-label="${tr.pushAction}">Q</button><button class="touch-control touch-control-hold" type="button" data-game-hold="interact" aria-label="${tr.logoAction}" aria-pressed="false">E</button></div>`
          : mode === "page_brawl"
            ? `${movementControls}<div class="control-cluster">${jumpControl}<button class="touch-control" type="button" data-action="push" aria-label="${tr.pushAction}">Q</button><button class="touch-control" type="button" data-action="area-burst" aria-label="${tr.areaBurst}">R</button></div>`
            : `${movementControls}<div class="control-cluster">${jumpControl}<button class="touch-control touch-control-hold" type="button" data-game-hold="interact" aria-label="${tr.interact}">${tr.interactShort}</button></div>`;
    const overlay = this.state.connectionLost
      ? `<div class="game-overlay"><div class="game-overlay-card"><h2>${tr.matchDisconnected}</h2><p>${escapeHtml(this.state.reconnectMessage)}</p><button class="button button-secondary" type="button" data-action="retry-reconnect">${tr.resumeRoom}</button></div></div>`
      : this.state.formError
        ? `<div class="game-overlay"><div class="game-overlay-card" role="alert"><h2>${tr.multiplayerError}</h2><p>${escapeHtml(this.state.formError)}</p><button class="button button-secondary" type="button" data-action="dismiss-error">${tr.close}</button></div></div>`
      : this.state.latestScene?.self?.caught && phase === "searching"
        ? `<div class="game-overlay"><div class="game-overlay-card"><h2>${tr.caught}</h2><p>${tr.caughtWait}</p></div></div>`
        : this.state.roundResult
          ? `<div class="game-overlay"><div class="game-overlay-card"><span class="eyebrow">${this.state.roundResult.winner === "seeker" ? tr.seekerWon : tr.hidersWon}</span><h2>${this.state.roundResult.round}. ${tr.roundComplete}</h2><p>${tr.nextRoundRoles}</p></div></div>`
          : this.state.assetError && !this.state.latestScene
            ? `<div class="game-overlay"><div class="game-overlay-card"><h2>${tr.assetMismatch}</h2><p>${escapeHtml(this.state.assetError)}</p><button class="button button-secondary" type="button" data-action="leave-room">${tr.leaveRoom}</button></div></div>`
            : "";
    const phaseName = this.phaseLabel(phase);
    const sourceTitle = this.state.loadedMap?.artifact.title ?? title;
    return `<section class="panel-screen panel-screen-wide" aria-label="${escapeHtml(title)}">
      <div class="game-screen">
        <div class="game-toolbar"><div class="game-title" title="${escapeHtml(sourceTitle)}">${icon(modeIcon(mode))}<span class="game-source-title">${escapeHtml(sourceTitle)}</span><span class="game-mode-label">${escapeHtml(title)}</span>${snapshot?.practice ? `<span class="player-sub">${tr.practiceUnranked}</span>` : snapshot ? `<span class="room-code">${escapeHtml(snapshot.roomCode)}</span>` : this.state.localChallengeId ? `<span class="player-sub">${tr.challengeTitle} · ${tr.practiceUnranked}</span>` : `<span class="player-sub">${tr.localLabel}</span>`}</div>${ownBestControl}<div class="game-stats"><span id="phase-label">${phaseName}</span><strong id="game-timer">${formatTime(mode === "site_hide_seek" ? snapshot?.phaseRemainingMs ?? 0 : this.state.latestScene?.self?.elapsedMs ?? 0)}</strong><button class="icon-button" type="button" data-action="leave-game" aria-label="${tr.exitGame}" title="${tr.exitGame}">${icon("exit")}</button></div></div>
        <div class="game-root" id="game-root" tabindex="0" aria-label="${escapeHtml(title)} oyun alanı">
          <details class="game-guide"><summary>${tr.movementControls}</summary><p>${escapeHtml(this.gameHelp(mode))}</p>${mode === "site_hide_seek" ? `<p id="game-role-guide">${escapeHtml(this.hideGameplayGuidance())}</p>` : ""}<p class="game-orientation-note">${tr.portraitMode}</p></details>
          ${overlay}
          ${rolePicker}
          <div class="game-controls">${controls}</div>
        </div>
        <div class="game-footer"><span id="game-hud-status">${escapeHtml(this.state.gameActionFeedback || this.gameHelp(mode))}</span><span>${snapshot ? `${snapshot.players.length} ${tr.playerCount}` : tr.localLabel}</span></div>
      </div>
      <p class="visually-hidden" id="game-announcer" aria-live="polite" aria-atomic="true"></p>
    </section>`;
  }

  private renderLocalResult(): string {
    const result = this.state.localResult;
    if (!result) return `<section class="panel-screen"><p class="inline-note">${tr.dnf}</p></section>`;
    const completed = result.finished;
    const checkpointCount = this.state.loadedMap?.artifact.setup.mode === "scroll_rush" ? this.state.loadedMap.artifact.setup.checkpoints.length : 3;
    return `<section class="panel-screen" aria-labelledby="local-result-title"><button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.homeNav}</button><div class="panel-title-row"><div><span class="eyebrow">${tr.localLabel}</span><h1 id="local-result-title">${completed ? tr.courseComplete : tr.dnf}</h1><p>${completed ? tr.localOnlyResult : tr.timeExpired}</p></div></div><section class="surface result-panel" aria-label="${tr.localCourseResult}"><div class="result-heading"><span class="result-mark">${icon(completed ? "check" : "clock")}</span><div><h2>${escapeHtml(result.mapTitle)}</h2><p>${tr.mapLabel} ${escapeHtml(result.artifactHash.slice(0, 12))} · ${tr.mapVersion} ${escapeHtml(result.simulationVersion)}</p></div></div><div class="result-time"><span>${tr.yourTime}</span><strong>${completed ? formatTime(result.elapsedMs + result.penaltyMs) : "DNF"}</strong></div><div class="result-detail"><div><span>${tr.checkpoints}</span><strong>${result.checkpoint}/${checkpointCount}</strong></div><div><span>${tr.penalty}</span><strong>+${formatTime(result.penaltyMs)}</strong></div></div><p class="inline-note">${tr.privacyNote}</p><div class="result-actions"><button class="button button-secondary" type="button" data-action="local-again" ${this.state.localVersionId ? `data-version-id="${escapeHtml(this.state.localVersionId)}"` : ""}>${icon("refresh")}${tr.playAgain}</button><button class="button button-primary" type="button" data-action="home">${tr.backHome}${icon("arrowRight")}</button></div></section></section>`;
  }

  private renderMatchResult(): string {
    const result = this.state.matchResult;
    if (!result) return `<section class="panel-screen"><p class="inline-note">${tr.results}</p></section>`;
    const rows = result.players.slice().sort((a, b) => a.rank - b.rank).map((player) => {
      const detail = player.finishStatus === "dnf"
        ? tr.dnf
        : result.mode === "site_hide_seek"
          ? `${player.score} ${tr.points}`
          : result.mode === "capture_logo"
            ? `${player.score} ${tr.captureScore}`
            : result.mode === "page_brawl"
              ? `${player.score} ${tr.fallPoints}`
              : result.mode === "browser_boss"
                ? `${player.score}/3 ${tr.bossCycles.toLocaleLowerCase("tr-TR")}`
          : player.elapsedMs === null
            ? tr.roundCompleted
            : `${formatTime(player.elapsedMs)} · +${formatTime(player.penaltyMs)} ${tr.penalty.toLocaleLowerCase("tr-TR")}`;
    const finalColumn = result.practice
        ? result.mode === "scroll_rush" && player.elapsedMs !== null ? formatTime(player.elapsedMs + player.penaltyMs) : tr.practiceUnranked
        : result.mode === "site_hide_seek"
          ? `${player.rank}. ${tr.rankPlace}`
          : result.mode === "scroll_rush"
            ? player.elapsedMs === null ? `${player.rank}. ${tr.rankPlace}` : formatTime(player.elapsedMs + player.penaltyMs)
            : `${player.rank}. ${tr.rankPlace}`;
      return `<li class="history-item"><span><strong>${escapeHtml(player.nickname)}</strong><span>${detail}</span></span><time>${finalColumn}</time></li>`;
    }).join("");
    const saved = result.practice || result.persistence === "practice" ? tr.practiceResult : result.persistence === "saved" ? tr.resultSaved : result.persistence === "pending" ? tr.resultSaving : result.persistence === "unconfigured" ? tr.resultUnconfigured : tr.resultFailed;
    const outcome = result.outcome === "red" ? tr.redTeamWon : result.outcome === "blue" ? tr.blueTeamWon : result.outcome === "draw" ? tr.matchDraw : result.outcome === "victory" ? tr.bossVictory : result.outcome === "defeat" ? tr.bossDefeat : "";
    return `<section class="panel-screen" aria-labelledby="match-result-title"><span class="eyebrow">${escapeHtml(modeTitle(result.mode))}${result.practice ? ` · ${tr.practiceUnranked}` : ""}</span><h1 id="match-result-title">${tr.results}</h1><section class="surface result-panel"><div class="result-heading"><span class="result-mark">${icon(result.outcome === "defeat" ? "clock" : "check")}</span><div><h2>${result.practice ? tr.practiceUnranked : this.state.snapshot ? escapeHtml(this.state.snapshot.roomCode) : "Özel oda"}</h2><p>Harita ${escapeHtml(result.artifactHash.slice(0, 12))}</p></div></div>${outcome ? `<p class="result-outcome">${outcome}</p>` : ""}<ol class="history-list">${rows}</ol><p class="${result.persistence === "failed" ? "inline-error" : "inline-note"}" role="status">${saved}</p>${this.state.formError ? `<p class="inline-error" role="alert">${escapeHtml(this.state.formError)}</p>` : ""}<div class="result-actions">${result.challengeId ? `<button class="button button-secondary" type="button" data-action="open-challenge-result">${icon("challenge")}${tr.challengeTitle}</button>` : ""}${result.practice ? `<button class="button button-secondary" type="button" data-action="practice-again" data-mode="${result.mode}">${icon("refresh")}${tr.practiceStart}</button>` : `<button class="button button-secondary" type="button" data-action="rematch">${icon("refresh")}${tr.rematch}</button>`}<button class="button button-quiet" type="button" data-action="leave-room">${this.roomSourceReturnTarget() ? tr.backToModeSelection : tr.backHome}${icon("arrowRight")}</button></div></section></section>`;
  }

  private renderSettings(): string {
    return `<section class="panel-screen" aria-labelledby="settings-title"><button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.homeNav}</button><div class="panel-title-row"><div><span class="eyebrow">${tr.personalPreferences}</span><h1 id="settings-title">${tr.settingsTitle}</h1><p>${tr.localSettings}</p></div></div><section class="surface form-surface"><div class="settings-list"><div class="settings-row"><div><strong>${tr.sound}</strong><span>${tr.localSoundsDescription}</span></div><button class="switch" role="switch" aria-checked="${this.preferences.soundEnabled}" data-setting="soundEnabled" aria-label="${tr.sound}"></button></div><div class="settings-row"><div><strong>${tr.reducedMotion}</strong><span>${tr.reducedMotionDescription}</span></div><button class="switch" role="switch" aria-checked="${this.preferences.reducedMotion}" data-setting="reducedMotion" aria-label="${tr.reducedMotion}"></button></div></div><label class="field quality-setting" for="quality-setting"><span>${tr.visualQuality}</span><select id="quality-setting" data-setting="quality"><option value="economy" ${this.preferences.quality === "economy" ? "selected" : ""}>${tr.qualityEconomy}</option><option value="balanced" ${this.preferences.quality === "balanced" ? "selected" : ""}>${tr.qualityBalanced}</option><option value="high" ${this.preferences.quality === "high" ? "selected" : ""}>${tr.qualityHigh}</option></select><span class="field-hint">${tr.visualQualityHelp}</span></label><p class="hero-footnote">${icon("shield")}${tr.anonymousSessionNote}</p><div class="form-actions"><button class="button button-secondary" type="button" data-action="history">${icon("book")}${tr.historyTitle}</button><button class="button button-primary" type="button" data-action="home">${tr.close}</button></div></section></section>`;
  }

  private renderHistory(): string {
    const locals = readLocalScores();
    const online = this.state.history.map((result) => `<li class="history-item"><span><strong>${escapeHtml(modeTitle(result.mode))} · ${escapeHtml(result.artifactHash.slice(0, 8))}</strong><span>${formatDate(result.endedAt)} · ${tr.onlineResult}</span></span><time>${result.players.length} ${tr.playerCount}</time></li>`).join("");
    const localItems = locals.map((score) => this.renderHistoryScore(score)).join("");
    const loading = this.state.historyLoad === "loading" ? `<p class="status-live"><span class="loading-indicator"><i class="spinner"></i>${tr.historyLoading}</span></p>` : "";
    const error = this.state.historyError ? `<p class="inline-note" role="status">${escapeHtml(this.state.historyError)}</p>` : "";
    return `<section class="panel-screen" aria-labelledby="history-title"><button class="back-link" type="button" data-action="home">${icon("arrowLeft")}${tr.homeNav}</button><div class="panel-title-row"><div><span class="eyebrow">${tr.localAndOnline}</span><h1 id="history-title">${tr.matchHistory}</h1><p>${tr.historyDescription}</p></div></div><section class="surface form-surface history-surface"><h2 class="section-label">${tr.localCourses}</h2>${locals.length ? `<ol class="history-list">${localItems}</ol>` : `<p class="field-hint">${tr.noHistory}</p>`}<p class="field-hint">${tr.privacyNote}</p><div class="form-actions"><button class="button button-small button-danger" type="button" data-action="clear-history" ${locals.length ? "" : "disabled"}>${tr.historyClear}</button></div></section><section class="surface form-surface history-surface"><h2 class="section-label">${tr.onlineMatches}</h2>${error}${loading}${online ? `<ol class="history-list">${online}</ol>` : this.state.historyLoad === "ready" ? `<p class="field-hint">${tr.noOnlineHistory}</p>` : ""}${this.multiplayer.enabled ? `<div class="form-actions"><button class="button button-small button-secondary" type="button" data-action="reload-history">${tr.retry}</button></div>` : `<p class="inline-note">${escapeHtml(multiplayerDisabledMessage())}</p>`}</section></section>`;
  }

  private renderHistoryScore(score: LocalScore): string {
    const total = score.elapsedMs + score.penaltyMs;
    return `<li class="history-item"><span><strong>${escapeHtml(score.mapTitle)}</strong><span>${formatDate(score.completedAt)} · ${score.finished === false ? tr.dnf : `+${formatTime(score.penaltyMs)} ${tr.penalty.toLocaleLowerCase()}`}</span></span><time>${score.finished === false ? "DNF" : formatTime(total)}</time></li>`;
  }

  private phaseLabel(phase: RoomPhase): string {
    const labels: Record<RoomPhase, string> = { lobby: tr.lobbyWaiting, loading: tr.phaseLoading, countdown: tr.phaseCountdown, hiding: tr.phaseHiding, searching: tr.phaseSearching, racing: tr.phaseRacing, playing: tr.phasePlaying, round_result: tr.phaseRoundResult, match_result: tr.results, paused: tr.phasePaused, aborted: tr.phaseAborted, closed: tr.phaseClosed };
    return labels[phase];
  }

  private navigate(path: string): void {
    this.cancelPendingRoomRequest();
    this.cancelLocalVersionLoad();
    window.history.pushState(null, "", path);
    this.handleLocation();
  }

  private cancelLocalVersionLoad(): void {
    if (!this.localVersionAbort) return;
    this.localVersionAbort.abort();
    this.localVersionAbort = null;
    this.localVersionRequest++;
    if (this.state.previewLoad === "loading") this.state.previewLoad = "idle";
  }

  private cancelPendingRoomRequest(): void {
    if (!this.roomRequestAbort) return;
    this.roomRequestId++;
    this.roomRequestAbort.abort();
    this.roomRequestAbort = null;
    this.state.busy = false;
  }

  private async submitConversion(rawUrl: string): Promise<void> {
    this.state.formError = "";
    this.state.conversionError = "";
    if (!this.multiplayer.enabled) {
      this.state.formError = tr.captureNotConfigured;
      this.render();
      return;
    }
    let url: string;
    try { url = normalizeUserUrl(rawUrl); }
    catch (error) {
      this.state.formError = this.conversionErrorText(error instanceof Error ? error.message : "INVALID_URL");
      this.render();
      this.root.querySelector<HTMLInputElement>("#source-url")?.focus();
      return;
    }
    this.conversionSourceDraft = url;
    this.state.urlDraft = url;
    this.state.busy = true;
    this.render();
    try {
      const requestId = await this.conversions.create({ url, locale: getLocale(), captureProfile: "desktop-page" });
      this.state.conversionRequestId = requestId;
      this.state.conversionStatus = null;
      this.state.conversionLoad = "loading";
      this.state.busy = false;
      this.navigate(`/build/${encodeURIComponent(requestId)}`);
    } catch (error) {
      this.state.busy = false;
      this.state.formError = this.productErrorText(error);
      this.render();
      this.root.querySelector<HTMLInputElement>("#source-url")?.focus();
    }
  }

  private async refreshConversion(requestId: string, showLoading = false): Promise<void> {
    window.clearTimeout(this.conversionPollHandle);
    const generation = ++this.conversionGeneration;
    this.state.conversionRequestId = requestId;
    if (showLoading) this.state.conversionLoad = "loading";
    this.state.conversionError = "";
    this.render();
    try {
      const request = await this.conversions.get(requestId);
      if (generation !== this.conversionGeneration || this.state.conversionRequestId !== requestId) return;
      this.state.conversionStatus = request;
      this.state.conversionLoad = "ready";
      this.state.conversionError = "";
      const isCurrentBuildRoute = this.state.screen === "build" &&
        this.state.conversionRequestId === requestId &&
        window.location.pathname === `/build/${encodeURIComponent(requestId)}`;
      if (request.phase === "ready" && request.mapId && isCurrentBuildRoute) {
        if (this.state.mapView?.id === request.mapId) {
          this.state.mapView = null;
          this.state.mapViewLoad = "idle";
          this.state.mapViewError = "";
          this.state.previewVersionId = "";
          this.state.previewLoad = "idle";
          this.state.previewError = "";
        }
        this.navigate(`/maps/${encodeURIComponent(request.mapId)}`);
        return;
      }
      this.render();
      if (!["ready", "rejected", "failed", "cancelled", "expired"].includes(request.phase)) {
        this.conversionPollHandle = window.setTimeout(() => void this.refreshConversion(requestId), 1800);
      }
    } catch (error) {
      if (generation !== this.conversionGeneration || this.state.conversionRequestId !== requestId) return;
      this.state.conversionLoad = "error";
      this.state.conversionError = this.productErrorText(error);
      this.render();
    }
  }

  private async cancelConversion(): Promise<void> {
    const requestId = this.state.conversionRequestId;
    if (!requestId || this.state.busy) return;
    this.state.busy = true;
    this.render();
    try {
      this.state.conversionStatus = await this.conversions.cancel(requestId).then(() => this.conversions.get(requestId));
      this.state.conversionLoad = "ready";
      this.state.conversionError = "";
    } catch (error) {
      this.state.conversionError = this.productErrorText(error);
    } finally {
      this.state.busy = false;
      this.render();
    }
  }

  private async retryConversion(): Promise<void> {
    const requestId = this.state.conversionRequestId;
    if (!requestId || this.state.busy) return;
    this.state.busy = true;
    this.render();
    try {
      const nextId = await this.conversions.retry(requestId);
      this.state.conversionRequestId = nextId;
      this.state.conversionStatus = null;
      this.state.conversionLoad = "loading";
      this.state.busy = false;
      this.navigate(`/build/${encodeURIComponent(nextId)}`);
    } catch (error) {
      this.state.busy = false;
      this.state.conversionError = this.productErrorText(error);
      this.render();
    }
  }

  private async loadMapView(mapId: string): Promise<void> {
    const accountGeneration = this.accountGeneration;
    this.state.mapViewLoad = "loading";
    this.state.mapViewError = "";
    this.render();
    try {
      const map = await this.productApi.getMap(mapId);
      if (accountGeneration !== this.accountGeneration) return;
      if ((this.state.screen !== "map_detail" && this.state.screen !== "map_edit") || window.location.pathname.split("/")[2] !== mapId) return;
      const cachedMapId = this.state.mapView?.id;
      const cachedPreviewVersionId = this.state.previewVersionId;
      const cachedPreviewReady = this.state.previewLoad === "ready";
      this.state.mapView = map;
      this.state.mapViewLoad = "ready";
      if (this.state.screen === "map_edit") {
        this.state.mapEditMode = map.versions[0]?.mode ?? "scroll_rush";
        this.state.editRegion = null;
      }
      if (this.state.screen === "map_detail") {
        const selectedMode = this.firstPlayableMapMode(map);
        this.state.selectedMode = selectedMode ?? "scroll_rush";
        const selectedVersion = selectedMode ? this.modeVersionForSelection(map, selectedMode) : undefined;
        this.state.previewVersionId = selectedVersion?.id ?? "";
        this.state.previewError = "";
        const exactCachedPreview = cachedMapId === map.id && cachedPreviewReady && cachedPreviewVersionId === selectedVersion?.id &&
          this.state.privatePreview?.artifact.artifactHash === selectedVersion?.artifactHash;
        this.state.previewLoad = exactCachedPreview ? "ready" : "idle";
      }
      this.render();
      if (this.state.screen === "map_detail" && this.state.previewLoad !== "ready") {
        const selectedMode = this.firstPlayableMapMode(map);
        const selectedVersion = selectedMode ? this.modeVersionForSelection(map, selectedMode) : undefined;
        if (selectedVersion) void this.loadAuthorizedPreview(selectedVersion.id);
      }
      if (!this.state.profile) void this.loadProfile();
    } catch (error) {
      if (accountGeneration !== this.accountGeneration) return;
      this.state.mapView = null;
      this.state.mapViewLoad = "error";
      this.state.mapViewError = this.productErrorText(error);
      this.render();
    }
  }

  private async loadProfile(force = false): Promise<void> {
    if (!force && this.state.profileLoad === "loading") return;
    const accountGeneration = this.accountGeneration;
    this.state.profileLoad = "loading";
    this.state.profileError = "";
    try {
      const profile = await this.productApi.getProfile();
      if (accountGeneration !== this.accountGeneration) return;
      this.state.profile = profile;
      this.state.profileLoad = "ready";
      this.state.profileError = "";
      if (this.localeDirty && this.state.profile.locale !== getLocale()) {
        try {
          const updated = await this.productApi.updateProfile({ locale: getLocale() });
          if (accountGeneration !== this.accountGeneration) return;
          this.state.profile = updated;
          this.localeDirty = false;
        } catch (error) {
          if (accountGeneration !== this.accountGeneration) return;
          this.state.profileError = this.productErrorText(error);
        }
      } else if (!this.localeDirty) {
        setLocale(this.state.profile.locale);
      }
      if (this.state.screen !== "game") this.render();
    } catch (error) {
      if (accountGeneration !== this.accountGeneration) return;
      this.state.profileLoad = "error";
      this.state.profileError = this.productErrorText(error);
      if (this.state.screen === "profile" || this.state.screen === "admin") this.render();
    }
  }

  private async deleteAccount(): Promise<void> {
    if (!this.state.profile || this.profileDeleteBusy || this.state.busy || !window.confirm(tr.deleteAccountConfirm)) return;
    this.profileDeleteBusy = true;
    this.profileDeleteError = "";
    this.render();
    try {
      await this.productApi.deleteAccount();
    } catch (error) {
      this.profileDeleteError = this.productErrorText(error) || tr.accountDeleteFailed;
      this.profileDeleteBusy = false;
      this.render();
      return;
    }

    this.accountGeneration++;
    const leaving = this.leaveRoom();
    this.state.screen = "home";
    this.clearQuery();
    this.render();
    this.clearDeletedAccountData();
    await Promise.allSettled([leaving, this.multiplayer.clearDeletedAccountSession()]);
    this.profileDeleteBusy = false;
    this.profileDeleteError = "";
    this.state.notice = tr.accountDeleted;
    this.announce(tr.accountDeleted);
    this.render();
  }

  private clearDeletedAccountData(): void {
    this.cancelLocalVersionLoad();
    this.privateAssetAbort?.abort();
    this.privateAssetAbort = null;
    this.privateAssetGeneration++;
    const privatePreview = this.state.privatePreview;
    privatePreview?.dispose();
    if (this.state.loadedMap && this.state.loadedMap !== privatePreview) this.state.loadedMap.dispose();
    this.state.privatePreview = null;
    this.state.previewVersionId = "";
    this.state.loadedMap = null;
    this.state.mapView = null;
    this.state.mapViewLoad = "idle";
    this.state.mapViewError = "";
    this.state.profile = null;
    this.state.profileLoad = "idle";
    this.state.profileError = "";
    this.state.libraryEntries = [];
    this.state.libraryLoad = "idle";
    this.state.libraryError = "";
    this.state.history = [];
    this.state.historyLoad = "idle";
    this.state.historyError = "";
    this.state.discoverPage = null;
    this.state.discoverRooms = [];
    this.state.challenge = null;
    this.state.challengeRankings = [];
    this.state.challengeReplay = null;
    this.state.creatorChallenge = null;
    this.state.creatorChallengeId = "";
    this.state.creatorSourceBounds = null;
    this.state.creatorChallengeRankings = [];
    this.state.creatorShare = null;
    this.state.shareView = null;
    this.state.adminSummary = null;
    this.state.urlDraft = "";
    this.state.conversionRequestId = "";
    this.state.conversionStatus = null;
    this.state.conversionLoad = "idle";
    this.state.conversionError = "";
    this.state.notice = "";
    this.conversionSourceDraft = "";
    this.conversionGeneration++;
    window.clearTimeout(this.conversionPollHandle);
  }

  private async loadLibrary(): Promise<void> {
    const accountGeneration = this.accountGeneration;
    this.state.libraryLoad = "loading";
    this.state.libraryError = "";
    this.render();
    try {
      const entries = await this.productApi.getLibrary();
      if (accountGeneration !== this.accountGeneration) return;
      this.state.libraryEntries = entries;
      this.state.libraryLoad = "ready";
    } catch (error) {
      if (accountGeneration !== this.accountGeneration) return;
      this.state.libraryError = this.productErrorText(error);
      this.state.libraryLoad = "error";
    }
    if (this.state.screen === "library") this.render();
  }

  private async loadDiscover(cursor?: string | null): Promise<void> {
    this.state.discoverLoad = "loading";
    this.state.discoverError = "";
    this.render();
    const [maps, rooms] = await Promise.allSettled([
      this.productApi.discover({ mode: this.state.discoverMode || undefined, cursor }),
      this.productApi.publicRooms(),
    ]);
    if (maps.status === "fulfilled") {
      const page = maps.value;
      this.state.discoverPage = cursor && this.state.discoverPage
        ? { items: [...this.state.discoverPage.items, ...page.items], nextCursor: page.nextCursor }
        : page;
    }
    if (rooms.status === "fulfilled") this.state.discoverRooms = rooms.value;
    const failures = [maps, rooms].filter((result): result is PromiseRejectedResult => result.status === "rejected");
    this.state.discoverError = failures.length ? this.productErrorText(failures[0]?.reason) : "";
    this.state.discoverLoad = "ready";
    if (this.state.screen === "discover") this.render();
  }

  private async loadAdminSummary(): Promise<void> {
    const accountGeneration = this.accountGeneration;
    this.state.adminLoad = "loading";
    this.state.adminError = "";
    this.render();
    try {
      const summary = await this.productApi.getAdminSummary();
      if (accountGeneration !== this.accountGeneration) return;
      this.state.adminSummary = summary;
      this.state.adminLoad = "ready";
    } catch (error) {
      if (accountGeneration !== this.accountGeneration) return;
      this.state.adminError = this.productErrorText(error);
      this.state.adminLoad = "error";
    }
    if (this.state.screen === "admin") this.render();
  }

  private async loadAuthorizedPreview(versionId: string, externalSignal?: AbortSignal): Promise<void> {
    if (externalSignal?.aborted) throw new DOMException("Private preview load cancelled.", "AbortError");
    const version = this.state.mapView?.versions.find((item) => item.id === versionId)
      ?? this.state.creatorChallenge?.versions.find((item) => item.id === versionId);
    if (!version) {
      this.state.previewError = tr.noMapVersion;
      this.state.previewLoad = "error";
      this.render();
      return;
    }
    this.privateAssetAbort?.abort();
    const controller = new AbortController();
    this.privateAssetAbort = controller;
    const abortFromRequest = () => controller.abort();
    externalSignal?.addEventListener("abort", abortFromRequest, { once: true });
    const generation = ++this.privateAssetGeneration;
    const restoreModeFocus = this.focusedMapModeButton();
    this.state.previewVersionId = versionId;
    this.state.previewLoad = "loading";
    this.state.previewError = "";
    this.render();
    if (restoreModeFocus) this.focusMapModeButton(restoreModeFocus);
    try {
      const loaded = await loadAuthorizedMapVersion(this.productApi, version, controller.signal);
      if (generation !== this.privateAssetGeneration || controller.signal.aborted) { loaded.dispose(); return; }
      if (this.state.privatePreview && this.state.privatePreview !== this.state.loadedMap) this.state.privatePreview.dispose();
      this.state.privatePreview = loaded;
      this.state.previewLoad = "ready";
      this.state.previewError = "";
      this.state.assetLoad = "ready";
      this.state.assetError = "";
      if (!this.state.editRegion && "region" in loaded.artifact && loaded.artifact.region) this.state.editRegion = loaded.artifact.region;
    } catch (error) {
      if (generation !== this.privateAssetGeneration || controller.signal.aborted) return;
      this.state.previewLoad = "error";
      this.state.previewError = this.conversionErrorText(error instanceof Error ? error.message : "ASSET_MISSING");
    } finally {
      externalSignal?.removeEventListener("abort", abortFromRequest);
      if (generation === this.privateAssetGeneration) {
        this.privateAssetAbort = null;
        if (this.state.screen !== "game") {
          this.render();
          if (restoreModeFocus && this.state.screen === "map_detail") this.focusMapModeButton(restoreModeFocus);
        }
      }
    }
  }

  private focusedMapModeButton(): ActiveMode | undefined {
    const focused = document.activeElement instanceof HTMLElement
      ? document.activeElement.closest<HTMLButtonElement>('button[data-action="select-map-mode"]')
      : null;
    const parsed = ActiveModeSchema.safeParse(focused?.dataset.mode);
    return parsed.success ? parsed.data : undefined;
  }

  private focusMapModeButton(mode: ActiveMode): void {
    this.root.querySelector<HTMLButtonElement>(`button[data-action="select-map-mode"][data-mode="${mode}"]`)?.focus();
  }

  private patchNumber(name: string, label: string, value: number, min: number, max: number, step = 1): string {
    return `<label class="field patch-number" for="patch-${name}"><span>${label}</span><input id="patch-${name}" name="${name}" type="number" min="${min}" max="${max}" step="${step}" value="${Number.isInteger(value) ? value : Number(value.toFixed(1))}" required /></label>`;
  }

  private patchPoint(name: string, title: string, point: { x: number; y: number }, world: { width: number; height: number }): string {
    return `<fieldset class="patch-group"><legend>${title}</legend><div class="patch-coordinate-grid">${this.patchNumber(`${name}-x`, tr.regionX, point.x, 0, world.width)}${this.patchNumber(`${name}-y`, tr.regionY, point.y, 0, world.height)}</div></fieldset>`;
  }

  private patchRect(name: string, title: string, rect: Rect, world: { width: number; height: number }): string {
    return `<fieldset class="patch-group"><legend>${title}</legend><div class="patch-coordinate-grid">${this.patchNumber(`${name}-x`, tr.regionX, rect.x, 0, world.width)}${this.patchNumber(`${name}-y`, tr.regionY, rect.y, 0, world.height)}${this.patchNumber(`${name}-width`, tr.regionWidth, rect.width, 1, world.width)}${this.patchNumber(`${name}-height`, tr.regionHeight, rect.height, 1, world.height)}</div></fieldset>`;
  }

  private renderCreatorSetupControls(loaded: LoadedMap, region: Rect | null | undefined, sourceBounds: Rect): string {
    const { artifact } = loaded;
    const setup = artifact.setup;
    const world = artifact.world;
    const bounds = region ?? artifact.region ?? { x: 0, y: 0, width: world.width, height: Math.min(900, world.height) };
    const sourceRegionMaxHeight = setup.mode === "scroll_rush" ? sourceBounds.height : Math.min(3600, sourceBounds.height);
    const schema4Ranges = artifact.schemaVersion === 4 ? this.creatorSchema4TileRanges(loaded, sourceBounds, setup.mode) : null;
    const currentRangeIsSelectable = schema4Ranges?.some((candidate) => this.sameRegion(candidate, bounds)) ?? false;
    const rangeOption = (candidate: Rect): string => {
      const end = candidate.y + candidate.height;
      const label = tr.creatorRegionTileRangeOption
        .replace("{start}", new Intl.NumberFormat(getLocale()).format(candidate.y))
        .replace("{end}", new Intl.NumberFormat(getLocale()).format(end))
        .replace("{height}", new Intl.NumberFormat(getLocale()).format(candidate.height));
      return `<option value="${candidate.y}:${end}" data-start="${candidate.y}" data-end="${end}" ${this.sameRegion(candidate, bounds) ? "selected" : ""}>${escapeHtml(label)}</option>`;
    };
    const unavailableCurrentOption = currentRangeIsSelectable ? "" : `<option value="" disabled selected>${escapeHtml(tr.creatorRegionTileRangeCurrent.replace("{start}", new Intl.NumberFormat(getLocale()).format(bounds.y)).replace("{end}", new Intl.NumberFormat(getLocale()).format(bounds.y + bounds.height)))}</option>`;
    const regionFields = schema4Ranges
      ? `<fieldset class="region-fields"><legend>${tr.selectedRegion}</legend><p class="field-hint">${escapeHtml(tr.creatorRegionFullWidth.replace("{width}", new Intl.NumberFormat(getLocale()).format(bounds.width)))}</p><label class="field" for="region-tile-range"><span>${tr.creatorRegionTileRangeLabel}</span><select id="region-tile-range" name="region-tile-range" data-control="region-tile-range" aria-describedby="region-tile-range-help region-tile-range-error" ${!schema4Ranges.length || this.state.busy ? "disabled" : ""}>${unavailableCurrentOption}${schema4Ranges.map(rangeOption).join("")}</select></label><p id="region-tile-range-help" class="field-hint">${escapeHtml(tr.creatorRegionTileRangeHelp)}</p><p id="region-tile-range-error" class="inline-error" role="alert" data-region-tile-error ${currentRangeIsSelectable ? "hidden" : ""}>${escapeHtml(schema4Ranges.length ? tr.creatorRegionTileRangeCurrentInvalid : tr.creatorRegionTileRangeUnavailable)}</p><input type="hidden" name="region-x" value="${bounds.x}" /><input type="hidden" name="region-y" value="${bounds.y}" /><input type="hidden" name="region-width" value="${bounds.width}" /><input type="hidden" name="region-height" value="${bounds.height}" /></fieldset>`
      : `<fieldset class="region-fields"><legend>${tr.selectedRegion}</legend><div class="patch-coordinate-grid">${this.patchNumber("region-x", tr.regionX, bounds.x, sourceBounds.x, sourceBounds.x + sourceBounds.width)}${this.patchNumber("region-y", tr.regionY, bounds.y, sourceBounds.y, sourceBounds.y + sourceBounds.height)}${this.patchNumber("region-width", tr.regionWidth, bounds.width, 1, sourceBounds.width)}${this.patchNumber("region-height", tr.regionHeight, bounds.height, 1, sourceRegionMaxHeight)}</div></fieldset>`;
    const currentSupports = artifact.creatorPatch?.supports ?? [];
    const supports = `<fieldset class="patch-section"><legend>${tr.supportPalette}</legend><p class="field-hint">${tr.supportPaletteHelp}</p>${Array.from({ length: 6 }, (_, index) => {
      const support = currentSupports[index];
      const defaultX = Math.max(0, Math.min(world.width - 96, 32 + index * 112));
      const defaultY = Math.max(0, Math.min(world.height - 20, 240 + index * 64));
      const rect = support ?? { x: defaultX, y: defaultY, width: 96, height: 16 };
      return `<div class="patch-support"><label class="check-row"><input type="checkbox" name="support-enabled-${index}" ${support ? "checked" : ""} /><span>${tr.supportPlatform} ${index + 1}</span></label>${this.patchRect(`support-${index}`, tr.supportBounds, rect, world)}</div>`;
    }).join("")}</fieldset>`;

    let modeFields = "";
    if (setup.mode === "scroll_rush") {
      const checkpoints = Array.from({ length: 5 }, (_, index) => {
        const existing = setup.checkpoints[index];
        if (existing) return existing;
        const start = setup.checkpoints[0]!;
        const finish = setup.finish;
        const t = (index + 1) / 6;
        return {
          x: start.x + (finish.x - start.x) * t,
          y: start.y + (finish.y - start.y) * t,
          width: start.width,
          height: start.height,
        };
      });
      modeFields = `<fieldset class="patch-section"><legend>${tr.rushLabel}</legend>${this.patchPoint("spawn", tr.spawnPoint, setup.spawn, world)}<label class="field" for="patch-checkpoint-count"><span>${tr.checkpointCount}</span><select id="patch-checkpoint-count" name="checkpointCount">${[2, 3, 4, 5].map((count) => `<option value="${count}" ${count === setup.checkpoints.length ? "selected" : ""}>${count}</option>`).join("")}</select></label>${checkpoints.map((checkpoint, index) => this.patchRect(`checkpoint-${index}`, `${tr.checkpoint} ${index + 1}`, checkpoint, world)).join("")}${this.patchRect("finish", tr.finishLine, setup.finish, world)}${this.patchNumber("time-limit-ms", tr.timeLimitMs, setup.timeLimitMs, 60000, 600000, 10000)}</fieldset>`;
    } else if (setup.mode === "site_hide_seek") {
      const props = artifact.creatorPatch?.mode === "site_hide_seek" ? artifact.creatorPatch.propTemplateIds : artifact.propTemplates.map((prop) => prop.id);
      modeFields = `<fieldset class="patch-section"><legend>${tr.hideLabel}</legend>${this.patchPoint("seeker-spawn", tr.seekerSpawn, setup.seekerSpawn, world)}${setup.hiderSpawns.map((point, index) => this.patchPoint(`hider-${index}`, `${tr.hiderSpawn} ${index + 1}`, point, world)).join("")}<label class="field" for="patch-decoy-count"><span>${tr.decoyCount}</span><input id="patch-decoy-count" name="decoyCount" type="number" min="8" max="24" step="1" value="${artifact.creatorPatch?.mode === "site_hide_seek" ? artifact.creatorPatch.decoyCount : 18}" required /></label><fieldset class="patch-choice-list"><legend>${tr.disguisePool}</legend>${artifact.propTemplates.map((prop) => {
        const image = loaded.assetUrls.get(prop.assetId);
        return `<label class="patch-choice"><input type="checkbox" name="propTemplateIds" value="${escapeHtml(prop.id)}" ${props.includes(prop.id) ? "checked" : ""} />${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" />` : ""}<span>${escapeHtml(prop.id)}</span></label>`;
      }).join("")}</fieldset></fieldset>`;
    } else if (setup.mode === "capture_logo") {
      const count = Math.max(setup.teamSpawns.red.length, setup.teamSpawns.blue.length);
      const points = (team: "red" | "blue", index: number) => {
        const list = setup.teamSpawns[team];
        if (list[index]) return list[index]!;
        const last = list.at(-1)!;
        return { x: Math.max(0, Math.min(world.width, last.x + (team === "red" ? 32 : -32))), y: last.y };
      };
      modeFields = `<fieldset class="patch-section"><legend>${tr.captureLabel}</legend><label class="field" for="patch-team-spawn-count"><span>${tr.teamSpawnCount}</span><select id="patch-team-spawn-count" name="teamSpawnCount">${[2, 3].map((size) => `<option value="${size}" ${size === count ? "selected" : ""}>${size}</option>`).join("")}</select></label><div class="patch-team-grid"><section><h3>${tr.redTeam}</h3>${Array.from({ length: 3 }, (_, index) => this.patchPoint(`red-spawn-${index}`, `${tr.spawnPoint} ${index + 1}`, points("red", index), world)).join("")}${this.patchRect("red-zone", tr.redDeliveryZone, setup.teamZones.red, world)}</section><section><h3>${tr.blueTeam}</h3>${Array.from({ length: 3 }, (_, index) => this.patchPoint(`blue-spawn-${index}`, `${tr.spawnPoint} ${index + 1}`, points("blue", index), world)).join("")}${this.patchRect("blue-zone", tr.blueDeliveryZone, setup.teamZones.blue, world)}</section></div>${this.patchPoint("logo-spawn", tr.logoSpawn, setup.logoSpawn, world)}</fieldset>`;
    } else if (setup.mode === "page_brawl") {
      const selected = artifact.creatorPatch?.mode === "page_brawl" ? artifact.creatorPatch.destructibleIds : setup.breakables.map((item) => item.id);
      modeFields = `<fieldset class="patch-section"><legend>${tr.brawlLabel}</legend>${setup.spawns.map((point, index) => this.patchPoint(`brawl-spawn-${index}`, `${tr.safeSpawn} ${index + 1}`, point, world)).join("")}<fieldset class="patch-choice-list"><legend>${tr.breakableSelection}</legend>${setup.breakables.map((item) => `<label class="check-row"><input type="checkbox" name="destructibleIds" value="${escapeHtml(item.id)}" ${selected.includes(item.id) ? "checked" : ""} /><span>${escapeHtml(item.id)}</span></label>`).join("")}</fieldset></fieldset>`;
    } else {
      const attackCount = artifact.creatorPatch?.mode === "browser_boss" ? Math.min(setup.attackZones.length, artifact.creatorPatch.attackZones.length) : setup.attackZones.length;
      modeFields = `<fieldset class="patch-section"><legend>${tr.bossLabel}</legend>${setup.spawns.map((point, index) => this.patchPoint(`boss-spawn-${index}`, `${tr.safeSpawn} ${index + 1}`, point, world)).join("")}<h3>${tr.bossSwitches}</h3>${setup.switches.map((item, index) => this.patchRect(`boss-switch-${index}`, `${tr.bossSwitches} ${index + 1}`, item.bounds, world)).join("")}<input type="hidden" name="boss-switch-0-id" value="${escapeHtml(setup.switches[0]!.id)}" /><input type="hidden" name="boss-switch-1-id" value="${escapeHtml(setup.switches[1]!.id)}" /><input type="hidden" name="boss-switch-2-id" value="${escapeHtml(setup.switches[2]!.id)}" />${this.patchRect("weak-point", tr.weakPoint, setup.weakPoint, world)}<label class="field" for="patch-attack-zone-count"><span>${tr.attackZoneCount}</span><select id="patch-attack-zone-count" name="attackZoneCount">${setup.attackZones.map((_, index) => index + 1).map((count) => `<option value="${count}" ${count === attackCount ? "selected" : ""}>${count}</option>`).join("")}</select></label>${setup.attackZones.map((zone, index) => this.patchRect(`attack-zone-${index}`, `${tr.attackZone} ${index + 1}`, zone, world)).join("")}</fieldset>`;
    }
    return `<div class="creator-setup-controls">${regionFields}${modeFields}${supports}</div>`;
  }

  private creatorPatchFromForm(artifact: LoadedMap["artifact"], values: FormData) {
    const number = (name: string): number => Number(values.get(name));
    const point = (name: string) => ({ x: number(`${name}-x`), y: number(`${name}-y`) });
    const rect = (name: string): Rect => ({ x: number(`${name}-x`), y: number(`${name}-y`), width: number(`${name}-width`), height: number(`${name}-height`) });
    const setup = artifact.setup;
    const region: Rect = rect("region");
    const supports = Array.from({ length: 6 }, (_, index) => values.get(`support-enabled-${index}`) === "on" ? rect(`support-${index}`) : null).filter((item): item is Rect => item !== null);
    const common = { region, ...(supports.length ? { supports } : {}) };
    let patch: unknown;
    if (setup.mode === "scroll_rush") {
      const count = number("checkpointCount");
      patch = { mode: setup.mode, ...common, spawn: point("spawn"), checkpoints: Array.from({ length: count }, (_, index) => rect(`checkpoint-${index}`)), finish: rect("finish"), timeLimitMs: number("time-limit-ms") };
    } else if (setup.mode === "site_hide_seek") {
      patch = { mode: setup.mode, ...common, seekerSpawn: point("seeker-spawn"), hiderSpawns: setup.hiderSpawns.map((_, index) => point(`hider-${index}`)), propTemplateIds: values.getAll("propTemplateIds").map(String), decoyCount: number("decoyCount") };
    } else if (setup.mode === "capture_logo") {
      const count = number("teamSpawnCount");
      patch = { mode: setup.mode, ...common, teamSpawns: { red: Array.from({ length: count }, (_, index) => point(`red-spawn-${index}`)), blue: Array.from({ length: count }, (_, index) => point(`blue-spawn-${index}`)) }, teamZones: { red: rect("red-zone"), blue: rect("blue-zone") }, logoSpawn: point("logo-spawn") };
    } else if (setup.mode === "page_brawl") {
      patch = { mode: setup.mode, ...common, spawns: setup.spawns.map((_, index) => point(`brawl-spawn-${index}`)), destructibleIds: values.getAll("destructibleIds").map(String) };
    } else {
      patch = { mode: setup.mode, ...common, spawns: setup.spawns.map((_, index) => point(`boss-spawn-${index}`)), switches: setup.switches.map((item, index) => ({ id: String(values.get(`boss-switch-${index}-id`) ?? item.id), bounds: rect(`boss-switch-${index}`) })), weakPoint: rect("weak-point"), attackZones: Array.from({ length: number("attackZoneCount") }, (_, index) => rect(`attack-zone-${index}`)) };
    }
    return CreatorPatchSchema.parse(patch);
  }

  private creatorArtifactMatchesVersion(version: MapVersionView, loaded: LoadedMap): boolean {
    const artifact = loaded.artifact;
    if (loaded.entry.mapId !== version.mapId || artifact.mapId !== version.mapId || artifact.artifactHash !== version.artifactHash || artifact.setup.mode !== version.mode) return false;
    return artifact.schemaVersion < 3 || (artifact.compilerVersion === version.compilerVersion && artifact.protocolVersion === version.protocolVersion &&
      artifact.rulesHash === version.rulesHash && artifact.simulationVersion === version.simulationVersion && artifact.rulesVersion === version.rulesVersion);
  }

  private sameRegion(left: Rect | null | undefined, right: Rect | null | undefined): boolean {
    return Boolean(left && right && left.x === right.x && left.y === right.y && left.width === right.width && left.height === right.height);
  }

  private creatorRegionFromForm(form: HTMLFormElement): Rect | null {
    const values = new FormData(form);
    const base = this.creatorEditorBase(form);
    if (base?.loaded.artifact.schemaVersion === 4) {
      const sourceBounds = this.creatorEditorSourceBounds(form);
      const selected = String(values.get("region-tile-range") ?? "");
      if (!sourceBounds || !selected) return null;
      return this.creatorSchema4TileRanges(base.loaded, sourceBounds, base.version.mode)
        .find((candidate) => `${candidate.y}:${candidate.y + candidate.height}` === selected) ?? null;
    }
    const coordinates = ["x", "y", "width", "height"].map((part) => Number(values.get(`region-${part}`)));
    if (coordinates.some((value) => !Number.isFinite(value))) return null;
    return { x: coordinates[0]!, y: coordinates[1]!, width: coordinates[2]!, height: coordinates[3]! };
  }

  private creatorEditorSourceBounds(form: HTMLFormElement): Rect | null {
    if (form.dataset.form === "map-edit") return this.state.mapView?.provenance.selectedRegion ?? null;
    if (form.dataset.form === "creator-version") return this.state.creatorSourceBounds ?? null;
    return null;
  }

  private creatorSchema4TileRanges(loaded: LoadedMap, sourceBounds: Rect, mode: ActiveMode): Rect[] {
    const artifact = loaded.artifact;
    if (artifact.schemaVersion !== 4 || !artifact.region) return [];
    const region = artifact.region;
    const world = artifact.world;
    const integerRect = (rect: Rect): boolean => [rect.x, rect.y, rect.width, rect.height].every(Number.isSafeInteger) && rect.width > 0 && rect.height > 0;
    if (!integerRect(region) || !integerRect(sourceBounds) || !Number.isSafeInteger(world.width) || !Number.isSafeInteger(world.height) ||
      region.x !== sourceBounds.x || region.width !== sourceBounds.width || region.width !== world.width || region.height !== world.height ||
      region.y < sourceBounds.y || region.y + region.height > sourceBounds.y + sourceBounds.height) return [];

    const tiles = artifact.backgroundTiles.map((tile) => tile.bounds);
    if (!tiles.length || tiles.length > 20 || tiles.some((tile) => !integerRect(tile) || tile.x < 0 || tile.y < 0 || tile.x + tile.width > world.width || tile.y + tile.height > world.height)) return [];
    const seams = [...new Set([0, world.height, ...tiles.flatMap((tile) => [tile.y, tile.y + tile.height])])].sort((left, right) => left - right);
    if (seams[0] !== 0 || seams.at(-1) !== world.height) return [];

    for (let index = 0; index < seams.length - 1; index++) {
      const top = seams[index]!;
      const bottom = seams[index + 1]!;
      if (bottom <= top) return [];
      const row = tiles.filter((tile) => tile.y <= top && tile.y + tile.height >= bottom).sort((left, right) => left.x - right.x);
      let coveredWidth = 0;
      for (const tile of row) {
        if (tile.x !== coveredWidth) return [];
        coveredWidth += tile.width;
      }
      if (coveredWidth !== world.width) return [];
    }

    const maxHeight = mode === "scroll_rush" ? sourceBounds.height : Math.min(3600, sourceBounds.height);
    const ranges: Rect[] = [];
    for (let startIndex = 0; startIndex < seams.length - 1; startIndex++) {
      const localStart = seams[startIndex]!;
      for (let endIndex = startIndex + 1; endIndex < seams.length; endIndex++) {
        const localEnd = seams[endIndex]!;
        const height = localEnd - localStart;
        if (height < 720 || height > maxHeight) continue;
        if (tiles.some((tile) => tile.y < localEnd && tile.y + tile.height > localStart && (tile.y < localStart || tile.y + tile.height > localEnd))) continue;
        const candidate = { x: region.x, y: region.y + localStart, width: region.width, height };
        if (this.validSourceRegion(candidate, sourceBounds, mode)) ranges.push(candidate);
      }
    }
    return ranges;
  }

  private editorHasInvalidSchema4Region(form: HTMLFormElement): boolean {
    const base = this.creatorEditorBase(form);
    if (base?.loaded.artifact.schemaVersion !== 4) return false;
    const sourceBounds = this.creatorEditorSourceBounds(form);
    const region = this.creatorRegionFromForm(form);
    return !sourceBounds || !region || !this.creatorSchema4TileRanges(base.loaded, sourceBounds, base.version.mode).some((candidate) => this.sameRegion(candidate, region));
  }

  private creatorEditorBase(form: HTMLFormElement): { version: MapVersionView; loaded: LoadedMap } | null {
    const loaded = this.state.privatePreview;
    if (!loaded) return null;
    let version: MapVersionView | undefined;
    if (form.dataset.form === "map-edit") {
      const map = this.state.mapView;
      const versionId = String(new FormData(form).get("versionId") ?? "");
      version = map?.versions.find((item) => item.id === versionId);
      if (!map || !version || version.mapId !== map.id || version.mode !== this.state.mapEditMode) return null;
    } else if (form.dataset.form === "creator-version") {
      const challenge = this.state.creatorChallenge;
      version = challenge?.versions.find((item) => item.id === this.state.creatorVersionId);
      if (!challenge || !version || version.mapId !== challenge.mapId) return null;
    }
    if (!version || this.state.previewVersionId !== version.id || !this.creatorArtifactMatchesVersion(version, loaded)) return null;
    return { version, loaded };
  }

  private editorRequiresRegionPreview(form: HTMLFormElement): boolean {
    const base = this.creatorEditorBase(form);
    const region = this.creatorRegionFromForm(form);
    return Boolean(base?.loaded.artifact.schemaVersion === 4 && region && base.loaded.artifact.region && !this.sameRegion(region, base.loaded.artifact.region));
  }

  private syncEditorRegionActions(form: HTMLFormElement): void {
    const requiresPreview = this.editorRequiresRegionPreview(form);
    const invalidSchema4Range = this.editorHasInvalidSchema4Region(form);
    const base = this.creatorEditorBase(form);
    const note = form.querySelector<HTMLElement>("[data-region-preview-note]");
    if (note) note.hidden = !requiresPreview;
    const rangeError = form.querySelector<HTMLElement>("[data-region-tile-error]");
    if (rangeError) rangeError.hidden = !invalidSchema4Range;
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submit) submit.disabled = this.state.busy || !base || requiresPreview || invalidSchema4Range;
    const preview = form.querySelector<HTMLButtonElement>('[data-action="creator-region-preview"]');
    if (preview) {
      preview.hidden = !requiresPreview || invalidSchema4Range;
      preview.disabled = this.state.busy || !base || invalidSchema4Range;
    }
  }

  private mapEditSelectionMatches(mapId: string, versionId: string, mode: ActiveMode, loaded: LoadedMap): boolean {
    const map = this.state.mapView;
    const version = map?.versions.find((item) => item.id === versionId);
    return this.state.screen === "map_edit" && map?.id === mapId && this.state.mapEditMode === mode && this.state.previewVersionId === versionId &&
      this.state.privatePreview === loaded && Boolean(version && version.mapId === mapId && version.mode === mode && this.creatorArtifactMatchesVersion(version, loaded));
  }

  private creatorChallengeSelectionMatches(challengeId: string, versionId: string, loaded: LoadedMap): boolean {
    const challenge = this.state.creatorChallenge;
    const version = challenge?.versions.find((item) => item.id === versionId);
    return this.state.screen === "creator_challenge" && challenge?.id === challengeId && this.state.creatorVersionId === versionId &&
      this.state.previewVersionId === versionId && this.state.privatePreview === loaded && Boolean(version && version.mapId === challenge.mapId && this.creatorArtifactMatchesVersion(version, loaded));
  }

  private async createCreatorRegionPreview(): Promise<void> {
    const form = this.root.querySelector<HTMLFormElement>('form[data-form="map-edit"]');
    const map = this.state.mapView;
    if (!form || !map || this.state.busy) return;
    const base = this.creatorEditorBase(form);
    const mode = ActiveModeSchema.safeParse(new FormData(form).get("mode"));
    const region = this.creatorRegionFromForm(form);
    const sourceBounds = this.creatorEditorSourceBounds(form);
    const wholeTileRange = Boolean(base && mode.success && region && sourceBounds &&
      this.creatorSchema4TileRanges(base.loaded, sourceBounds, mode.data).some((candidate) => this.sameRegion(candidate, region)));
    if (!base || !mode.success || !region || base.version.mode !== mode.data || base.loaded.artifact.schemaVersion !== 4 || !base.loaded.artifact.region || this.sameRegion(region, base.loaded.artifact.region) ||
      !wholeTileRange || !this.validSourceRegion(region, map.provenance.selectedRegion, mode.data)) {
      this.state.formError = base?.loaded.artifact.schemaVersion === 4 && (!region || !wholeTileRange)
        ? tr.creatorRegionWholeTilesRequired
        : tr.regionInvalid;
      this.render();
      return;
    }
    const { version, loaded } = base;
    const mapId = map.id;
    this.state.editRegion = region;
    this.state.busy = true;
    this.state.formError = "";
    this.render();
    try {
      const response = await this.productApi.createVariant(mapId, { mode: mode.data, region, baseVersionId: version.id });
      const parsed = (await import("@urlcade/contracts")).ConversionRequestSchema.safeParse(response);
      if (!parsed.success) throw new Error("PRODUCT_RESPONSE_INVALID");
      if (!this.mapEditSelectionMatches(mapId, version.id, mode.data, loaded)) {
        this.state.busy = false;
        if (this.state.screen === "map_edit") this.render();
        return;
      }
      this.state.conversionRequestId = parsed.data.id;
      this.state.conversionStatus = parsed.data;
      this.state.conversionLoad = "ready";
      this.state.busy = false;
      this.navigate(`/build/${encodeURIComponent(parsed.data.id)}`);
    } catch (error) {
      if (!this.mapEditSelectionMatches(mapId, version.id, mode.data, loaded)) {
        this.state.busy = false;
        if (this.state.screen === "map_edit") this.render();
        return;
      }
      this.state.formError = this.productErrorText(error);
      this.state.busy = false;
      this.render();
    }
  }

  private validSourceRegion(region: Rect, sourceBounds: Rect, mode: ActiveMode): boolean {
    const maximumHeight = mode === "scroll_rush" ? sourceBounds.height : Math.min(3600, sourceBounds.height);
    return Number.isFinite(region.x) && Number.isFinite(region.y) && Number.isFinite(region.width) && Number.isFinite(region.height) &&
      region.x === sourceBounds.x && region.width === sourceBounds.width && region.y >= sourceBounds.y && region.height >= 720 &&
      region.height <= maximumHeight && region.y + region.height <= sourceBounds.y + sourceBounds.height;
  }

  private async prepareVersionForRoom(versionId: string, practice: boolean, signal?: AbortSignal): Promise<void> {
    const version = this.state.mapView?.versions.find((item) => item.id === versionId)
      ?? this.state.creatorChallenge?.versions.find((item) => item.id === versionId);
    if (!version) throw new Error(tr.noMapVersion);
    if (version.protocolVersion !== PROTOCOL_VERSION || !version.rulesHash) throw new Error(tr.practiceOnlyLegacyVersion);
    if (signal?.aborted) throw new DOMException("Version preparation cancelled.", "AbortError");
    if (this.state.privatePreview?.artifact.artifactHash !== version.artifactHash) await this.loadAuthorizedPreview(versionId, signal);
    if (signal?.aborted) throw new DOMException("Version preparation cancelled.", "AbortError");
    const loaded = this.state.privatePreview;
    if (!loaded || loaded.artifact.schemaVersion < 3 || loaded.artifact.artifactHash !== version.artifactHash || loaded.artifact.compilerVersion !== version.compilerVersion ||
      loaded.artifact.setup.mode !== version.mode || loaded.artifact.protocolVersion !== PROTOCOL_VERSION ||
      loaded.artifact.rulesHash !== version.rulesHash || loaded.artifact.simulationVersion !== version.simulationVersion) throw new Error(tr.assetMismatch);
    if (this.state.loadedMap && this.state.loadedMap !== loaded && this.state.loadedMap !== this.state.privatePreview) this.state.loadedMap.dispose();
    this.state.loadedMap = loaded;
    this.state.assetLoad = "ready";
    this.state.assetError = "";
    this.state.createMode = version.mode;
    this.state.createMapHash = version.artifactHash;
    this.state.createMapVersionId = version.id;
    if (practice) this.state.practiceMode = version.mode;
  }

  private retryPracticeResult(result: MatchResult): void {
    const snapshot = this.state.snapshot;
    const snapshotConflicts = Boolean(snapshot && (
      snapshot.practice === false || snapshot.mode !== result.mode || snapshot.artifactHash !== result.artifactHash ||
      (result.mapVersionId && snapshot.mapVersionId && snapshot.mapVersionId !== result.mapVersionId) ||
      (result.rulesHash && snapshot.rulesHash && snapshot.rulesHash !== result.rulesHash)
    ));
    const snapshotMatchesResult = Boolean(snapshot?.practice && snapshot.mode === result.mode &&
      snapshot.artifactHash === result.artifactHash && (!result.rulesHash || !snapshot.rulesHash || snapshot.rulesHash === result.rulesHash));
    const versionId = result.mapVersionId ?? (snapshotMatchesResult ? snapshot?.mapVersionId : undefined);

    if (snapshotConflicts) {
      this.state.formError = tr.assetMismatch;
      this.render();
      return;
    }
    if (versionId) {
      const version = this.state.mapView?.versions.find((item) => item.id === versionId);
      if (!version || version.mode !== result.mode || version.artifactHash !== result.artifactHash ||
        (result.rulesHash !== undefined && version.rulesHash !== result.rulesHash) ||
        (snapshot?.rulesHash !== undefined && version.rulesHash !== snapshot.rulesHash)) {
        this.state.formError = tr.assetMismatch;
        this.render();
        return;
      }
      void this.startVersionPractice(version.id);
      return;
    }
    if (result.mapVersionId || snapshot?.mapVersionId) {
      this.state.formError = tr.assetMismatch;
      this.render();
      return;
    }
    void this.startPractice(result.mode, result.artifactHash);
  }

  private async releaseCompletedPracticeBeforeRetry(artifactHash: string, request: number, signal: AbortSignal): Promise<boolean> {
    if (request !== this.roomRequestId || signal.aborted) return false;

    const leaving = this.multiplayer.dispose();
    this.clearGameActionFeedback();
    this.mapLoadRequest++;
    this.mapLoadAbort?.abort();
    this.mapLoadAbort = null;
    this.roomAssetAbort?.abort();
    this.roomAssetAbort = null;
    this.assetLoadKey = "";
    this.readySentHash = "";
    this.practiceReadyKey = "";
    this.practiceStartKey = "";
    this.replayRequest++;
    this.ownBestRequest++;
    this.gameSession?.destroy();
    this.gameSession = null;
    this.state.snapshot = null;
    this.state.latestScene = null;
    this.state.roomPhase = null;
    this.state.roundResult = null;
    this.state.matchResult = null;
    this.state.challengeReplay = null;
    this.state.replayLoad = "idle";
    this.state.replayError = "";
    this.state.ownBestReplay = null;
    this.state.ownBestVisible = false;
    this.state.ownBestLoad = "idle";
    this.state.ownBestError = "";
    this.state.roomCode = "";
    this.state.inviteToken = "";
    this.ownPlayerId = "";
    this.state.connectionLost = false;
    this.state.reconnectMessage = "";
    this.state.formError = "";
    this.state.notice = "";
    this.state.assetLoad = this.state.loadedMap?.artifact.artifactHash === artifactHash ? "ready" : "idle";
    this.state.assetError = "";
    this.clearHints();

    await leaving;
    return request === this.roomRequestId && !signal.aborted;
  }

  private async startVersionPractice(versionId: string): Promise<void> {
    if (!this.multiplayer.enabled || this.state.busy) return;
    const version = this.state.mapView?.versions.find((item) => item.id === versionId);
    if (!version) { this.state.mapViewError = tr.noMapVersion; this.render(); return; }
    const resultRetry = this.state.screen === "match_result" && this.state.matchResult?.practice ? this.state.matchResult : null;
    const resultVersionId = resultRetry?.mapVersionId ?? this.state.snapshot?.mapVersionId;
    if (resultRetry && (resultRetry.mode !== version.mode || resultRetry.artifactHash !== version.artifactHash ||
      (resultVersionId && resultVersionId !== version.id) ||
      (resultRetry.rulesHash !== undefined && resultRetry.rulesHash !== version.rulesHash))) {
      this.state.formError = tr.assetMismatch;
      this.render();
      return;
    }
    this.state.practiceRetryHash = version.artifactHash;
    this.state.practiceRetryVersionId = version.id;
    this.roomRequestAbort?.abort();
    const request = ++this.roomRequestId;
    const controller = new AbortController();
    this.roomRequestAbort = controller;
    this.state.practiceMode = version.mode;
    this.state.formError = "";
    this.state.busy = true;
    this.state.screen = "practice_starting";
    this.state.previewVersionId = version.id;
    this.state.previewLoad = "loading";
    this.render();
    try {
      if (resultRetry && !(await this.releaseCompletedPracticeBeforeRetry(version.artifactHash, request, controller.signal))) return;
      await this.prepareVersionForRoom(version.id, true, controller.signal);
      if (request !== this.roomRequestId || controller.signal.aborted) return;
      const loaded = this.state.loadedMap!;
      if (loaded.artifact.artifactHash !== version.artifactHash || loaded.artifact.schemaVersion < 3 || loaded.artifact.compilerVersion !== version.compilerVersion || loaded.artifact.protocolVersion !== PROTOCOL_VERSION || loaded.artifact.rulesHash !== version.rulesHash || loaded.artifact.simulationVersion !== version.simulationVersion) throw new Error(tr.versionMismatch);
      const created = await this.multiplayer.createRoom(version.mode, version.artifactHash, this.state.profile?.nickname ?? tr.defaultNickname, this.roomHandlers(), {
        mapVersionId: version.id, practice: true, characterId: this.state.profile?.characterId, signal: controller.signal,
      });
      if (request !== this.roomRequestId || controller.signal.aborted) { if (this.multiplayer.currentConnection === created.connection) void this.multiplayer.dispose(); return; }
      this.state.roomCode = created.code;
      this.state.inviteToken = created.inviteToken;
      this.state.practiceMode = version.mode;
      this.state.screen = this.state.snapshot?.phase === "match_result" ? "match_result" : this.state.snapshot && this.state.snapshot.phase !== "lobby" ? "game" : "lobby";
      this.state.notice = tr.practiceLobby;
      this.state.busy = false;
      this.state.previewLoad = "ready";
      this.updatePlayUrl(created.connection.roomId);
      this.render();
      if (this.state.snapshot) void this.loadRoomMap(this.state.snapshot);
      this.maybeAdvancePractice();
    } catch (error) {
      if (request !== this.roomRequestId || controller.signal.aborted) return;
      this.state.busy = false;
      this.state.previewLoad = "error";
      this.state.formError = this.productErrorText(error);
      this.render();
    } finally {
      if (request === this.roomRequestId) { this.roomRequestAbort = null; this.state.busy = false; }
    }
  }

  private async startCreatorChallenge(): Promise<void> {
    const challenge = this.state.creatorChallenge;
    const version = challenge?.versions.find((item) => item.id === this.state.creatorVersionId);
    if (!challenge || !version || !this.multiplayer.enabled || this.state.busy) return;
    this.roomRequestAbort?.abort();
    const request = ++this.roomRequestId;
    const controller = new AbortController();
    this.roomRequestAbort = controller;
    this.state.busy = true;
    this.state.creatorChallengeError = "";
    this.render();
    try {
      await this.prepareVersionForRoom(version.id, false, controller.signal);
      if (request !== this.roomRequestId || controller.signal.aborted) return;
      const loaded = this.state.loadedMap!;
      if (loaded.artifact.artifactHash !== version.artifactHash || loaded.artifact.schemaVersion < 3 || loaded.artifact.compilerVersion !== version.compilerVersion || loaded.artifact.protocolVersion !== PROTOCOL_VERSION || loaded.artifact.rulesHash !== version.rulesHash || loaded.artifact.simulationVersion !== version.simulationVersion) throw new Error(tr.versionMismatch);
      const created = await this.multiplayer.createRoom(version.mode, version.artifactHash, this.state.profile?.nickname ?? tr.defaultNickname, this.roomHandlers(), {
        mapVersionId: version.id, challengeVersionId: version.challengeVersionId ?? undefined, characterId: this.state.profile?.characterId, signal: controller.signal,
      });
      if (request !== this.roomRequestId || controller.signal.aborted) { if (this.multiplayer.currentConnection === created.connection) void this.multiplayer.dispose(); return; }
      this.state.roomCode = created.code;
      this.state.inviteToken = created.inviteToken;
      this.state.screen = this.state.snapshot?.phase === "match_result" ? "match_result" : this.state.snapshot && this.state.snapshot.phase !== "lobby" ? "game" : "lobby";
      this.state.busy = false;
      this.updatePlayUrl(created.connection.roomId);
      this.render();
      if (this.state.snapshot) void this.loadRoomMap(this.state.snapshot);
    } catch (error) {
      if (request !== this.roomRequestId || controller.signal.aborted) return;
      this.state.creatorChallengeError = this.productErrorText(error); this.state.busy = false; this.render();
    } finally {
      if (request === this.roomRequestId) { this.roomRequestAbort = null; this.state.busy = false; }
    }
  }

  private async beginVersionRoom(versionId: string): Promise<void> {
    this.roomRequestAbort?.abort();
    const request = ++this.roomRequestId;
    const controller = new AbortController();
    this.roomRequestAbort = controller;
    this.state.busy = true;
    this.state.formError = "";
    this.render();
    try {
      await this.prepareVersionForRoom(versionId, false, controller.signal);
      if (request !== this.roomRequestId || controller.signal.aborted) return;
      this.state.screen = "create";
      this.state.busy = false;
      this.render();
      this.root.querySelector<HTMLInputElement>('#nickname')?.focus();
    } catch (error) {
      if (request !== this.roomRequestId || controller.signal.aborted) return;
      this.state.mapViewError = this.productErrorText(error);
      this.state.busy = false;
      this.render();
    }
    if (request === this.roomRequestId) { this.roomRequestAbort = null; this.state.busy = false; }
  }

  private async joinPublicRoom(roomCode: string): Promise<void> {
    if (!this.multiplayer.enabled || this.state.busy) return;
    this.state.busy = true;
    this.state.formError = "";
    const request = ++this.roomRequestId;
    const controller = new AbortController();
    this.roomRequestAbort = controller;
    this.render();
    try {
      const joined = await this.multiplayer.joinRoom(roomCode, this.state.profile?.nickname ?? "Oyuncu", this.roomHandlers(), controller.signal, this.state.profile?.characterId);
      if (request !== this.roomRequestId || controller.signal.aborted) return;
      this.state.roomCode = joined.code;
      this.state.inviteToken = joined.inviteToken;
      this.state.screen = "lobby";
      this.state.busy = false;
      this.updatePlayUrl(this.multiplayer.currentConnection?.roomId ?? "");
      this.render();
      if (this.state.snapshot) void this.loadRoomMap(this.state.snapshot);
    } catch (error) {
      if (request !== this.roomRequestId || controller.signal.aborted) return;
      this.state.discoverError = this.productErrorText(error);
      this.state.busy = false;
      this.render();
    } finally { if (request === this.roomRequestId) this.roomRequestAbort = null; }
  }

  private async loadCreatorReplay(replayId: string): Promise<void> {
    const challenge = this.state.creatorChallenge;
    const version = challenge?.versions.find((entry) => entry.id === this.state.creatorVersionId);
    if (!challenge || !version) return;
    const request = ++this.replayRequest;
    if (this.state.challengeReplay?.id === replayId) {
      this.state.challengeReplay = null;
      this.state.replayLoad = "ready";
      this.state.replayError = "";
      this.gameSession?.setReplay(null);
      if (this.state.screen === "game") this.patchGameOverlay(); else this.render();
      return;
    }
    this.state.replayLoad = "loading";
    this.state.replayError = "";
    this.render();
    try {
      const replay = await this.productApi.getCreatorReplay(challenge.id, replayId);
      if (request !== this.replayRequest || this.state.creatorChallenge?.id !== challenge.id || this.state.creatorVersionId !== version.id) return;
      if (replay.artifactHash !== version.artifactHash || replay.simulationVersion !== version.simulationVersion || replay.challengeId !== challenge.id ||
        replay.mapVersionId !== version.id || replay.rulesHash !== version.rulesHash || replay.challengeVersionId !== version.challengeVersionId || !version.challengeVersionId) throw new Error("VERSION_MISMATCH");
      this.state.challengeReplay = replay;
      this.state.replayLoad = "ready";
      this.state.replayError = "";
      const snapshot = this.state.snapshot;
      if (snapshot && this.state.screen === "game" && this.replayMatchesSnapshot(replay, snapshot)) this.gameSession?.setReplay(replay.samples, replay.nickname);
    } catch (error) {
      if (request !== this.replayRequest || this.state.creatorChallenge?.id !== challenge.id || this.state.creatorVersionId !== version.id) return;
      this.state.replayLoad = "error";
      this.state.replayError = this.productErrorText(error);
    }
    if (this.state.screen === "game") this.patchGameOverlay(); else this.render();
  }

  private replayMatchesSnapshot(replay: Replay, snapshot: RoomSnapshot): boolean {
    const artifact = this.state.loadedMap?.artifact;
    const snapshotChallengeId = snapshot.creatorChallengeId ?? null;
    const snapshotChallengeVersionId = snapshot.challengeVersionId ?? null;
    return !snapshot.practice && snapshot.protocolVersion === PROTOCOL_VERSION &&
      Boolean(snapshot.mapVersionId && snapshot.rulesHash) && snapshot.artifactHash === replay.artifactHash &&
      snapshot.mapVersionId === replay.mapVersionId && snapshot.rulesHash === replay.rulesHash &&
      snapshotChallengeId === replay.challengeId && snapshotChallengeVersionId === replay.challengeVersionId &&
      !snapshot.challengeId && Boolean(artifact && artifact.schemaVersion >= 3 && artifact.protocolVersion === PROTOCOL_VERSION &&
        artifact.artifactHash === replay.artifactHash && artifact.rulesHash === replay.rulesHash &&
        artifact.simulationVersion === replay.simulationVersion);
  }

  private canShowOwnBestControl(snapshot = this.state.snapshot): boolean {
    return this.state.screen === "game" && Boolean(snapshot && !snapshot.practice && snapshot.mode === "scroll_rush" &&
      snapshot.protocolVersion === PROTOCOL_VERSION && snapshot.mapVersionId && snapshot.rulesHash &&
      !snapshot.challengeId && !snapshot.creatorChallengeId && !snapshot.challengeVersionId);
  }

  private async toggleOwnBestGhost(): Promise<void> {
    const snapshot = this.state.snapshot;
    if (!snapshot || !this.canShowOwnBestControl(snapshot) || !snapshot.mapVersionId || !snapshot.rulesHash) return;
    if (this.state.ownBestReplay && this.replayMatchesSnapshot(this.state.ownBestReplay, snapshot)) {
      this.state.ownBestVisible = !this.state.ownBestVisible;
      this.gameSession?.setReplay(this.state.ownBestVisible ? this.state.ownBestReplay.samples : null, this.state.ownBestReplay.nickname);
      this.patchGameGhostControl();
      return;
    }
    const request = ++this.ownBestRequest;
    this.state.ownBestLoad = "loading";
    this.state.ownBestError = "";
    this.patchGameGhostControl();
    try {
      const best = await this.productApi.getMapVersionBest(snapshot.mapVersionId);
      const current = this.state.snapshot;
      if (request !== this.ownBestRequest || !current || current.mapVersionId !== snapshot.mapVersionId ||
        current.artifactHash !== snapshot.artifactHash || current.rulesHash !== snapshot.rulesHash ||
        current.creatorChallengeId !== snapshot.creatorChallengeId || current.challengeVersionId !== snapshot.challengeVersionId ||
        current.practice || this.state.screen !== "game") return;
      if (!best.replay || !this.replayMatchesSnapshot(best.replay, current)) {
        this.state.ownBestReplay = null;
        this.state.ownBestVisible = false;
        this.state.ownBestLoad = "ready";
        this.state.ownBestError = tr.bestGhostMissing;
        this.gameSession?.setReplay(null);
      } else {
        this.state.ownBestReplay = best.replay;
        this.state.ownBestVisible = true;
        this.state.ownBestLoad = "ready";
        this.state.ownBestError = "";
        this.gameSession?.setReplay(best.replay.samples, best.replay.nickname);
      }
    } catch (error) {
      if (request !== this.ownBestRequest) return;
      this.state.ownBestLoad = "error";
      this.state.ownBestError = this.productErrorText(error);
    }
    this.patchGameGhostControl();
  }

  private async loadCreatorRankings(challengeId: string, versionId: string, playerCount = 1): Promise<void> {
    const request = ++this.creatorRankingsRequest;
    this.state.creatorRankingsLoad = "loading";
    this.state.creatorRankingsError = "";
    this.state.creatorChallengeRankings = [];
    if (this.state.screen === "creator_challenge") this.render();
    try {
      const rows = await this.productApi.getCreatorLeaderboard(challengeId, versionId, playerCount);
      if (request !== this.creatorRankingsRequest || this.state.creatorChallenge?.id !== challengeId || this.state.creatorVersionId !== versionId || this.state.creatorPlayerCount !== playerCount) return;
      this.state.creatorChallengeRankings = rows;
      this.state.creatorRankingsLoad = "ready";
    } catch (error) {
      if (request !== this.creatorRankingsRequest || this.state.creatorChallenge?.id !== challengeId || this.state.creatorVersionId !== versionId || this.state.creatorPlayerCount !== playerCount) return;
      this.state.creatorChallengeRankings = [];
      this.state.creatorRankingsLoad = "error";
      this.state.creatorRankingsError = this.productErrorText(error);
    }
    if (this.state.screen === "creator_challenge") this.render();
  }

  private updateCreatorChallengeUrl(id: string): void {
    const url = new URL(window.location.href);
    url.pathname = `/creator/challenges/${encodeURIComponent(id)}`;
    url.search = "";
    window.history.replaceState(null, "", url);
  }

  private async resolveMapShare(mapId: string, token: string): Promise<void> {
    const request = ++this.shareRedeemGeneration;
    this.state.mapViewLoad = "loading";
    this.state.mapViewError = "";
    this.render();
    try {
      const redeemed = await this.productApi.redeemShare(token);
      if (request !== this.shareRedeemGeneration || redeemed.mapId !== mapId || redeemed.challengeId) throw new Error("INVALID_SHARE");
      const url = new URL(window.location.href);
      url.searchParams.delete("share");
      window.history.replaceState(null, "", url);
      await this.loadMapView(mapId);
    } catch (error) {
      if (request !== this.shareRedeemGeneration) return;
      this.state.mapViewLoad = "error";
      this.state.mapView = null;
      this.state.mapViewError = this.productErrorText(error);
      this.render();
    }
  }

  private async resolveChallengeShare(token: string): Promise<void> {
    const request = ++this.shareRedeemGeneration;
    this.state.creatorChallenge = null;
    this.state.creatorChallengeLoad = "loading";
    this.state.creatorChallengeError = "";
    this.render();
    try {
      const redeemed = await this.productApi.redeemShare(token);
      if (request !== this.shareRedeemGeneration || !redeemed.challengeId) throw new Error("INVALID_SHARE");
      this.state.creatorChallengeId = redeemed.challengeId;
      await this.loadCreatorChallenge(redeemed.challengeId);
    } catch (error) {
      if (request !== this.shareRedeemGeneration) return;
      this.state.creatorChallengeLoad = "error";
      this.state.creatorChallengeError = this.productErrorText(error);
      this.render();
    }
  }

  private async loadCreatorChallenge(challengeId: string): Promise<void> {
    const request = ++this.challengeLoadRequest;
    this.state.creatorChallengeId = challengeId;
    this.state.creatorChallenge = null;
    this.state.creatorChallengeLoad = "loading";
    this.state.creatorChallengeError = "";
    this.state.creatorChallengeRankings = [];
    this.state.creatorRankingsLoad = "idle";
    this.state.creatorRankingsError = "";
    this.state.challengeReplay = null;
    this.state.replayError = "";
    this.replayRequest++;
    this.state.creatorShare = null;
    this.state.creatorSourceBounds = null;
    this.render();
    try {
      const challenge = await this.productApi.getCreatorChallenge(challengeId);
      if (request !== this.challengeLoadRequest || this.state.creatorChallengeId !== challengeId) return;
      this.state.creatorChallenge = challenge;
      if (challenge.owner) {
        try {
          const sourceMap = this.state.mapView?.id === challenge.mapId ? this.state.mapView : await this.productApi.getMap(challenge.mapId);
          if (request !== this.challengeLoadRequest || this.state.creatorChallengeId !== challengeId) return;
          if (sourceMap.id === challenge.mapId) this.state.creatorSourceBounds = sourceMap.provenance.selectedRegion;
        } catch {
          if (request !== this.challengeLoadRequest || this.state.creatorChallengeId !== challengeId) return;
          this.state.creatorSourceBounds = null;
        }
      }
      const version = challenge.versions.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
      this.state.creatorVersionId = version?.id ?? "";
      if (version) {
        await this.loadAuthorizedPreview(version.id);
        if (request !== this.challengeLoadRequest) return;
        await this.loadCreatorRankings(challenge.id, version.id, version.mode === "browser_boss" ? this.state.creatorPlayerCount : 1);
      }
      this.state.creatorChallengeLoad = "ready";
    } catch (error) {
      if (request !== this.challengeLoadRequest) return;
      this.state.creatorChallengeLoad = "error";
      this.state.creatorChallengeError = this.productErrorText(error);
    }
    if (this.state.screen === "creator_challenge") this.render();
  }

  private async saveMapToLibrary(mapId: string): Promise<void> {
    try {
      const entry = await this.productApi.saveLibrary(mapId, true);
      if (this.state.mapView?.id === entry.mapId) this.state.mapView = { ...this.state.mapView, favorite: entry.favorite };
      this.state.notice = tr.savedToLibrary;
      this.render();
    } catch (error) { this.state.mapViewError = this.productErrorText(error); this.render(); }
  }

  private async toggleMapFavorite(mapId: string, favorite: boolean): Promise<void> {
    try {
      const entry = await this.productApi.setFavorite(mapId, favorite);
      this.state.libraryEntries = this.state.libraryEntries.map((item) => item.mapId === mapId ? { ...item, favorite: entry.favorite } : item);
      if (this.state.mapView?.id === mapId) this.state.mapView = { ...this.state.mapView, favorite: entry.favorite };
      this.state.notice = entry.favorite ? tr.addFavoriteDone : tr.removeFavoriteDone;
    } catch (error) {
      this.state.libraryError = this.productErrorText(error);
      this.state.mapViewError = this.state.libraryError;
    }
    this.render();
  }

  private async recaptureMap(mapId: string): Promise<void> {
    try {
      const request = await this.productApi.recapture(mapId);
      const parsed = (await import("@urlcade/contracts")).ConversionRequestSchema.safeParse(request);
      if (!parsed.success) throw new Error("PRODUCT_RESPONSE_INVALID");
      this.state.conversionRequestId = parsed.data.id;
      this.state.conversionStatus = parsed.data;
      this.state.conversionLoad = "ready";
      this.navigate(`/build/${encodeURIComponent(parsed.data.id)}`);
    } catch (error) { this.state.mapViewError = this.productErrorText(error); this.render(); }
  }

  private async createMapShare(mapId: string): Promise<void> {
    try {
      const response = await this.productApi.createShare(mapId);
      const share = { ...response, url: sameOriginShareUrl(response.url) };
      this.state.shareView = share;
      try { await navigator.clipboard.writeText(share.url); this.state.notice = tr.shareLinkCopied; }
      catch { this.state.notice = tr.shareLinkReadyToCopy; }
    } catch (error) { this.state.notice = this.productErrorText(error); }
    this.render();
  }

  private async revokeMapShare(shareId: string): Promise<void> {
    try {
      await this.productApi.revokeShare(shareId);
      this.state.shareView = null;
      this.state.notice = tr.shareRevoked;
    } catch (error) { this.state.notice = this.productErrorText(error); }
    this.render();
  }

  private async createCreatorShare(): Promise<void> {
    const challenge = this.state.creatorChallenge;
    if (!challenge) return;
    try {
      const response = await this.productApi.createCreatorChallengeShare(challenge.id);
      const share = { ...response, url: sameOriginShareUrl(response.url) };
      this.state.creatorShare = share;
      try { await navigator.clipboard.writeText(share.url); this.state.notice = tr.shareLinkCopied; }
      catch { this.state.notice = tr.shareLinkReadyToCopy; }
    } catch (error) { this.state.creatorChallengeError = this.productErrorText(error); }
    this.render();
  }

  private async adminReportAction(reportId: string, action: "dismiss" | "takedown", reason: string): Promise<void> {
    if (reason.trim().length < 3 || reason.trim().length > 500) { this.state.adminError = tr.adminReasonRequired; this.render(); return; }
    try {
      await this.productApi.adminReportAction(reportId, action, reason.trim());
      await this.loadAdminSummary();
    } catch (error) { this.state.adminError = this.productErrorText(error); this.render(); }
  }

  private async publishMap(mapId: string): Promise<void> {
    try {
      const map = await this.productApi.publishMap(mapId);
      this.state.mapView = map;
      this.state.notice = map.status === "pending_review" ? tr.publicationPendingReview : tr.publicationStatusUpdated;
    } catch (error) { this.state.mapViewError = this.productErrorText(error); }
    this.render();
  }

  private productErrorText(error: unknown): string {
    if (error instanceof ConversionApiError) return this.localizedProductError(error.productError);
    if (error instanceof ProductApiError) return this.localizedProductError(error);
    if (error instanceof Error) return this.conversionErrorText(error.message);
    return tr.conversionGenericError;
  }

  private localizedProductError(error: { code: string; messageKey: string }): string {
    if (error.messageKey.startsWith("errors.")) return this.conversionErrorText(error.code);
    const message = tr[error.messageKey as keyof typeof tr];
    return typeof message === "string" ? message : this.conversionErrorText(error.code);
  }

  private async refreshCatalog(): Promise<void> {
    this.state.mapsLoad = "loading";
    this.render();
    try {
      this.state.maps = await loadCatalog();
      this.state.mapsLoad = "ready";
      this.state.mapsError = "";
    } catch (error) {
      this.state.mapsLoad = "error";
      this.state.mapsError = error instanceof Error ? error.message : tr.loadFailed;
    }
    if (!this.state.selectedHash || !mapByHash(this.state.maps, this.state.selectedHash)) {
      this.state.selectedHash = this.state.maps.find((map) => map.mode === this.state.selectedMode)?.artifactHash ?? "";
    }
    if (!this.state.createMapHash) this.state.createMapHash = this.state.maps.find((map) => map.mode === this.state.createMode)?.artifactHash ?? "";
    if (this.state.screen === "challenge" && this.state.challengeId) {
      this.render();
      void this.loadChallenge(this.state.challengeId);
    } else if (this.state.screen !== "game") this.render();
  }

  private async loadChallenge(id: string): Promise<void> {
    if (this.state.mapsLoad !== "ready" || !id) return;
    const request = ++this.challengeLoadRequest;
    this.state.challengeId = id;
    this.state.challengeLoad = "loading";
    this.state.challengeError = "";
    this.state.challenge = null;
    this.state.challengeRankings = [];
    this.state.rankingsLoad = "loading";
    this.state.rankingsError = "";
    this.state.challengeReplay = null;
    this.state.replayError = "";
    this.render();
    try {
      const challenge = await this.multiplayer.getChallenge(id);
      if (request !== this.challengeLoadRequest || this.state.challengeId !== id) return;
      this.state.challenge = challenge;
      this.state.challengeLoad = "ready";
      const entry = mapByHash(this.state.maps, challenge.artifactHash);
      if (!entry || entry.mode !== "scroll_rush") {
        this.state.challengeError = tr.challengeMapUnavailable;
      } else {
        try {
          const loaded = await this.loadSelectedMap(entry.artifactHash);
          if (request !== this.challengeLoadRequest || this.state.challengeId !== id) return;
          const option = loaded.artifact.setup.mode === "scroll_rush" ? loaded.artifact.setup.challengeOptions?.find((item) => item.id === challenge.optionId) : undefined;
          if (loaded.artifact.artifactHash !== challenge.artifactHash || loaded.artifact.simulationVersion !== challenge.simulationVersion ||
            loaded.artifact.protocolVersion !== challenge.protocolVersion || !(SUPPORTED_ARTIFACT_VERSIONS as readonly string[]).includes(challenge.protocolVersion) || !option) this.state.challengeError = tr.challengeVersionUnavailable;
          else this.state.challengeError = "";
        } catch (error) {
          this.state.challengeError = error instanceof Error ? error.message : tr.challengeMapUnavailable;
        }
      }
      this.render();
    } catch (error) {
      if (request !== this.challengeLoadRequest || this.state.challengeId !== id) return;
      this.state.challengeLoad = "error";
      this.state.challengeError = error instanceof Error ? error.message : tr.challengeError;
      this.state.rankingsLoad = "error";
      this.state.rankingsError = this.state.challengeError;
      this.render();
      return;
    }
    void this.loadChallengeRankings(id);
  }

  private async loadChallengeRankings(id = this.state.challengeId): Promise<void> {
    if (!id) return;
    const request = ++this.rankingsRequest;
    this.state.rankingsLoad = "loading";
    this.state.rankingsError = "";
    this.render();
    try {
      const rows = await this.multiplayer.getChallengeRankings(id);
      if (request !== this.rankingsRequest || id !== this.state.challengeId) return;
      this.state.challengeRankings = rows;
      this.state.rankingsLoad = "ready";
    } catch (error) {
      if (request !== this.rankingsRequest || id !== this.state.challengeId) return;
      this.state.challengeRankings = [];
      this.state.rankingsLoad = "error";
      this.state.rankingsError = error instanceof Error ? error.message : tr.challengeLeaderboardUnavailable;
    }
    this.render();
  }

  private async loadChallengeReplay(replayId: string): Promise<void> {
    const challenge = this.state.challenge;
    if (!challenge || !mapByHash(this.state.maps, challenge.artifactHash)) return;
    const request = ++this.replayRequest;
    if (this.state.challengeReplay?.id === replayId) {
      this.state.challengeReplay = null;
      this.state.replayLoad = "ready";
      this.state.replayError = "";
      this.gameSession?.setReplay(null);
      this.render();
      return;
    }
    this.state.replayLoad = "loading";
    this.state.replayError = "";
    this.render();
    try {
      const replay = await this.multiplayer.getReplay(replayId);
      if (request !== this.replayRequest || challenge.id !== this.state.challengeId || challenge.id !== this.state.challenge?.id) return;
      const loaded = this.state.loadedMap;
      if (replay.challengeId !== challenge.id || replay.artifactHash !== challenge.artifactHash || replay.simulationVersion !== challenge.simulationVersion ||
        !loaded || loaded.artifact.artifactHash !== replay.artifactHash || loaded.artifact.simulationVersion !== replay.simulationVersion) throw new Error(tr.ghostReplayUnavailable);
      this.state.challengeReplay = replay;
      this.state.replayLoad = "ready";
      const snapshot = this.state.snapshot;
      if (this.state.screen === "game" && snapshot?.challengeId === challenge.id &&
        snapshot.artifactHash === replay.artifactHash && snapshot.challengeOptionId === challenge.optionId &&
        this.state.loadedMap?.artifact.artifactHash === replay.artifactHash && this.state.loadedMap.artifact.simulationVersion === replay.simulationVersion) {
        this.gameSession?.setReplay(replay.samples, replay.nickname);
      }
    } catch (error) {
      if (request !== this.replayRequest || challenge.id !== this.state.challengeId || challenge.id !== this.state.challenge?.id) return;
      this.state.challengeReplay = null;
      this.state.replayLoad = "error";
      this.state.replayError = error instanceof Error ? error.message : tr.ghostReplayUnavailable;
    }
    this.render();
  }

  private async loadChallengeMapForCreation(): Promise<void> {
    const hash = this.state.createChallengeMapHash;
    if (!hash || this.state.busy) return;
    try {
      const loaded = await this.loadSelectedMap(hash);
      if (loaded.artifact.setup.mode !== "scroll_rush") throw new Error(tr.mapForRushOnly);
      const options = loaded.artifact.setup.challengeOptions ?? [];
      this.state.createChallengeOptionId = options[0]?.id ?? "";
      if (!options.length) this.state.formError = tr.challengeOptionMissing;
    } catch (error) {
      this.state.formError = error instanceof Error ? error.message : tr.mapLoadFailed;
    }
    if (this.state.screen === "challenge_create") this.render();
  }

  private async loadHistory(): Promise<void> {
    if (!this.multiplayer.enabled) {
      this.state.historyLoad = "ready";
      this.render();
      return;
    }
    const accountGeneration = this.accountGeneration;
    this.state.historyLoad = "loading";
    this.state.historyError = "";
    this.render();
    try {
      const history = await this.multiplayer.getResults();
      if (accountGeneration !== this.accountGeneration) return;
      this.state.history = history;
      this.state.historyLoad = "ready";
    } catch (error) {
      if (accountGeneration !== this.accountGeneration) return;
      this.state.historyLoad = "error";
      this.state.historyError = error instanceof Error ? error.message : "Maç geçmişi açılamadı.";
    }
    this.render();
  }

  private async loadSelectedMap(hash: string): Promise<LoadedMap> {
    const entry = mapByHash(this.state.maps, hash);
    if (!entry) throw new MapLoadError("MANIFEST_MISMATCH", tr.localMapMissing);
    if (this.state.loadedMap?.artifact.artifactHash === hash) return this.state.loadedMap;
    this.mapLoadAbort?.abort();
    const controller = new AbortController();
    this.mapLoadAbort = controller;
    const request = ++this.mapLoadRequest;
    this.state.assetLoad = "loading";
    this.state.assetError = "";
    this.state.busy = true;
    this.state.selectedHash = hash;
    this.render();
    this.state.loadedMap?.dispose();
    this.state.loadedMap = null;
    try {
      const loaded = await loadMap(entry, controller.signal);
      if (request !== this.mapLoadRequest) {
        loaded.dispose();
        throw new Error(tr.mapLoadFailed);
      }
      this.state.loadedMap = loaded;
      this.state.assetLoad = "ready";
      this.state.assetError = "";
      return loaded;
    } catch (error) {
      if (request === this.mapLoadRequest) {
        this.state.assetLoad = "error";
        this.state.assetError = this.mapErrorMessage(error);
      }
      throw error;
    } finally {
      if (request === this.mapLoadRequest) {
        this.mapLoadAbort = null;
        this.state.busy = false;
        if (this.state.screen !== "game") this.render();
      }
    }
  }

  private mapErrorMessage(error: unknown): string {
    if (!(error instanceof MapLoadError)) return error instanceof Error ? error.message : tr.mapLoadFailed;
    switch (error.code) {
      case "VERSION_MISMATCH": return tr.versionMismatch;
      case "ASSET_DIGEST_MISMATCH": return tr.assetDigestError;
      case "ASSET_MISSING": return tr.assetMissingError;
      case "ASSET_TOO_LARGE": return tr.assetTooLargeError;
      case "ASSET_INVALID": return tr.assetInvalidError;
      case "CRYPTO_UNAVAILABLE": return tr.cryptoRequiredError;
      case "MANIFEST_MISMATCH": return tr.assetMismatch;
      case "CATALOG_UNAVAILABLE": return tr.loadFailed;
    }
  }

  private async startLocalRush(hash = this.state.selectedHash, challengeOptionId?: string, challengeId?: string): Promise<void> {
    if (this.state.busy) return;
    this.state.selectedMode = "scroll_rush";
    this.state.formError = "";
    if (!challengeId) {
      this.state.localChallengeId = "";
      this.state.localChallengeOptionId = "";
    }
    try {
      const loaded = await this.loadSelectedMap(hash);
      if (loaded.artifact.setup.mode !== "scroll_rush") throw new Error(tr.mapForRushOnly);
      if (challengeId) {
        const challenge = this.state.challenge;
        const hasOption = loaded.artifact.setup.challengeOptions?.some((option) => option.id === challengeOptionId);
        if (challenge?.id !== challengeId || challenge.artifactHash !== hash || challenge.optionId !== challengeOptionId ||
          challenge.simulationVersion !== loaded.artifact.simulationVersion || challenge.protocolVersion !== loaded.artifact.protocolVersion ||
          !(SUPPORTED_ARTIFACT_VERSIONS as readonly string[]).includes(loaded.artifact.protocolVersion) || !hasOption) throw new Error(tr.challengeVersionUnavailable);
        this.state.localChallengeId = challengeId;
        this.state.localChallengeOptionId = challengeOptionId ?? "";
      }
      this.state.localResult = null;
      this.state.localVersionId = "";
      this.state.snapshot = null;
      this.state.latestScene = null;
      this.state.matchResult = null;
      this.localScoreSaved = false;
      this.state.screen = "game";
      this.render();
    } catch {
      if (this.state.screen !== "game") this.render();
    }
  }

  private async startLocalVersionRush(versionId: string): Promise<void> {
    if (this.state.busy || this.multiplayer.currentConnection) return;
    const version = this.state.mapView?.versions.find((candidate) => candidate.id === versionId);
    if (!version || version.mode !== "scroll_rush" || version.protocolVersion !== PROTOCOL_VERSION || !version.rulesHash) {
      this.state.mapViewError = tr.practiceOnlyLegacyVersion;
      this.render();
      return;
    }
    this.localVersionAbort?.abort();
    const controller = new AbortController();
    this.localVersionAbort = controller;
    const request = ++this.localVersionRequest;
    this.privateAssetAbort?.abort();
    this.privateAssetAbort = null;
    this.privateAssetGeneration++;
    this.state.previewLoad = "loading";
    this.state.previewError = "";
    this.render();
    try {
      let loaded: LoadedMap;
      if (this.state.previewVersionId === version.id && this.state.privatePreview?.artifact.artifactHash === version.artifactHash) loaded = this.state.privatePreview;
      else loaded = await loadAuthorizedMapVersion(this.productApi, version, controller.signal);
      if (request !== this.localVersionRequest || controller.signal.aborted || this.state.mapView?.id !== version.mapId) {
        if (loaded !== this.state.privatePreview && loaded !== this.state.loadedMap) loaded.dispose();
        return;
      }
      if (loaded.artifact.schemaVersion < 3 || loaded.artifact.setup.mode !== "scroll_rush" || loaded.artifact.artifactHash !== version.artifactHash || loaded.artifact.compilerVersion !== version.compilerVersion || loaded.artifact.rulesHash !== version.rulesHash || loaded.artifact.protocolVersion !== PROTOCOL_VERSION || loaded.artifact.simulationVersion !== version.simulationVersion) throw new Error(tr.versionMismatch);
      if (loaded !== this.state.privatePreview) {
        if (this.state.privatePreview && this.state.privatePreview !== this.state.loadedMap) this.state.privatePreview.dispose();
        this.state.privatePreview = loaded;
      }
      this.state.previewVersionId = version.id;
      this.state.previewLoad = "ready";
      this.state.previewError = "";
      this.state.loadedMap = loaded;
      this.state.assetLoad = "ready";
      this.state.assetError = "";
      this.state.snapshot = null;
      this.state.latestScene = null;
      this.state.roomPhase = null;
      this.state.matchResult = null;
      this.state.localResult = null;
      this.state.localVersionId = version.id;
      this.state.localChallengeId = "";
      this.state.localChallengeOptionId = "";
      this.state.challengeReplay = null;
      this.state.ownBestReplay = null;
      this.state.ownBestVisible = false;
      this.ownBestRequest++;
      this.localScoreSaved = false;
      this.state.screen = "game";
      this.render();
    } catch (error) {
      if (request !== this.localVersionRequest || controller.signal.aborted) return;
      this.state.previewLoad = "error";
      this.state.previewError = this.productErrorText(error);
      this.render();
    } finally {
      if (this.localVersionAbort === controller) this.localVersionAbort = null;
    }
  }

  private async startPractice(mode: ActiveMode, requestedHash?: string): Promise<void> {
    if (!this.multiplayer.enabled || this.state.busy) return;
    const resultRetry = this.state.screen === "match_result" && this.state.matchResult?.practice && requestedHash &&
      this.state.matchResult.mode === mode && this.state.matchResult.artifactHash === requestedHash
      ? this.state.matchResult
      : null;
    if (this.multiplayer.currentConnection && !resultRetry) return;
    const entry = requestedHash
      ? this.state.maps.find((map) => map.mode === mode && map.artifactHash === requestedHash)
      : this.state.maps.find((map) => map.mode === mode);
    if (!entry) {
      this.state.formError = tr.localMapMissing;
      this.render();
      return;
    }
    this.state.practiceRetryHash = entry.artifactHash;
    this.state.practiceRetryVersionId = "";
    this.roomRequestAbort?.abort();
    const request = ++this.roomRequestId;
    const controller = new AbortController();
    this.roomRequestAbort = controller;
    this.practiceReadyKey = "";
    this.practiceStartKey = "";
    this.state.practiceMode = mode;
    this.state.localVersionId = "";
    this.state.localChallengeId = "";
    this.state.localChallengeOptionId = "";
    this.state.pendingChallengeId = "";
    this.state.challengeReplay = null;
    this.state.ownBestReplay = null;
    this.state.ownBestVisible = false;
    this.state.ownBestLoad = "idle";
    this.replayRequest++;
    this.ownBestRequest++;
    this.state.formError = "";
    this.state.busy = true;
    this.state.screen = "practice_starting";
    this.render();
    try {
      if (resultRetry && !(await this.releaseCompletedPracticeBeforeRetry(entry.artifactHash, request, controller.signal))) return;
      const loaded = await this.loadSelectedMap(entry.artifactHash);
      if (request !== this.roomRequestId || controller.signal.aborted) return;
      if (loaded.artifact.artifactHash !== entry.artifactHash || loaded.artifact.setup.mode !== mode ||
        !(SUPPORTED_ARTIFACT_VERSIONS as readonly string[]).includes(loaded.artifact.protocolVersion)) {
        throw new Error(tr.versionMismatch);
      }
      this.state.assetLoad = "ready";
      this.state.busy = true;
      this.render();
      const created = await this.multiplayer.createRoom(mode, loaded.artifact.artifactHash, this.state.profile?.nickname ?? tr.defaultNickname, this.roomHandlers(), { practice: true, characterId: this.state.profile?.characterId, signal: controller.signal });
      if (request !== this.roomRequestId || controller.signal.aborted) {
        if (this.multiplayer.currentConnection === created.connection) void this.multiplayer.dispose();
        return;
      }
      this.state.roomCode = created.code;
      this.state.practiceMode = mode;
      this.state.screen = this.state.snapshot?.phase === "match_result" ? "match_result" : this.state.snapshot && this.state.snapshot.phase !== "lobby" ? "game" : "lobby";
      this.state.busy = false;
      this.state.notice = tr.practiceLobby;
      this.clearQuery();
      this.render();
      if (this.state.snapshot) void this.loadRoomMap(this.state.snapshot);
      this.maybeAdvancePractice();
    } catch (error) {
      if (request !== this.roomRequestId || controller.signal.aborted) return;
      this.state.busy = false;
      this.state.formError = error instanceof Error && error.message === "RATE_LIMITED"
        ? tr.errorRateLimited
        : error instanceof Error ? error.message : tr.multiplayerError;
      this.render();
    } finally {
      if (request === this.roomRequestId) {
        this.state.busy = false;
        this.roomRequestAbort = null;
        if (this.state.screen === "practice_starting") this.render();
      }
    }
  }

  private maybeAdvancePractice(): void {
    const snapshot = this.state.snapshot;
    const connection = this.multiplayer.currentConnection;
    if (!snapshot?.practice || snapshot.phase !== "lobby" || !connection || this.state.assetLoad !== "ready" ||
      this.state.loadedMap?.artifact.artifactHash !== snapshot.artifactHash || !this.ownPlayerId || snapshot.hostId !== this.ownPlayerId ||
      snapshot.players.length !== 1 || snapshot.players[0]?.id !== this.ownPlayerId || !snapshot.players[0].connected) return;
    const key = `${connection.roomId}:${snapshot.mapVersionId ?? "catalog"}:${snapshot.artifactHash}:${snapshot.rulesHash ?? ""}`;
    const self = snapshot.players[0];
    if (!self.ready) {
      if (this.practiceReadyKey !== key) {
        this.practiceReadyKey = key;
        connection.send("client_ready", { ready: true, artifactHash: snapshot.artifactHash, ...(snapshot.rulesHash ? { rulesHash: snapshot.rulesHash } : {}), protocolVersion: PROTOCOL_VERSION });
      }
      return;
    }
    if (this.practiceStartKey !== key) {
      this.practiceStartKey = key;
      connection.send("host_start", {});
    }
  }

  private setPracticeRole(role: "hider" | "seeker"): void {
    const snapshot = this.state.snapshot;
    if (this.state.screen !== "game" || !snapshot?.practice || snapshot.mode !== "site_hide_seek" ||
      (this.state.roomPhase ?? snapshot.phase) !== "searching" || this.state.connectionLost) return;
    this.multiplayer.currentConnection?.send("practice_role", { role });
  }

  private mountGameSession(): void {
    this.gameHudStatus = "";
    const container = this.root.querySelector<HTMLElement>("#game-root");
    const loaded = this.state.loadedMap;
    if (!container || !loaded) {
      this.state.assetError = tr.sceneNeedsMap;
      this.patchGameOverlay();
      return;
    }
    if (this.gameSession) this.gameSession.destroy();
    const snapshot = this.state.snapshot;
    let replayForRoom = snapshot
      ? [this.state.challengeReplay, this.state.ownBestReplay].find((replay): replay is Replay => Boolean(replay && this.replayMatchesSnapshot(replay, snapshot) && (replay === this.state.challengeReplay || this.state.ownBestVisible))) ?? null
      : null;
    if (!snapshot && this.state.localChallengeId) {
      const challenge = this.state.challenge;
      const replay = this.state.challengeReplay;
      if (challenge && replay && challenge.id === this.state.localChallengeId && challenge.artifactHash === loaded.artifact.artifactHash &&
        challenge.optionId === this.state.localChallengeOptionId && challenge.simulationVersion === loaded.artifact.simulationVersion &&
        challenge.protocolVersion === loaded.artifact.protocolVersion && replay.challengeId === challenge.id &&
        replay.artifactHash === challenge.artifactHash && replay.simulationVersion === challenge.simulationVersion) replayForRoom = replay;
    }
    this.gameSession = new GameSession({
      container,
      map: loaded,
      mode: this.state.snapshot?.mode ?? "scroll_rush",
      networked: Boolean(this.state.snapshot),
      quality: this.preferences.quality,
      onHud: (hud) => {
        this.updateGameHud(hud);
        if (hud.announcement && hud.announcement !== tr.gameReadyAnnouncement) this.setGameActionFeedback(hud.announcement);
      },
      onLocalFinish: (result) => this.finishLocal(result),
      onInput: (frames) => {
        if (this.multiplayer.currentConnection) this.multiplayer.currentConnection.send("input", { frames });
      },
      onAction: (action, payload) => this.sendGameAction(action, payload),
      reducedMotion: this.preferences.reducedMotion,
      challengeOptionId: this.state.snapshot?.challengeOptionId ?? (this.state.localChallengeOptionId || null),
      replaySamples: replayForRoom?.samples,
      replayNickname: replayForRoom?.nickname,
      characterId: characterFor(this.state.profile?.characterId),
    });
    this.gameSession.setPhase(this.state.roomPhase ?? this.state.snapshot?.phase ?? "racing");
    this.gameSession.setFrozen(this.state.connectionLost || Boolean(this.state.latestScene?.self?.caught));
    if (this.state.latestScene) this.gameSession.applyProjection(this.state.latestScene);
    this.gameSession.setHintRegions(this.state.hintRegions);
    this.gameSession.setChallengeOption(this.state.snapshot?.challengeOptionId ?? (this.state.localChallengeOptionId || null));
    this.bindTouchControls();
  }

  private bindTouchControls(): void {
    const guideSummary = this.root.querySelector<HTMLElement>(".game-guide > summary");
    const isolateGuideSpace = (event: KeyboardEvent) => {
      // Preserve native details activation without Phaser's window-level
      // capture preventing it or interpreting it as a gameplay jump.
      if (event.code === "Space" || event.key === " ") event.stopPropagation();
    };
    guideSummary?.addEventListener("keydown", isolateGuideSpace);
    guideSummary?.addEventListener("keyup", isolateGuideSpace);
    this.root.querySelectorAll<HTMLButtonElement>("[data-game-input]").forEach((button) => {
      const action = button.dataset.gameInput;
      if (action !== "left" && action !== "right" && action !== "jump" && action !== "dash") return;
      const down = (event: PointerEvent) => {
        event.preventDefault();
        button.setPointerCapture?.(event.pointerId);
        button.setAttribute("aria-pressed", "true");
        this.gameSession?.setControl(action, true);
      };
      const up = (event: PointerEvent) => {
        event.preventDefault();
        button.setAttribute("aria-pressed", "false");
        this.gameSession?.setControl(action, false);
      };
      button.addEventListener("pointerdown", down);
      button.addEventListener("pointerup", up);
      button.addEventListener("pointercancel", up);
      button.addEventListener("lostpointercapture", up);
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-game-hold]").forEach((button) => {
      if (button.dataset.gameHold !== "interact") return;
      const down = (event: PointerEvent) => {
        event.preventDefault();
        if (button.getAttribute("aria-pressed") === "true") return;
        button.setPointerCapture?.(event.pointerId);
        button.setAttribute("aria-pressed", "true");
        this.gameSession?.setInteractHeld(true);
      };
      const up = (event: PointerEvent) => {
        event.preventDefault();
        button.setAttribute("aria-pressed", "false");
        this.gameSession?.setInteractHeld(false);
      };
      button.addEventListener("pointerdown", down);
      button.addEventListener("pointerup", up);
      button.addEventListener("pointercancel", up);
      button.addEventListener("lostpointercapture", up);
    });
  }

  private gameHelp(mode: ActiveMode): string {
    if (mode === "scroll_rush") return tr.inputHelp;
    if (mode === "site_hide_seek") return tr.hideInputHelp;
    if (mode === "capture_logo") return tr.captureInputHelp;
    if (mode === "page_brawl") return tr.brawlInputHelp;
    return tr.bossInputHelp;
  }

  private hideActionBlockReason(action: "select_prop" | "toggle_disguise" | "scan"): string | null {
    if (this.state.snapshot?.mode !== "site_hide_seek") return null;
    if (this.state.connectionLost) return tr.matchDisconnected;
    const player = this.state.latestScene?.self;
    if (!player) return tr.hideActionWaiting;
    if (player.caught) return tr.caughtWait;
    const phase = this.state.roomPhase ?? this.state.snapshot.phase;

    if (action === "scan") {
      if (player.role !== "seeker") return tr.hideSeekerOnly;
      if (phase !== "searching") return tr.hideScanPhase;
      if (player.energy <= 0) return tr.hideScanEnergy;
      if (player.scanCooldownMs > 0) return tr.hideScanCooldown.replace("{seconds}", String(Math.max(1, Math.ceil(player.scanCooldownMs / 1000))));
      return null;
    }

    if (player.role !== "hider") return tr.hideHiderOnly;
    if (phase !== "hiding" && phase !== "searching") return tr.hideHiderPhase;
    if (action === "select_prop") return null;
    if (player.disguised) return null;
    if (!player.selectedTemplateId) return tr.hideChooseProp;
    if (!player.movement.grounded) return tr.hideLandToTransform;
    if (Math.abs(player.movement.vx) > 0.01) return tr.hideStopToTransform;
    if (player.disguiseCooldownMs > 0) return tr.hideTransformCooldown.replace("{seconds}", String(Math.max(1, Math.ceil(player.disguiseCooldownMs / 1000))));
    return null;
  }

  private hideActionDescription(action: "select_prop" | "toggle_disguise" | "scan"): string {
    const reason = this.hideActionBlockReason(action);
    if (reason) return reason;
    const player = this.state.latestScene?.self;
    if (action === "select_prop") return tr.nearPropHelp;
    if (action === "toggle_disguise") return player?.disguised ? tr.hideBreakDisguise : tr.hideTransformReady;
    return tr.hideScanGuidance;
  }

  private hideControlAttributes(action: "select_prop" | "toggle_disguise" | "scan"): string {
    return `aria-disabled="${this.hideActionBlockReason(action) ? "true" : "false"}" title="${escapeHtml(this.hideActionDescription(action))}" aria-describedby="game-hud-status"`;
  }

  private patchHideControls(): void {
    if (this.state.snapshot?.mode !== "site_hide_seek") return;
    const controls = [
      ["select-prop", "select_prop"],
      ["toggle-disguise", "toggle_disguise"],
      ["scan", "scan"],
    ] as const;
    for (const [actionName, action] of controls) {
      const button = this.root.querySelector<HTMLButtonElement>(`[data-action="${actionName}"]`);
      if (!button) continue;
      button.setAttribute("aria-disabled", this.hideActionBlockReason(action) ? "true" : "false");
      button.title = this.hideActionDescription(action);
      button.setAttribute("aria-describedby", "game-hud-status");
    }
  }

  private hideGameplayGuidance(): string {
    const player = this.state.latestScene?.self;
    if (!player) return tr.hideActionWaiting;
    if (player.role === "hider") {
      const reason = this.hideActionBlockReason("toggle_disguise");
      return reason ?? (player.disguised ? tr.hideBreakDisguise : tr.hideTransformReady);
    }
    if (player.role === "seeker") return this.hideActionBlockReason("scan") ?? tr.hideScanGuidance;
    return tr.hideActionWaiting;
  }

  private updateGameHud(hud: GameHud): void {
    const time = this.root.querySelector<HTMLElement>("#game-timer");
    const elapsed = this.state.latestScene?.self?.elapsedMs ?? hud.elapsedMs;
    const remaining = hud.mode === "capture_logo" ? Math.max(0, EXT_RULES.logoLimitMs - elapsed) : hud.mode === "page_brawl" ? Math.max(0, EXT_RULES.brawlLimitMs - elapsed) : hud.mode === "browser_boss" ? Math.max(0, EXT_RULES.bossLimitMs - elapsed) : elapsed;
    if (time) time.textContent = formatTime(hud.mode === "site_hide_seek" ? this.state.snapshot?.phaseRemainingMs ?? 0 : remaining);
    const status = this.root.querySelector<HTMLElement>("#game-hud-status");
    const livePhase = hud.phase ?? this.state.roomPhase ?? this.state.snapshot?.phase;
    const actionLocked = livePhase !== "playing" || (hud.stunnedMs ?? 0) > 0;
    this.patchHideControls();
    this.root.querySelectorAll<HTMLButtonElement>('[data-action="push"]').forEach((button) => {
      button.disabled = actionLocked || (hud.actionCooldownMs ?? 0) > 0;
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-action="area-burst"]').forEach((button) => {
      button.disabled = actionLocked || (hud.burstCooldownMs ?? 0) > 0;
    });
    if (status) {
      if (hud.mode === "scroll_rush") status.textContent = `${tr.checkpoints}: ${hud.checkpoint}/3 · ${tr.penalty}: +${formatTime(hud.penaltyMs)}`;
      else if (hud.mode === "site_hide_seek") status.textContent = hud.role === "seeker"
        ? `${tr.seeker} · ${tr.energy}: ${hud.energy ?? 0}/5 · ${this.hideGameplayGuidance()}`
        : hud.role === "hider"
          ? `${tr.hider}${hud.disguised ? ` · ${tr.hidden}` : ""} · ${this.hideGameplayGuidance()}`
          : tr.hideActionWaiting;
      else if (hud.mode === "capture_logo") {
        const scores = hud.arena?.mode === "capture_logo" ? hud.arena.logo : undefined;
        const team = hud.team === "red" ? tr.redTeam : hud.team === "blue" ? tr.blueTeam : tr.captureTeam;
        let logoState: string = tr.logoCenter;
        if (scores?.state === "carried") {
          const carrier = scores.carrierId ? this.state.snapshot?.players.find((player) => player.id === scores.carrierId) : undefined;
          logoState = scores.carrierId === this.ownPlayerId ? tr.logoYouCarry : tr.logoCarrier.replace("{name}", carrier?.nickname ?? tr.logoCarried);
        } else if (scores?.state === "dropped") {
          logoState = `${tr.logoDropped} · ${tr.logoReturnsIn.replace("{seconds}", String(Math.ceil(scores.returnRemainingMs / 1000)))}`;
        }
        status.textContent = `${team} · ${scores?.redScore ?? 0} ${tr.redTeam} — ${scores?.blueScore ?? 0} ${tr.blueTeam} · ${logoState} · ${hud.actionCooldownMs ? `${tr.actionCooldown}: ${Math.ceil(hud.actionCooldownMs / 1000)}s` : tr.pushHint}`;
      } else if (hud.mode === "page_brawl") {
        const ownScore = this.state.snapshot?.players.find((player) => player.id === this.ownPlayerId)?.score ?? 0;
        const broken = hud.arena?.mode === "page_brawl" ? hud.arena.breakables.filter((item) => !item.active).length : 0;
        status.textContent = `${tr.fallPoints}: ${ownScore} · ${tr.breakableRebuild}: ${broken} · ${hud.burstCooldownMs ? `${tr.burstCooldown}: ${Math.ceil(hud.burstCooldownMs / 1000)}s` : tr.areaBurst}`;
      } else {
        const boss = hud.arena?.mode === "browser_boss" ? hud.arena.boss : undefined;
        const progress = boss ? Math.min(100, Math.round((boss.progressMs / Math.max(1, boss.thresholdMs)) * 100)) : 0;
        const bossPhase = boss?.phase === "weak" ? `${tr.bossWeakWindow} · ${Math.ceil(boss.weakRemainingMs / 1000)}s` : boss?.phase === "telegraph" ? tr.bossTelegraph : `${tr.bossSwitches}: ${boss?.activeSwitchIds.length ?? 0}/3`;
        status.textContent = `${bossPhase} · ${tr.bossProgress}: ${progress}% · ${tr.bossCycles}: ${boss?.completedCycles ?? 0}/3 · ${tr.bossRescues}: ${boss?.rescues ?? 0}${hud.stunnedMs ? ` · ${tr.stunned}` : ""}`;
      }
      this.gameHudStatus = status.textContent ?? "";
      if (this.state.gameActionFeedback) status.textContent = this.state.gameActionFeedback;
    }
    const roleGuide = this.root.querySelector<HTMLElement>("#game-role-guide");
    if (roleGuide) {
      const guidance = this.hideGameplayGuidance();
      if (roleGuide.textContent !== guidance) roleGuide.textContent = guidance;
    }
    const phase = this.root.querySelector<HTMLElement>("#phase-label");
    if (phase) phase.textContent = hud.phase ? this.phaseLabel(hud.phase) : this.state.snapshot ? this.phaseLabel(this.state.roomPhase ?? this.state.snapshot.phase) : tr.localLabel;
    if (hud.announcement) {
      const announcer = this.root.querySelector<HTMLElement>("#game-announcer");
      if (announcer) announcer.textContent = hud.announcement;
    }
  }

  private updateSnapshot(snapshot: RoomSnapshot): void {
    const previous = this.state.snapshot;
    const replayScopeChanged = Boolean(previous && (previous.artifactHash !== snapshot.artifactHash || previous.mapVersionId !== snapshot.mapVersionId ||
      previous.rulesHash !== snapshot.rulesHash || previous.mode !== snapshot.mode || previous.practice !== snapshot.practice ||
      previous.challengeId !== snapshot.challengeId || previous.creatorChallengeId !== snapshot.creatorChallengeId ||
      previous.challengeVersionId !== snapshot.challengeVersionId));
    if (!previous) {
      this.ownBestRequest++;
      this.state.ownBestReplay = null;
      this.state.ownBestVisible = false;
      this.state.ownBestLoad = "idle";
      this.state.ownBestError = "";
    } else if (replayScopeChanged) {
      this.ownBestRequest++;
      this.replayRequest++;
      this.state.ownBestReplay = null;
      this.state.ownBestVisible = false;
      this.state.ownBestLoad = "idle";
      this.state.ownBestError = "";
      this.state.challengeReplay = null;
      this.state.replayLoad = "idle";
      this.state.replayError = "";
      this.gameSession?.setReplay(null);
    }
    this.state.snapshot = snapshot;
    this.ownPlayerId = snapshot.selfId ?? this.ownPlayerId;
    this.state.roomPhase = snapshot.phase;
    this.state.roomCode = snapshot.roomCode;
    this.state.connectionLost = false;
    this.state.reconnectMessage = "";
    if (previous && previous.round !== snapshot.round) this.state.roundResult = null;
    if (snapshot.practice && (!previous || previous.artifactHash !== snapshot.artifactHash || previous.mode !== snapshot.mode)) {
      this.practiceReadyKey = "";
      this.practiceStartKey = "";
    }
    if (snapshot.practice && previous?.phase === "match_result" && snapshot.phase === "lobby") {
      this.practiceReadyKey = "";
      this.practiceStartKey = "";
    }
    if (previous && previous.phase !== snapshot.phase && snapshot.phase !== "searching") this.clearHints();
    if (snapshot.phase === "hiding" || snapshot.phase === "racing") this.state.roundResult = null;
    this.gameSession?.setPhase(snapshot.phase);
    this.gameSession?.setChallengeOption(snapshot.challengeOptionId ?? null);
    if (snapshot.challengeId) this.state.challengeId = snapshot.challengeId;
    if (snapshot.phase === "match_result") {
      if (this.state.screen !== "match_result") {
        this.state.screen = "match_result";
        this.render();
      } else this.patchMatchResult();
      return;
    }
    if (snapshot.phase !== "lobby" && this.state.screen !== "game") {
      this.state.roundResult = null;
      this.state.screen = "game";
      this.render();
    } else if (snapshot.phase === "lobby" && this.state.screen !== "lobby") {
      this.state.screen = "lobby";
      this.state.roundResult = null;
      this.render();
    } else if (snapshot.phase === "lobby" && this.state.screen === "lobby") {
      this.patchLobby();
    } else if (snapshot.phase !== "lobby" && this.state.screen === "game") {
      this.updateGameHud({ mode: snapshot.mode, phase: snapshot.phase, elapsedMs: this.state.latestScene?.self?.elapsedMs ?? 0, checkpoint: 0, penaltyMs: 0 });
    }
    if (!previous || previous.artifactHash !== snapshot.artifactHash || previous.mode !== snapshot.mode ||
      previous.mapVersionId !== snapshot.mapVersionId || previous.rulesHash !== snapshot.rulesHash) void this.loadRoomMap(snapshot);
    this.maybeAdvancePractice();
    this.patchPracticeRolePicker();
    this.patchGameOverlay();
  }

  private async loadRoomMap(snapshot: RoomSnapshot): Promise<void> {
    const entry = mapByHash(this.state.maps, snapshot.artifactHash);
    const version = [...(this.state.mapView?.versions ?? []), ...(this.state.creatorChallenge?.versions ?? [])]
      .find((candidate) => candidate.id === snapshot.mapVersionId && candidate.artifactHash === snapshot.artifactHash && candidate.mode === snapshot.mode);
    this.readySentHash = "";
    if ((!snapshot.practice && (!snapshot.mapVersionId || !snapshot.rulesHash)) ||
      (!snapshot.mapVersionId && (!snapshot.practice || !entry || entry.mode !== snapshot.mode))) {
      this.state.assetLoad = "error";
      this.state.assetError = tr.roomMapCatalogMismatch;
      this.patchLobby();
      this.patchGameOverlay();
      this.maybeAdvancePractice();
      return;
    }
    this.roomAssetAbort?.abort();
    const controller = new AbortController();
    this.roomAssetAbort = controller;
    const key = `${snapshot.mapVersionId ?? "catalog"}:${snapshot.artifactHash}:${snapshot.rulesHash ?? ""}:${snapshot.mode}`;
    this.assetLoadKey = key;
    this.state.assetLoad = "loading";
    this.state.assetError = "";
    this.patchLobby();
    this.patchGameOverlay();
    try {
      let loaded: LoadedMap;
      if (snapshot.mapVersionId) {
        if (version && this.state.privatePreview?.artifact.artifactHash === version.artifactHash && this.state.previewVersionId === version.id) loaded = this.state.privatePreview;
        else loaded = await loadAuthorizedRoomMapVersion(this.productApi, snapshot.mapVersionId, {
          mapId: snapshot.mapId,
          artifactHash: snapshot.artifactHash,
          mode: snapshot.mode,
          ...(version ? { compilerVersion: version.compilerVersion } : {}),
          ...(snapshot.rulesHash ? { rulesHash: snapshot.rulesHash } : {}),
          ...(!snapshot.practice ? { protocolVersion: PROTOCOL_VERSION, simulationVersion: PROTOCOL_VERSION } : {}),
        }, controller.signal);
        if (controller.signal.aborted || this.assetLoadKey !== key || this.state.snapshot?.mapVersionId !== snapshot.mapVersionId || this.state.snapshot?.artifactHash !== snapshot.artifactHash) {
          if (loaded !== this.state.privatePreview && loaded !== this.state.loadedMap) loaded.dispose();
          return;
        }
        if (this.state.loadedMap && this.state.loadedMap !== loaded && this.state.loadedMap !== this.state.privatePreview) this.state.loadedMap.dispose();
        this.state.loadedMap = loaded;
        this.state.assetLoad = "ready";
        this.state.assetError = "";
      } else {
        loaded = await this.loadSelectedMap(snapshot.artifactHash);
      }
      if (controller.signal.aborted || this.assetLoadKey !== key || this.state.snapshot?.artifactHash !== snapshot.artifactHash || this.state.snapshot?.mapVersionId !== snapshot.mapVersionId) return;
      if (loaded.artifact.setup.mode !== snapshot.mode || (version && loaded.artifact.compilerVersion !== version.compilerVersion) ||
        (snapshot.practice
          ? !(SUPPORTED_ARTIFACT_VERSIONS as readonly string[]).includes(loaded.artifact.protocolVersion)
          : loaded.artifact.schemaVersion < 3 || loaded.artifact.protocolVersion !== PROTOCOL_VERSION || loaded.artifact.rulesHash !== snapshot.rulesHash)) {
        throw new MapLoadError("VERSION_MISMATCH");
      }
      // A catalog map may already be loaded before joining practice. Its cache
      // hit does not update assetLoad; publish ready only after this room load's
      // cancellation/identity and artifact checks have all succeeded.
      this.state.assetLoad = "ready";
      this.state.assetError = "";
      if (this.state.latestScene && this.state.screen === "game") {
        this.gameSession?.setMap(loaded);
        this.gameSession?.setChallengeOption(snapshot.challengeOptionId ?? null);
      }
      this.patchLobby();
      this.patchGameOverlay();
      this.maybeAdvancePractice();
    } catch (error) {
      if (controller.signal.aborted || this.assetLoadKey !== key) return;
      this.state.assetLoad = "error";
      this.state.assetError = error instanceof Error ? this.conversionErrorText(error.message) : tr.assetMismatch;
      if (this.multiplayer.currentConnection) this.multiplayer.currentConnection.send("client_ready", { ready: false, artifactHash: snapshot.artifactHash, ...(snapshot.rulesHash ? { rulesHash: snapshot.rulesHash } : {}), protocolVersion: PROTOCOL_VERSION });
      this.patchLobby();
      this.patchGameOverlay();
    } finally {
      if (this.roomAssetAbort === controller) this.roomAssetAbort = null;
    }
  }

  private updateProjection(scene: SceneProjection): void {
    const previousScene = this.state.latestScene;
    const previousLogo = previousScene?.arena?.mode === "capture_logo" ? previousScene.arena.logo : undefined;
    const logo = scene.arena?.mode === "capture_logo" ? scene.arena.logo : undefined;
    const logoChanged = previousLogo && logo && (previousLogo.carrierId !== logo.carrierId ||
      previousLogo.redScore !== logo.redScore || previousLogo.blueScore !== logo.blueScore);
    this.state.latestScene = scene;
    const previousOwnPlayerId = this.ownPlayerId;
    this.ownPlayerId = scene.self?.id ?? this.state.snapshot?.selfId ?? "";
    this.gameSession?.applyProjection(scene);
    // A confirmed role or objective change supersedes an earlier rejected action.
    if (previousScene?.self?.role !== scene.self?.role || logoChanged) {
      this.clearGameActionFeedback();
      this.gameHudStatus = "";
    }
    this.patchPracticeRolePicker();
    this.updateGameHud({
      mode: this.state.snapshot?.mode ?? "scroll_rush",
      phase: this.state.snapshot?.phase,
      elapsedMs: scene.self?.elapsedMs ?? 0,
      checkpoint: scene.self?.checkpoint ?? 0,
      penaltyMs: scene.self?.penaltyMs ?? 0,
      role: scene.self?.role,
      energy: scene.self?.energy,
      disguised: scene.self?.disguised,
      team: scene.self?.team,
      stunnedMs: scene.self?.stunnedMs,
      actionCooldownMs: scene.self?.actionCooldownMs,
      burstCooldownMs: scene.self?.burstCooldownMs,
      arena: scene.arena,
    });
    if (scene.self?.caught) {
      this.gameSession?.setFrozen(true);
    } else if (!this.state.connectionLost) {
      this.gameSession?.setFrozen(false);
    }
    if (this.state.screen === "lobby" && previousOwnPlayerId !== this.ownPlayerId) this.patchLobby();
    this.patchGameOverlay();
  }

  private roomHandlers(): RoomHandlers {
    return {
      onSnapshot: (snapshot) => this.updateSnapshot(snapshot),
      onScene: (scene) => this.updateProjection(scene),
      onRoundResult: (result) => {
        this.state.roundResult = result;
        this.state.roomPhase = "round_result";
        this.gameSession?.setPhase("round_result");
        this.patchGameOverlay();
      },
      onMatchResult: (result) => {
        this.clearGameActionFeedback();
        this.state.matchResult = result;
        this.state.formError = "";
        this.state.roomPhase = "match_result";
        const alreadyShowingResult = this.state.screen === "match_result";
        this.state.screen = "match_result";
        this.state.connectionLost = false;
        if (alreadyShowingResult) this.patchMatchResult();
        else this.render();
      },
      onRoomError: (error) => {
        const code = String(error.code);
        if (this.state.screen === "game" && !this.state.connectionLost && error.message === code && (code === "CONFLICT" || code === "FORBIDDEN")) {
          this.state.formError = "";
          this.state.notice = "";
          this.setGameActionFeedback(tr.gameActionUnavailable);
          this.patchGameOverlay();
          return;
        }
        this.clearGameActionFeedback();
        this.state.formError = roomErrorText(error.code, error.message);
        this.state.notice = "";
        if (this.state.screen === "lobby") this.patchLobby();
        else if (this.state.screen === "game") this.patchGameOverlay();
        else if (this.state.screen === "match_result") this.patchMatchResult();
        else this.render();
      },
      onReconnect: (status) => {
        if (status.status === "resumed") {
          this.state.connectionLost = false;
          this.state.reconnectMessage = "";
          this.state.notice = tr.connectionRestored;
        } else if (status.status === "aborted") {
          this.state.connectionLost = true;
          this.state.reconnectMessage = tr.reconnectSeatUnavailable;
        }
        this.patchLobby();
        this.patchGameOverlay();
      },
      onPhase: (phase) => {
        this.state.roomPhase = phase.phase;
        if (this.state.snapshot) this.state.snapshot = { ...this.state.snapshot, phase: phase.phase, phaseRemainingMs: phase.remainingMs };
        if (phase.phase === "hiding" || phase.phase === "racing") this.state.roundResult = null;
        if (phase.phase !== "searching") this.clearHints();
        this.gameSession?.setPhase(phase.phase);
        this.patchPracticeRolePicker();
        this.patchGameOverlay();
        this.updateGameHud({ mode: this.state.snapshot?.mode ?? "scroll_rush", phase: phase.phase, elapsedMs: this.state.latestScene?.self?.elapsedMs ?? 0, checkpoint: this.state.latestScene?.self?.checkpoint ?? 0, penaltyMs: this.state.latestScene?.self?.penaltyMs ?? 0 });
      },
      onHint: (hint) => {
        this.state.hintRegions = hint.regions;
        this.gameSession?.setHintRegions(hint.regions);
        window.clearTimeout(this.hintTimeout);
        this.announce(hint.expiresInMs > 0 ? tr.temporaryHint : tr.hintSignal);
        if (hint.expiresInMs > 0) {
          this.hintTimeout = window.setTimeout(() => this.clearHints(), hint.expiresInMs);
        }
      },
      onClosing: (message) => {
        this.state.connectionLost = true;
        this.state.reconnectMessage = message;
        this.patchLobby();
        this.patchGameOverlay();
      },
      onDisconnected: (message) => {
        this.state.connectionLost = true;
        this.state.reconnectMessage = message;
        this.patchLobby();
        this.patchGameOverlay();
      },
    };
  }

  private sendGameAction(action: "select_prop" | "toggle_disguise" | "scan" | "push_attack" | "area_pulse" | "interact" | "drop_carried", payload?: string | { x: number; y: number } | { direction: -1 | 1; targetId?: string }): void {
    const connection = this.multiplayer.currentConnection;
    if (!connection) return;
    const phase = this.state.roomPhase ?? this.state.snapshot?.phase;
    const player = this.state.latestScene?.self;
    const mode = this.state.snapshot?.mode;
    if (mode === "site_hide_seek" && (action === "select_prop" || action === "toggle_disguise" || action === "scan")) {
      const reason = this.hideActionBlockReason(action);
      if (reason) {
        this.setGameActionFeedback(reason);
        return;
      }
    }
    if (!player || player.caught || this.state.connectionLost) return;
    if (action === "scan" && (mode !== "site_hide_seek" || player.role !== "seeker" || phase !== "searching")) return;
    if ((action === "select_prop" || action === "toggle_disguise") && (mode !== "site_hide_seek" || player.role !== "hider" || (phase !== "hiding" && phase !== "searching"))) return;
    if (action === "select_prop" && typeof payload === "string") connection.send("select_prop", { templateId: payload });
    if (action === "toggle_disguise") connection.send("toggle_disguise", {});
    if (action === "scan" && typeof payload === "object" && payload && "x" in payload) connection.send("scan", payload);
    if (action === "push_attack" && typeof payload === "object" && "direction" in payload && phase === "playing" && (mode === "capture_logo" || mode === "page_brawl") &&
      (mode === "capture_logo" ? player.role === "teammate" : player.role === "fighter") && (player.stunnedMs ?? 0) === 0) connection.send("push_attack", payload);
    if (action === "area_pulse" && mode === "page_brawl" && phase === "playing" && player.role === "fighter" && (player.stunnedMs ?? 0) === 0) connection.send("area_pulse", {});
    if (action === "interact" && typeof payload === "string" && phase === "playing" && (player.stunnedMs ?? 0) === 0 &&
      ((mode === "browser_boss" && player.role === "cooperator") || (mode === "capture_logo" && player.role === "teammate"))) connection.send("interact", { targetId: payload });
    if (action === "drop_carried" && mode === "capture_logo" && phase === "playing" && player.role === "teammate" && this.state.latestScene?.arena?.mode === "capture_logo" && this.state.latestScene.arena.logo?.carrierId === player.id && (player.stunnedMs ?? 0) === 0) connection.send("drop_carried", {});
  }

  private clearHints(): void {
    window.clearTimeout(this.hintTimeout);
    this.hintTimeout = 0;
    this.state.hintRegions = [];
    this.gameSession?.setHintRegions([]);
  }

  private finishLocal(result: LocalRushResult): void {
    if (this.localScoreSaved) return;
    this.localScoreSaved = true;
    const map = this.state.loadedMap?.artifact;
    if (map && !this.state.localChallengeId) saveLocalScore(map, result.elapsedMs, result.penaltyMs, result.finished, result.checkpoint);
    this.state.localResult = result;
    this.state.screen = "local_result";
    this.sound.play("finish");
    this.render();
  }

  private lobbyScope(): string {
    const snapshot = this.state.snapshot;
    return JSON.stringify([this.state.screen, this.state.screen === "match_result" ? this.state.matchResult?.matchId : null,
      this.multiplayer.currentConnection?.roomId, snapshot?.roomCode, snapshot?.phase,
      snapshot?.artifactHash, snapshot?.mapVersionId, snapshot?.rulesHash, this.ownPlayerId, this.state.connectionLost, this.state.inviteToken]);
  }

  private clearLobbyInteraction(): void {
    window.clearTimeout(this.lobbyInteractionTimer);
    this.lobbyInteractionTimer = 0;
    this.lobbyInteractionScope = "";
    this.lobbyPatchPending = false;
  }

  private beginLobbyInteraction(event: Event): void {
    if (!["lobby", "match_result"].includes(this.state.screen) || this.lobbyInteractionScope || !(event.target instanceof Element) ||
      !event.target.closest("#main-content button, #main-content input, #main-content select, #main-content summary")) return;
    this.lobbyInteractionScope = this.lobbyScope();
    this.lobbyInteractionTimer = window.setTimeout(() => {
      const pending = this.lobbyPatchPending;
      this.clearLobbyInteraction();
      if (pending) this.patchRoomPanel();
    }, 2000);
  }

  private finishLobbyInteraction(): void {
    if (!this.lobbyInteractionScope) return;
    window.clearTimeout(this.lobbyInteractionTimer);
    // A task (rather than a microtask) lets the native click following pointerup/keyup run first.
    this.lobbyInteractionTimer = window.setTimeout(() => {
      const pending = this.lobbyPatchPending;
      this.clearLobbyInteraction();
      if (pending) this.patchRoomPanel();
    }, 0);
  }

  private patchLobby(): void {
    if (this.state.screen !== "lobby") { this.clearLobbyInteraction(); return; }
    const markup = this.renderLobby();
    let scopeChanged = false;
    if (this.lobbyInteractionScope) {
      if (this.lobbyInteractionScope === this.lobbyScope()) {
        if (markup !== this.lobbyMarkup) this.lobbyPatchPending = true;
        return;
      }
      this.clearLobbyInteraction(); // A changed room/version/phase must invalidate the old action target.
      scopeChanged = true;
    }
    // Compare generated markup, not native DOM attributes such as details.open or input selection.
    if (!scopeChanged && markup === this.lobbyMarkup) return;
    const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const priorFocus = focused?.dataset.control;
    const selection = focused instanceof HTMLInputElement ? [focused.selectionStart, focused.selectionEnd] : null;
    const main = this.root.querySelector<HTMLElement>("#main-content");
    if (!main) return;
    const replacement = document.createElement("div");
    replacement.innerHTML = markup;
    const next = replacement.firstElementChild;
    const old = main.firstElementChild;
    if (!next || !old) { this.render(); return; }
    old.replaceWith(next);
    this.lobbyMarkup = markup;
    if (priorFocus) {
      const target = this.root.querySelector<HTMLElement>(`[data-control="${priorFocus}"]`);
      target?.focus();
      if (target instanceof HTMLInputElement && selection?.[0] != null && selection[1] != null)
        target.setSelectionRange(selection[0], selection[1]);
    }
  }

  private patchRoomPanel(): void {
    if (this.state.screen === "lobby") this.patchLobby();
    else if (this.state.screen === "match_result") this.patchMatchResult();
    else this.clearLobbyInteraction();
  }

  private patchMatchResult(): void {
    if (this.state.screen !== "match_result") { this.clearLobbyInteraction(); return; }
    const markup = this.renderMatchResult();
    let scopeChanged = false;
    if (this.lobbyInteractionScope) {
      if (this.lobbyInteractionScope === this.lobbyScope()) {
        if (markup !== this.resultMarkup) this.lobbyPatchPending = true;
        return;
      }
      this.clearLobbyInteraction();
      scopeChanged = true;
    }
    if (!scopeChanged && markup === this.resultMarkup) return;
    const main = this.root.querySelector<HTMLElement>("#main-content");
    const replacement = document.createElement("div");
    replacement.innerHTML = markup;
    const next = replacement.firstElementChild;
    const old = main?.firstElementChild;
    if (!next || !old) { this.render(); return; }
    old.replaceWith(next);
    this.resultMarkup = markup;
  }

  private patchGameOverlay(): void {
    if (this.state.screen !== "game") return;
    const existing = this.root.querySelector<HTMLElement>(".game-overlay");
    const host = this.root.querySelector<HTMLElement>("#game-root");
    if (!host) return;
    const previous = this.gameSession;
    const replacement = document.createElement("div");
    replacement.innerHTML = this.renderGame();
    const nextGame = replacement.querySelector<HTMLElement>(".game-screen");
    if (!nextGame) return;
    const nextOverlay = nextGame.querySelector<HTMLElement>(".game-overlay");
    if (existing && nextOverlay) existing.replaceWith(nextOverlay);
    else if (existing && !nextOverlay) existing.remove();
    else if (!existing && nextOverlay) host.append(nextOverlay);
    this.patchHideControls();
    this.patchGameGhostControl();
    if (previous) previous.setFrozen(this.state.connectionLost || this.state.assetLoad !== "ready" || Boolean(this.state.latestScene?.self?.caught));
  }

  private patchGameGhostControl(): void {
    const toolbar = this.root.querySelector<HTMLElement>(".game-toolbar");
    if (!toolbar) return;
    let control = toolbar.querySelector<HTMLElement>(".game-ghost-control");
    if (!this.canShowOwnBestControl()) {
      control?.remove();
      return;
    }
    if (!control) {
      control = document.createElement("div");
      control.className = "game-ghost-control";
      const button = document.createElement("button");
      button.className = "button button-small button-secondary";
      button.type = "button";
      button.dataset.action = "own-best-ghost";
      const status = document.createElement("span");
      status.className = "field-hint";
      status.setAttribute("role", "status");
      control.append(button, status);
      const stats = toolbar.querySelector(".game-stats");
      toolbar.insertBefore(control, stats);
    }
    const button = control.querySelector<HTMLButtonElement>("button[data-action='own-best-ghost']");
    const status = control.querySelector<HTMLElement>("[role='status']");
    if (button) {
      button.disabled = this.state.ownBestLoad === "loading" || this.state.assetLoad !== "ready";
      button.setAttribute("aria-pressed", String(this.state.ownBestVisible));
      button.textContent = this.state.ownBestLoad === "loading" ? tr.bestGhostLoading : this.state.ownBestVisible ? tr.hideBestGhost : tr.showBestGhost;
    }
    if (status) status.textContent = this.state.ownBestError || (this.state.ownBestVisible ? this.state.ownBestReplay?.nickname ?? "" : "");
  }

  private patchPracticeRolePicker(): void {
    const picker = this.root.querySelector<HTMLElement>(".practice-role-picker");
    const snapshot = this.state.snapshot;
    if (!picker || !snapshot?.practice || snapshot.mode !== "site_hide_seek") return;
    const role = this.state.latestScene?.self?.role;
    const enabled = (this.state.roomPhase ?? snapshot.phase) === "searching" && !this.state.connectionLost;
    for (const button of picker.querySelectorAll<HTMLButtonElement>("button[data-action^='practice-']")) {
      const selected = button.dataset.action === `practice-${role}`;
      button.disabled = !enabled;
      button.setAttribute("aria-pressed", String(selected));
      button.classList.toggle("button-primary", selected);
      button.classList.toggle("button-secondary", !selected);
    }
  }

  private announce(message: string): void {
    const announcer = this.root.querySelector<HTMLElement>("#app-announcer, #game-announcer");
    if (announcer) announcer.textContent = message;
  }

  private setGameActionFeedback(message: string): void {
    this.state.gameActionFeedback = message;
    this.announce(message);
    const status = this.root.querySelector<HTMLElement>("#game-hud-status");
    if (status) status.textContent = message;
    if (this.gameActionFeedbackTimer !== null) window.clearTimeout(this.gameActionFeedbackTimer);
    this.gameActionFeedbackTimer = window.setTimeout(() => {
      this.gameActionFeedbackTimer = null;
      if (this.state.gameActionFeedback !== message) return;
      this.clearGameActionFeedback();
      const currentStatus = this.root.querySelector<HTMLElement>("#game-hud-status");
      if (currentStatus) currentStatus.textContent = this.gameHudStatus || this.gameHelp(this.state.snapshot?.mode ?? "scroll_rush");
    }, 3600);
  }

  private clearGameActionFeedback(): void {
    if (this.gameActionFeedbackTimer !== null) window.clearTimeout(this.gameActionFeedbackTimer);
    this.gameActionFeedbackTimer = null;
    const announcer = this.root.querySelector<HTMLElement>("#game-announcer");
    if (announcer?.textContent === this.state.gameActionFeedback) announcer.textContent = "";
    this.state.gameActionFeedback = "";
  }

  private async handleClick(event: Event): Promise<void> {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-action], [data-map-hash], [data-setting]") : null;
    if (!target) return;
    if (target.hasAttribute("data-setting")) {
      const setting = target.dataset.setting as keyof Preferences | undefined;
      if (setting === "soundEnabled" || setting === "reducedMotion") this.togglePreference(setting);
      return;
    }
    const mapHash = target.dataset.mapHash;
    if (mapHash) {
      if (this.state.screen === "maps") this.state.selectedHash = mapHash;
      this.state.createMapHash = mapHash;
      if (this.state.screen !== "game") this.render();
      this.sound.play("click");
      return;
    }
    const action = target.dataset.action;
    if (!action) return;
    if (target instanceof HTMLAnchorElement) event.preventDefault();
    switch (action) {
      case "home": {
        this.cancelLocalVersionLoad();
        if (["lobby", "game", "match_result", "practice_starting"].includes(this.state.screen) || this.roomRequestAbort) void this.leaveRoom();
        this.state.screen = "home"; this.state.pendingChallengeId = ""; this.state.formError = ""; this.state.notice = "";
        const homeUrl = new URL(window.location.href);
        homeUrl.pathname = "/";
        homeUrl.search = "";
        window.history.replaceState(null, "", homeUrl);
        this.render(); break;
      }
      case "library": this.navigate("/library"); if (this.state.libraryLoad === "idle" || this.state.libraryLoad === "error") void this.loadLibrary(); break;
      case "discover": this.navigate("/discover"); if (this.state.discoverLoad === "idle" || this.state.discoverLoad === "error") void this.loadDiscover(); break;
      case "profile": this.navigate("/profile"); if (this.state.profileLoad === "idle" || this.state.profileLoad === "error") void this.loadProfile(true); break;
      case "admin": this.navigate("/admin"); if (this.state.adminLoad === "idle" || this.state.adminLoad === "error") void this.loadAdminSummary(); break;
      case "map-detail": if (this.state.mapView) this.navigate(`/maps/${encodeURIComponent(this.state.mapView.id)}`); break;
      case "map-edit": if (this.state.mapView) this.navigate(`/maps/${encodeURIComponent(this.state.mapView.id)}/edit`); break;
      case "open-map": if (target.dataset.mapId) this.navigate(`/maps/${encodeURIComponent(target.dataset.mapId)}`); break;
      case "edit-map-id": if (target.dataset.mapId) this.navigate(`/maps/${encodeURIComponent(target.dataset.mapId)}/edit`); break;
      case "how": this.root.querySelector("#modes")?.scrollIntoView({ behavior: this.preferences.reducedMotion ? "auto" : "smooth" }); break;
      case "focus-url": {
        this.state.screen = "home";
        this.render();
        this.root.querySelector<HTMLInputElement>("#source-url")?.focus();
        this.root.querySelector<HTMLInputElement>("#source-url")?.scrollIntoView({ block: "center", behavior: this.preferences.reducedMotion ? "auto" : "smooth" });
        break;
      }
      case "open-converted-map": {
        const mapId = target.dataset.mapId;
        if (mapId) this.navigate(`/maps/${encodeURIComponent(mapId)}`);
        break;
      }
      case "reload-conversion": if (this.state.conversionRequestId) void this.refreshConversion(this.state.conversionRequestId, true); break;
      case "cancel-conversion": void this.cancelConversion(); break;
      case "retry-conversion": void this.retryConversion(); break;
      case "retry-map-view": {
        const mapId = this.state.mapView?.id ?? window.location.pathname.split("/")[2];
        if (mapId) void this.loadMapView(mapId);
        break;
      }
      case "select-map-mode": {
        const parsedMode = ActiveModeSchema.safeParse(target.dataset.mode);
        const map = this.state.mapView;
        if (!parsedMode.success || this.state.screen !== "map_detail" || !map || !this.mapModeIsPlayable(map, parsedMode.data)) break;
        const version = this.modeVersionForSelection(map, parsedMode.data);
        if (!version) break;
        if (this.state.previewLoad === "loading" && this.state.previewVersionId === version.id) break;
        if (this.state.previewLoad === "loading") {
          this.privateAssetAbort?.abort();
          this.privateAssetAbort = null;
          this.privateAssetGeneration++;
        }
        const previewReady = this.state.previewLoad === "ready" && this.state.previewVersionId === version.id &&
          this.state.privatePreview?.artifact.artifactHash === version.artifactHash;
        this.state.selectedMode = parsedMode.data;
        this.state.previewVersionId = version.id;
        this.state.previewError = "";
        this.state.previewLoad = previewReady ? "ready" : "idle";
        this.render();
        this.focusMapModeButton(parsedMode.data);
        if (!previewReady) void this.loadAuthorizedPreview(version.id);
        break;
      }
      case "library-save-map": if (target.dataset.mapId) void this.saveMapToLibrary(target.dataset.mapId); break;
      case "favorite-map": if (target.dataset.mapId) void this.toggleMapFavorite(target.dataset.mapId, target.dataset.favorite === "true"); break;
      case "recapture-map": if (target.dataset.mapId) void this.recaptureMap(target.dataset.mapId); break;
      case "create-share": if (target.dataset.mapId) void this.createMapShare(target.dataset.mapId); break;
      case "copy-map-share": if (this.state.shareView) { try { await navigator.clipboard.writeText(this.state.shareView.url); this.state.notice = tr.shareLinkCopied; } catch { this.state.notice = tr.shareLinkReadyToCopy; } this.render(); } break;
      case "revoke-map-share": if (target.dataset.shareId) void this.revokeMapShare(target.dataset.shareId); break;
      case "publish-map": if (target.dataset.mapId) void this.publishMap(target.dataset.mapId); break;
      case "load-private-preview": if (target.dataset.versionId) void this.loadAuthorizedPreview(target.dataset.versionId); break;
      case "creator-region-preview": void this.createCreatorRegionPreview(); break;
      case "start-version-practice": if (target.dataset.versionId) void this.startVersionPractice(target.dataset.versionId); break;
      case "create-version-room": if (target.dataset.versionId) void this.beginVersionRoom(target.dataset.versionId); break;
      case "report-map": {
        const ownerTools = this.root.querySelector<HTMLDetailsElement>("#map-owner-tools");
        if (ownerTools) ownerTools.open = true;
        this.root.querySelector<HTMLElement>(".report-form")?.scrollIntoView({ behavior: this.preferences.reducedMotion ? "auto" : "smooth", block: "center" });
        this.root.querySelector<HTMLSelectElement>(".report-form select")?.focus();
        break;
      }
      case "reload-library": this.state.libraryLoad = "idle"; void this.loadLibrary(); break;
      case "library-filter": this.state.libraryFavoritesOnly = !this.state.libraryFavoritesOnly; this.render(); break;
      case "reload-discover": this.state.discoverLoad = "idle"; void this.loadDiscover(); break;
      case "discover-more": if (this.state.discoverPage?.nextCursor) void this.loadDiscover(this.state.discoverPage.nextCursor); break;
      case "join-public-room": if (target.dataset.roomCode) void this.joinPublicRoom(target.dataset.roomCode); break;
      case "reload-profile": void this.loadProfile(true); break;
      case "delete-account": void this.deleteAccount(); break;
      case "reload-admin": this.state.adminLoad = "idle"; void this.loadAdminSummary(); break;
      case "admin-report-dismiss":
      case "admin-report-takedown": {
        const reportId = target.dataset.reportId;
        const reason = target.closest(".admin-row")?.querySelector<HTMLInputElement>("[name=adminReason]")?.value ?? "";
        if (reportId) void this.adminReportAction(reportId, action === "admin-report-dismiss" ? "dismiss" : "takedown", reason);
        break;
      }
      case "creator-share": void this.createCreatorShare(); break;
      case "copy-creator-share": if (this.state.creatorShare) { try { await navigator.clipboard.writeText(this.state.creatorShare.url); this.state.notice = tr.shareLinkCopied; } catch { this.state.notice = tr.shareLinkReadyToCopy; } this.render(); } break;
      case "revoke-creator-share": if (target.dataset.shareId) { await this.revokeMapShare(target.dataset.shareId); this.state.creatorShare = null; this.render(); } break;
      case "play-creator-challenge": void this.startCreatorChallenge(); break;
      case "creator-ghost": if (target.dataset.replayId) void this.loadCreatorReplay(target.dataset.replayId); break;
      case "reload-creator-rankings": {
        const version = this.state.creatorChallenge?.versions.find((item) => item.id === this.state.creatorVersionId);
        if (this.state.creatorChallenge && version) void this.loadCreatorRankings(this.state.creatorChallenge.id, version.id, version.mode === "browser_boss" ? this.state.creatorPlayerCount : 1);
        break;
      }
      case "own-best-ghost": void this.toggleOwnBestGhost(); break;
      case "retry-creator-challenge": if (this.state.creatorChallengeId) void this.loadCreatorChallenge(this.state.creatorChallengeId); break;
      case "local":
        this.state.screen = "maps"; this.state.selectedMode = "scroll_rush"; this.state.selectedHash = this.state.maps.find((map) => map.mode === "scroll_rush")?.artifactHash ?? ""; this.state.assetError = ""; this.state.formError = ""; this.render(); break;
      case "create": this.state.screen = "create"; this.state.pendingChallengeId = ""; this.state.formError = ""; this.state.createMode = "scroll_rush"; this.state.createMapHash = this.state.maps.find((map) => map.mode === "scroll_rush")?.artifactHash ?? ""; this.render(); break;
      case "create-mode": {
        const mode = ActiveModeSchema.safeParse(target.dataset.mode);
        if (!mode.success || !this.state.maps.some((map) => map.mode === mode.data)) break;
        this.state.screen = "create"; this.state.pendingChallengeId = ""; this.state.formError = "";
        this.state.createMode = mode.data; this.state.createMapHash = this.state.maps.find((map) => map.mode === mode.data)?.artifactHash ?? "";
        this.render(); break;
      }
      case "practice-mode": {
        const mode = ActiveModeSchema.safeParse(target.dataset.mode);
        if (mode.success) void this.startPractice(mode.data);
        break;
      }
      case "practice-again": {
        const mode = ActiveModeSchema.safeParse(target.dataset.mode ?? this.state.practiceMode);
        if (!mode.success) break;
        if (this.state.screen === "match_result") {
          const result = this.state.matchResult;
          if (result?.practice && result.mode === mode.data) this.retryPracticeResult(result);
          break;
        }
        const requestedHash = target.dataset.artifactHash;
        const versionId = target.dataset.versionId;
        if (versionId) {
          const version = this.state.mapView?.versions.find((item) => item.id === versionId);
          if (!requestedHash || !version || version.mode !== mode.data || version.artifactHash !== requestedHash || requestedHash !== this.state.practiceRetryHash) {
            this.state.practiceRetryHash = "";
            this.state.practiceRetryVersionId = "";
            this.state.formError = tr.assetMismatch;
            this.render();
          } else void this.startVersionPractice(versionId);
        } else if (requestedHash && requestedHash === this.state.practiceRetryHash) {
          void this.startPractice(mode.data, requestedHash);
        }
        break;
      }
      case "challenge-create":
        this.state.screen = "challenge_create"; this.state.formError = ""; this.state.challenge = null;
        this.state.createChallengeMapHash = this.state.maps.find((map) => map.mode === "scroll_rush")?.artifactHash ?? "";
        this.state.createChallengeOptionId = "";
        this.state.assetLoad = this.state.loadedMap?.artifact.artifactHash === this.state.createChallengeMapHash ? "ready" : "idle";
        this.render(); break;
      case "join": this.state.screen = "join"; this.state.formError = ""; this.render(); break;
      case "settings": this.state.screen = "settings"; this.render(); break;
      case "history": this.state.screen = "history"; this.state.historyLoad = "idle"; this.render(); break;
      case "retry-maps": void this.refreshCatalog(); break;
      case "start-local": await this.startLocalRush(); break;
      case "local-again": {
        const result = this.state.localResult;
        const versionId = target.dataset.versionId || this.state.localVersionId;
        const version = versionId ? this.state.mapView?.versions.find((item) => item.id === versionId && item.mode === "scroll_rush") : undefined;
        if (version) await this.startLocalVersionRush(version.id);
        else await this.startLocalRush(result?.artifactHash, this.state.localChallengeOptionId || undefined, this.state.localChallengeId || undefined);
        break;
      }
      case "start-local-version-rush": if (target.dataset.versionId) await this.startLocalVersionRush(target.dataset.versionId); break;
      case "load-challenge-map": await this.loadChallengeMapForCreation(); break;
      case "challenge-practice":
      case "challenge-play":
        if (!this.state.challenge || this.state.challengeLoad !== "ready") break;
        void this.startLocalRush(this.state.challenge.artifactHash, this.state.challenge.optionId, this.state.challenge.id);
        break;
      case "copy-challenge": await this.copyChallenge(); break;
      case "challenge-ghost": if (target.dataset.replayId) await this.loadChallengeReplay(target.dataset.replayId); break;
      case "reload-challenge": if (this.state.challengeId) void this.loadChallenge(this.state.challengeId); break;
      case "reload-rankings": if (this.state.challengeId) void this.loadChallengeRankings(this.state.challengeId); break;
      case "open-challenge-result": {
        const id = this.state.matchResult?.challengeId ?? this.state.snapshot?.challengeId;
        if (!id) break;
        void this.leaveRoom();
        this.state.challengeId = id;
        this.state.screen = "challenge";
        this.updateChallengeUrl(id);
        this.render();
        void this.loadChallenge(id);
        break;
      }
      case "toggle-sound": this.togglePreference("soundEnabled"); break;
      case "copy-invite": await this.copyInvite(); break;
      case "toggle-ready": this.toggleReady(); break;
      case "select-team": {
        const team = target.dataset.team;
        const snapshot = this.state.snapshot;
        if ((team === "red" || team === "blue") && snapshot?.mode === "capture_logo" && snapshot.phase === "lobby" && !snapshot.practice && this.ownPlayerId) {
          this.multiplayer.currentConnection?.send("select_team", { team });
        }
        break;
      }
      case "host-start": this.startMultiplayerMatch(); break;
      case "leave-room": this.leaveRoomToSourceOrHome(); break;
      case "leave-game": void this.leaveGame(); break;
      case "rematch": this.requestRematch(); break;
      case "retry-asset": if (this.state.snapshot) await this.loadRoomMap(this.state.snapshot); break;
      case "retry-reconnect": await this.multiplayer.currentConnection?.retryReconnect(); break;
      case "dismiss-error": this.state.formError = ""; this.patchGameOverlay(); break;
      case "select-prop": {
        const reason = this.hideActionBlockReason("select_prop");
        if (reason) this.setGameActionFeedback(reason);
        else this.gameSession?.chooseNearestProp();
        break;
      }
      case "toggle-disguise": this.sendGameAction("toggle_disguise"); break;
      case "scan": {
        const reason = this.hideActionBlockReason("scan");
        if (reason) this.setGameActionFeedback(reason);
        else this.gameSession?.scanNearestProp();
        break;
      }
      case "push": this.gameSession?.pushNearestActor(); break;
      case "area-burst": this.gameSession?.areaBurst(); break;
      case "practice-hider": this.setPracticeRole("hider"); break;
      case "practice-seeker": this.setPracticeRole("seeker"); break;
      case "reload-history": this.state.historyLoad = "idle"; void this.loadHistory(); break;
      case "clear-history": this.clearHistory(); break;
    }
  }

  private async handleSubmit(event: Event): Promise<void> {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const kind = form.dataset.form;
    if (kind === "conversion") {
      event.preventDefault();
      const values = new FormData(form);
      void this.submitConversion(String(values.get("url") ?? ""));
      return;
    }
    if (kind === "profile") {
      event.preventDefault();
      const values = new FormData(form);
      const characterId = CharacterIdSchema.safeParse(values.get("characterId"));
      const locale = values.get("locale");
      if (!characterId.success || (locale !== "tr-TR" && locale !== "en-US")) { this.state.profileError = tr.profileInputInvalid; this.render(); return; }
      this.state.busy = true;
      this.state.profileError = "";
      this.render();
      try {
        this.state.profile = await this.productApi.updateProfile({ nickname: String(values.get("nickname") ?? ""), characterId: characterId.data, locale });
        this.state.profileLoad = "ready";
        setLocale(locale);
        this.localeDirty = false;
        this.state.notice = tr.profileSaved;
      } catch (error) { this.state.profileError = this.productErrorText(error); }
      finally { this.state.busy = false; this.render(); }
      return;
    }
    if (kind === "upgrade-account") {
      event.preventDefault();
      this.state.busy = true;
      this.state.formError = "";
      this.render();
      try {
        await this.multiplayer.requestAccountEmail(String(new FormData(form).get("email") ?? ""));
        this.state.notice = tr.accountUpgradeAccepted;
      } catch (error) { this.state.formError = this.productErrorText(error); }
      finally { this.state.busy = false; this.render(); }
      return;
    }
    if (kind === "set-password") {
      event.preventDefault();
      this.state.busy = true;
      this.state.formError = "";
      this.render();
      try {
        await this.multiplayer.setAccountPassword(String(new FormData(form).get("password") ?? ""));
        this.state.notice = tr.accountPasswordSaved;
      } catch (error) { this.state.formError = this.productErrorText(error); }
      finally { this.state.busy = false; this.render(); }
      return;
    }
    if (kind === "report") {
      event.preventDefault();
      const values = new FormData(form);
      try {
        await this.productApi.reportMap({ mapId: String(values.get("mapId") ?? ""), reason: String(values.get("reason") ?? "other") as "privacy" | "rights" | "unsafe" | "abuse" | "other", details: String(values.get("details") ?? "") });
        this.state.notice = tr.reportSubmitted;
      } catch (error) { this.state.notice = this.productErrorText(error); }
      this.render();
      return;
    }
    if (kind === "map-edit") {
      event.preventDefault();
      const values = new FormData(form);
      const map = this.state.mapView;
      const mode = ActiveModeSchema.safeParse(values.get("mode"));
      const versionId = String(values.get("versionId") ?? "");
      const coords = ["region-x", "region-y", "region-width", "region-height"].map((name) => Number(values.get(name)));
      const selectedRegion = this.creatorRegionFromForm(form);
      const region: Rect = selectedRegion ?? { x: coords[0] ?? Number.NaN, y: coords[1] ?? Number.NaN, width: coords[2] ?? Number.NaN, height: coords[3] ?? Number.NaN };
      const activeVersion = map?.versions.find((entry) => entry.id === versionId);
      const artifact = this.state.privatePreview?.artifact;
      const loaded = this.state.privatePreview;
      if (!map || !mode.success || !activeVersion || activeVersion.mapId !== map.id || activeVersion.mode !== mode.data || this.state.mapEditMode !== mode.data ||
        !loaded || this.state.previewVersionId !== versionId || !this.creatorArtifactMatchesVersion(activeVersion, loaded) || !artifact ||
        coords.some((value) => !Number.isFinite(value)) || (artifact.schemaVersion === 4 && !selectedRegion) || !this.validSourceRegion(region, map.provenance.selectedRegion, mode.data)) {
        this.state.formError = artifact?.schemaVersion === 4 && !selectedRegion ? tr.creatorRegionWholeTilesRequired : tr.regionInvalid;
        this.render();
        return;
      }
      const baseVersionId = artifact.schemaVersion === 4 ? activeVersion.id : undefined;
      if (baseVersionId) {
        values.set("region-x", String(region.x));
        values.set("region-y", String(region.y));
        values.set("region-width", String(region.width));
        values.set("region-height", String(region.height));
      }
      if (baseVersionId && !this.sameRegion(region, artifact.region)) {
        this.state.formError = tr.creatorRegionPreviewOnly;
        this.render();
        return;
      }
      let patch: ReturnType<typeof CreatorPatchSchema.parse>;
      try { patch = this.creatorPatchFromForm(artifact, values); }
      catch { this.state.formError = tr.creatorPatchInvalid; this.render(); return; }
      this.state.busy = true;
      this.state.formError = "";
      this.render();
      try {
        const request = await this.productApi.createVariant(map.id, { mode: mode.data, region, patch, ...(baseVersionId ? { baseVersionId } : {}) });
        const parsed = (await import("@urlcade/contracts")).ConversionRequestSchema.safeParse(request);
        if (!parsed.success) throw new Error("PRODUCT_RESPONSE_INVALID");
        if (!this.mapEditSelectionMatches(map.id, activeVersion.id, mode.data, loaded)) {
          this.state.busy = false;
          if (this.state.screen === "map_edit") this.render();
          return;
        }
        this.state.editRegion = region;
        this.state.conversionRequestId = parsed.data.id;
        this.state.conversionStatus = parsed.data;
        this.state.conversionLoad = "ready";
        this.state.busy = false;
        this.navigate(`/build/${encodeURIComponent(parsed.data.id)}`);
      } catch (error) {
        if (!this.mapEditSelectionMatches(map.id, activeVersion.id, mode.data, loaded)) {
          this.state.busy = false;
          if (this.state.screen === "map_edit") this.render();
          return;
        }
        this.state.formError = this.productErrorText(error); this.state.busy = false; this.render();
      }
      return;
    }
    if (kind === "creator-draft") {
      event.preventDefault();
      const values = new FormData(form);
      const versionId = String(values.get("mapVersionId") ?? "");
      const sourceMap = this.state.mapView;
      const sourceVersion = sourceMap?.versions.find((version) => version.id === versionId);
      if (!sourceMap?.owner || !sourceVersion || sourceVersion.mapId !== sourceMap.id) {
        this.state.formError = tr.noMapVersion;
        this.render();
        return;
      }
      const sourceSnapshotId = sourceMap.snapshotId;
      const sourceBounds = sourceMap.provenance.selectedRegion;
      const sourceSelectionIsCurrent = (): boolean => {
        const currentMap = this.state.mapView;
        const currentVersion = currentMap?.versions.find((version) => version.id === versionId);
        return this.state.screen === "map_edit" && currentMap?.id === sourceMap.id && currentMap.snapshotId === sourceSnapshotId &&
          currentMap.owner && this.sameRegion(currentMap.provenance.selectedRegion, sourceBounds) &&
          currentVersion?.mapId === sourceMap.id && currentVersion.mode === sourceVersion.mode &&
          currentVersion.artifactHash === sourceVersion.artifactHash && currentVersion.rulesHash === sourceVersion.rulesHash;
      };
      this.state.busy = true;
      this.state.formError = "";
      this.render();
      try {
        const draft = await this.productApi.createCreatorChallenge({ mapVersionId: versionId, title: String(values.get("title") ?? "").trim(), description: String(values.get("description") ?? "").trim(), visibility: String(values.get("visibility") ?? "private") as "private" | "unlisted" | "public" });
        const returnedVersion = draft.versions.find((version) => version.id === sourceVersion.id);
        if (draft.mapId !== sourceMap.id || !draft.owner || !returnedVersion || returnedVersion.mapId !== sourceMap.id ||
          returnedVersion.mode !== sourceVersion.mode || returnedVersion.artifactHash !== sourceVersion.artifactHash || returnedVersion.rulesHash !== sourceVersion.rulesHash) {
          throw new Error("VERSION_MISMATCH");
        }
        if (!sourceSelectionIsCurrent()) {
          this.state.busy = false;
          if (this.state.screen === "map_edit") this.render();
          return;
        }
        this.state.creatorChallenge = draft;
        this.state.creatorChallengeId = draft.id;
        this.state.creatorChallengeLoad = "ready";
        this.state.creatorVersionId = versionId;
        this.state.creatorSourceBounds = sourceBounds;
        this.state.creatorChallengeRankings = [];
        this.state.screen = "creator_challenge";
        this.state.busy = false;
        this.updateCreatorChallengeUrl(draft.id);
        this.render();
      } catch (error) {
        if (!sourceSelectionIsCurrent()) {
          this.state.busy = false;
          if (this.state.screen === "map_edit") this.render();
          return;
        }
        this.state.formError = this.productErrorText(error);
        this.state.busy = false;
        this.render();
      }
      return;
    }
    if (kind === "creator-version") {
      event.preventDefault();
      const challenge = this.state.creatorChallenge;
      const versionId = this.state.creatorVersionId;
      const version = challenge?.versions.find((item) => item.id === versionId);
      const loaded = this.state.privatePreview;
      if (!challenge || !version || version.mapId !== challenge.mapId || !loaded || this.state.previewVersionId !== version.id || !this.creatorArtifactMatchesVersion(version, loaded)) { this.state.creatorChallengeError = tr.previewRequiresAccess; this.render(); return; }
      const values = new FormData(form);
      const selectedRegion = this.creatorRegionFromForm(form);
      if (loaded.artifact.schemaVersion === 4 && !selectedRegion) {
        this.state.creatorChallengeError = tr.creatorRegionWholeTilesRequired;
        this.render();
        return;
      }
      if (loaded.artifact.schemaVersion === 4 && selectedRegion) {
        values.set("region-x", String(selectedRegion.x));
        values.set("region-y", String(selectedRegion.y));
        values.set("region-width", String(selectedRegion.width));
        values.set("region-height", String(selectedRegion.height));
      }
      let patch: ReturnType<typeof CreatorPatchSchema.parse>;
      try { patch = this.creatorPatchFromForm(loaded.artifact, values); }
      catch { this.state.creatorChallengeError = tr.creatorPatchInvalid; this.render(); return; }
      if (!this.state.creatorSourceBounds || !patch.region || !this.validSourceRegion(patch.region, this.state.creatorSourceBounds, patch.mode)) {
        this.state.creatorChallengeError = tr.regionInvalid;
        this.render();
        return;
      }
      const baseVersionId = loaded.artifact.schemaVersion === 4 ? version.id : undefined;
      if (baseVersionId && !this.sameRegion(patch.region, loaded.artifact.region)) {
        this.state.creatorChallengeError = tr.creatorRegionPreviewRequired;
        this.render();
        return;
      }
      this.state.busy = true;
      this.state.creatorChallengeError = "";
      this.render();
      try {
        const response = await this.productApi.createChallengeVersion(challenge.id, patch, baseVersionId);
        const parsed = (await import("@urlcade/contracts")).ConversionRequestSchema.safeParse(response);
        if (!parsed.success) throw new Error("PRODUCT_RESPONSE_INVALID");
        if (!this.creatorChallengeSelectionMatches(challenge.id, version.id, loaded)) {
          this.state.busy = false;
          if (this.state.screen === "creator_challenge") this.render();
          return;
        }
        this.state.conversionRequestId = parsed.data.id;
        this.state.conversionStatus = parsed.data;
        this.state.conversionLoad = "ready";
        this.state.busy = false;
        this.navigate(`/build/${encodeURIComponent(parsed.data.id)}`);
      } catch (error) {
        if (!this.creatorChallengeSelectionMatches(challenge.id, version.id, loaded)) {
          this.state.busy = false;
          if (this.state.screen === "creator_challenge") this.render();
          return;
        }
        this.state.creatorChallengeError = this.productErrorText(error); this.state.busy = false; this.render();
      }
      return;
    }
    if (kind === "admin-policy") {
      event.preventDefault();
      const values = new FormData(form);
      try {
        await this.productApi.adminPolicy({ host: String(values.get("host") ?? ""), action: String(values.get("action") ?? "block") as "allow" | "block", reason: String(values.get("reason") ?? "") });
        await this.loadAdminSummary();
      } catch (error) { this.state.adminError = this.productErrorText(error); this.render(); }
      return;
    }
    if (kind !== "create" && kind !== "join" && kind !== "challenge-create") return;
    event.preventDefault();
    if (!this.multiplayer.enabled) { this.state.formError = multiplayerDisabledMessage(); this.render(); return; }
    const values = new FormData(form);
    const nickname = String(values.get("nickname") ?? "");
    this.state.formError = "";
    this.state.busy = true;
    const roomRequest = kind === "challenge-create" ? 0 : ++this.roomRequestId;
    const roomController = kind === "challenge-create" ? null : new AbortController();
    if (roomController) this.roomRequestAbort = roomController;
    this.render();
    try {
      if (kind === "challenge-create") {
        const title = String(values.get("title") ?? "").trim();
        const loaded = this.state.loadedMap;
        if (title.length < 2 || title.length > 60) throw new Error(tr.challengeTitleValidation);
        if (!loaded || loaded.artifact.artifactHash !== this.state.createChallengeMapHash || loaded.artifact.setup.mode !== "scroll_rush") throw new Error(tr.challengeIntegrityHint);
        const optionId = String(values.get("option") ?? this.state.createChallengeOptionId);
        if (!loaded.artifact.setup.challengeOptions?.some((option) => option.id === optionId)) throw new Error(tr.challengeOptionMissing);
        const challenge = await this.multiplayer.createChallenge({ artifactHash: loaded.artifact.artifactHash, optionId, title });
        this.state.challenge = challenge;
        this.state.challengeId = challenge.id;
        this.state.pendingChallengeId = "";
        this.state.challengeLoad = "ready";
        this.state.challengeError = "";
        this.state.challengeRankings = [];
        this.state.rankingsLoad = "idle";
        this.state.challengeReplay = null;
        this.state.screen = "challenge";
        this.state.busy = false;
        this.updateChallengeUrl(challenge.id);
        this.render();
        this.sound.play("ready");
        void this.loadChallengeRankings(challenge.id);
        return;
      }
      if (kind === "create") {
        const selectedMode = values.get("mode") ?? this.state.createMode;
        const parsedMode = ActiveModeSchema.safeParse(selectedMode);
        const mode = parsedMode.success ? parsedMode.data : null;
        const mapVersionId = String(values.get("mapVersionId") ?? "");
        const version = mapVersionId ? this.state.mapView?.versions.find((item) => item.id === mapVersionId) : undefined;
        if (!mode) throw new Error(tr.roomModeInvalid);
        if (!version || version.mode !== mode || version.protocolVersion !== PROTOCOL_VERSION || !version.rulesHash) throw new Error(tr.createVersionRequired);
        const mapHash = version.artifactHash;
        if (this.state.pendingChallengeId) throw new Error(tr.practiceOnlyLegacyVersion);
        this.state.createMode = mode;
        this.state.createMapHash = mapHash;
        if (this.state.privatePreview?.artifact.artifactHash !== version.artifactHash) await this.loadAuthorizedPreview(version.id);
        if (roomRequest !== this.roomRequestId || roomController?.signal.aborted) return;
        const loaded = this.state.privatePreview;
        if (!loaded || loaded.artifact.schemaVersion < 3 || loaded.artifact.artifactHash !== version.artifactHash || loaded.artifact.compilerVersion !== version.compilerVersion ||
          loaded.artifact.rulesHash !== version.rulesHash || loaded.artifact.protocolVersion !== PROTOCOL_VERSION ||
          loaded.artifact.simulationVersion !== version.simulationVersion) throw new Error(tr.assetMismatch);
        const teamSizeValue = Number(values.get("teamSize"));
        const created = await this.multiplayer.createRoom(mode, mapHash, nickname, this.roomHandlers(), {
          mapVersionId: version.id,
          challengeVersionId: version.challengeVersionId ?? undefined,
          characterId: this.state.profile?.characterId ?? undefined,
          listed: values.get("listed") === "on",
          ...(mode === "capture_logo" && (teamSizeValue === 2 || teamSizeValue === 3) ? { teamSize: teamSizeValue as 2 | 3 } : {}),
          signal: roomController?.signal,
        });
        if (roomRequest !== this.roomRequestId || roomController?.signal.aborted) return;
        this.state.roomCode = created.code;
        this.state.inviteToken = created.inviteToken;
      } else {
        const inviteToken = String(values.get("inviteToken") ?? this.state.inviteToken);
        const code = String(values.get("roomCode") ?? "");
        const characterId = this.state.profile?.characterId;
        const joined = inviteToken
          ? await this.multiplayer.joinInvite(inviteToken, nickname, this.roomHandlers(), roomController?.signal, characterId)
          : await this.multiplayer.joinRoom(code, nickname, this.roomHandlers(), roomController?.signal, characterId);
        if (roomRequest !== this.roomRequestId || roomController?.signal.aborted) return;
        this.state.roomCode = joined.code;
        this.state.inviteToken = joined.inviteToken;
      }
      this.state.screen = "lobby";
      this.state.notice = kind === "create" ? tr.roomCreated : tr.roomJoined;
      this.state.busy = false;
      this.updateUrl(this.multiplayer.currentConnection?.roomId ?? this.state.roomCode);
      this.render();
      if (this.state.snapshot) void this.loadRoomMap(this.state.snapshot);
      this.sound.play("ready");
    } catch (error) {
      if (roomRequest && (roomRequest !== this.roomRequestId || roomController?.signal.aborted)) return;
      this.state.formError = error instanceof Error ? error.message : tr.multiplayerError;
      this.state.busy = false;
      this.render();
    } finally {
      if (roomRequest === this.roomRequestId && roomController && this.roomRequestAbort === roomController) this.roomRequestAbort = null;
    }
  }

  private handleChange(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.dataset.setting === "quality") {
      if (target.value === "economy" || target.value === "balanced" || target.value === "high") this.setVisualQuality(target.value);
      return;
    }
    if (target instanceof HTMLInputElement && target.type === "checkbox" && target.name === "listed") {
      this.state.roomListed = target.checked;
      return;
    }
    if (!(target instanceof HTMLSelectElement)) return;
    if (target.dataset.control === "region-tile-range") {
      const form = target.closest<HTMLFormElement>('form[data-form="map-edit"], form[data-form="creator-version"]');
      if (!form) return;
      const region = this.creatorRegionFromForm(form);
      const error = form.querySelector<HTMLElement>("[data-region-tile-error]");
      if (!region) {
        if (error) error.hidden = false;
        this.syncEditorRegionActions(form);
        return;
      }
      for (const [name, value] of [["region-x", region.x], ["region-y", region.y], ["region-width", region.width], ["region-height", region.height]] as const) {
        const field = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
        if (field) field.value = String(value);
      }
      this.state.editRegion = region;
      this.state.formError = "";
      this.state.creatorChallengeError = "";
      this.syncEditorRegionActions(form);
    } else if (target.dataset.control === "locale") {
      const locale = target.value === "en-US" ? "en-US" : "tr-TR";
      setLocale(locale);
      this.localeDirty = true;
      this.render();
      if (this.state.profile) void this.saveLocale(locale);
    } else if (target.name === "locale") {
      // Profile locale is saved with the profile form.
    } else if (target.name === "mode") {
      const mode = ActiveModeSchema.safeParse(target.value);
      if (mode.success) {
        if (this.state.screen === "map_edit") {
          if (this.state.busy) return;
          this.state.mapEditMode = mode.data;
          this.state.editRegion = null;
          this.state.formError = "";
          const version = this.state.mapView?.versions.find((item) => item.mode === mode.data);
          if (version) void this.loadAuthorizedPreview(version.id);
          else {
            this.state.previewVersionId = "";
            this.state.previewLoad = "idle";
          }
          this.render();
          return;
        }
        this.state.createMode = mode.data;
        const version = this.state.mapView?.versions.find((item) => item.mode === mode.data);
        const first = this.state.maps.find((map) => map.mode === mode.data);
        this.state.createMapVersionId = version?.id ?? "";
        this.state.createMapHash = version?.artifactHash ?? first?.artifactHash ?? "";
        if (version) void this.loadAuthorizedPreview(version.id);
        this.render();
      }
    } else if (target.name === "map") {
      this.state.createMapHash = target.value;
    } else if (target.name === "mapVersionId") {
      this.state.createMapVersionId = target.value;
      const version = this.state.mapView?.versions.find((item) => item.id === target.value);
      if (version) {
        this.state.createMode = version.mode;
        this.state.createMapHash = version.artifactHash;
        void this.loadAuthorizedPreview(version.id);
      }
    } else if (target.name === "challenge-map") {
      this.state.createChallengeMapHash = target.value;
      this.state.createChallengeOptionId = "";
      this.state.assetLoad = this.state.loadedMap?.artifact.artifactHash === target.value ? "ready" : "idle";
      this.state.formError = "";
      this.render();
    } else if (target.name === "option") {
      this.state.createChallengeOptionId = target.value;
    } else if (target.name === "teamSize") {
      if (target.value === "2" || target.value === "3") this.state.roomTeamSize = Number(target.value) as 2 | 3;
    } else if (target.name === "discover-mode") {
      const mode = target.value === "" ? "" : ActiveModeSchema.safeParse(target.value).success ? target.value as ActiveMode : "";
      this.state.discoverMode = mode;
      this.state.discoverPage = null;
      void this.loadDiscover();
    } else if (target.dataset.control === "preview-version") {
      this.state.previewVersionId = target.value;
      this.state.editRegion = null;
      const version = this.state.mapView?.versions.find((item) => item.id === target.value);
      if (this.state.screen === "map_edit" && version) this.state.mapEditMode = version.mode;
    } else if (target.dataset.control === "creator-version") {
      if (this.state.busy) return;
      this.state.creatorVersionId = target.value;
      this.state.creatorChallengeRankings = [];
      this.state.creatorRankingsLoad = "idle";
      this.state.creatorRankingsError = "";
      this.state.challengeReplay = null;
      this.state.replayLoad = "idle";
      this.state.replayError = "";
      this.replayRequest++;
      this.gameSession?.setReplay(null);
      const version = this.state.creatorChallenge?.versions.find((item) => item.id === target.value);
      if (version) {
        const challengeId = this.state.creatorChallengeId;
        void this.loadAuthorizedPreview(version.id).then(() => {
          if (this.state.creatorChallenge?.id === challengeId && this.state.creatorVersionId === version.id) {
            void this.loadCreatorRankings(challengeId, version.id, version.mode === "browser_boss" ? this.state.creatorPlayerCount : 1);
          }
        });
      }
    } else if (target.dataset.control === "creator-player-count") {
      const count = Number(target.value);
      if (count !== 1 && count !== 2 && count !== 3 && count !== 4) return;
      this.state.creatorPlayerCount = count;
      const challenge = this.state.creatorChallenge;
      const version = challenge?.versions.find((item) => item.id === this.state.creatorVersionId);
      if (challenge && version?.mode === "browser_boss") void this.loadCreatorRankings(challenge.id, version.id, count);
    } else if (target.dataset.control === "host-mode") {
      const mode = ActiveModeSchema.safeParse(target.value);
      if (!mode.success) return;
      if (!this.ownPlayerId || this.state.snapshot?.hostId !== this.ownPlayerId) return;
      const version = this.state.mapView?.versions.find((item) => item.mode === mode.data && item.protocolVersion === PROTOCOL_VERSION && Boolean(item.rulesHash));
      if (version) this.multiplayer.currentConnection?.send("host_select", { mode: version.mode, artifactHash: version.artifactHash, mapVersionId: version.id, challengeVersionId: version.challengeVersionId ?? undefined });
    } else if (target.dataset.control === "host-map") {
      const version = this.state.mapView?.versions.find((item) => item.id === target.value && item.protocolVersion === PROTOCOL_VERSION && Boolean(item.rulesHash));
      if (!version || !this.ownPlayerId || this.state.snapshot?.hostId !== this.ownPlayerId) return;
      this.multiplayer.currentConnection?.send("host_select", { mode: version.mode, artifactHash: version.artifactHash, mapVersionId: version.id, challengeVersionId: version.challengeVersionId ?? undefined });
    }
  }

  private async saveLocale(locale: "tr-TR" | "en-US"): Promise<void> {
    try {
      this.state.profile = await this.productApi.updateProfile({ locale });
      this.state.profileError = "";
      this.localeDirty = false;
      this.announce(tr.profileSaved);
    } catch (error) {
      this.state.profileError = this.productErrorText(error);
      this.announce(this.state.profileError);
    }
  }

  private handleInput(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.name === "url") {
      this.state.urlDraft = target.value;
      return;
    }
    if (target instanceof HTMLInputElement && target.name === "roomCode") {
      const start = target.selectionStart;
      const end = target.selectionEnd;
      target.value = target.value.toUpperCase().replace(/[^ABCDEFGHJKLMNPQRSTUVWXYZ23456789]/g, "").slice(0, 8);
      if (start !== null && end !== null) target.setSelectionRange(start, end);
      this.state.roomCode = target.value;
      return;
    }
    if (target instanceof HTMLInputElement && target.name.startsWith("region-")) {
      const form = target.closest<HTMLFormElement>('form[data-form="map-edit"], form[data-form="creator-version"]');
      if (form) this.syncEditorRegionActions(form);
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      if (this.state.screen === "settings" || this.state.screen === "history" || this.state.screen === "create" || this.state.screen === "join" || this.state.screen === "maps" || this.state.screen === "challenge_create" || this.state.screen === "challenge") {
        this.state.screen = "home"; this.state.pendingChallengeId = ""; this.state.formError = ""; this.clearQuery(); this.render();
      }
    }
  }

  private handleGameAction(action: string): void {
    if (action === "jump" || action === "dash") this.gameSession?.setControl(action, true);
  }

  private togglePreference(key: "soundEnabled" | "reducedMotion"): void {
    this.preferences = { ...this.preferences, [key]: !this.preferences[key] };
    writePreferences(this.preferences);
    this.sound.setPreferences(this.preferences);
    this.gameSession?.setReducedMotion(this.preferences.reducedMotion);
    if (this.state.screen === "game") {
      const button = this.root.querySelector<HTMLButtonElement>('[data-action="toggle-sound"]');
      if (button) {
        button.innerHTML = icon(this.preferences.soundEnabled ? "sound" : "soundOff");
        button.setAttribute("aria-label", this.preferences.soundEnabled ? tr.soundMute : tr.soundEnable);
        button.title = this.preferences.soundEnabled ? tr.soundMute : tr.soundEnable;
      }
      return;
    }
    this.render();
  }

  private setVisualQuality(quality: VisualQuality): void {
    this.preferences = { ...this.preferences, quality };
    writePreferences(this.preferences);
    this.gameSession?.setVisualQuality(quality);
    this.render();
  }

  private async copyInvite(): Promise<void> {
    if (!this.state.inviteToken) { this.state.notice = tr.inviteNotReady; this.patchLobby(); return; }
    const url = new URL(`/room/${encodeURIComponent(this.state.inviteToken)}`, window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(url.toString());
      this.state.notice = tr.copied;
      this.sound.play("click");
    } catch {
      this.state.notice = tr.copyFailed;
    }
    this.patchLobby();
    if (this.state.notice === tr.copyFailed) {
      const input = this.root.querySelector<HTMLInputElement>("#lobby-invite-url");
      input?.focus();
      input?.select();
    }
  }

  private async copyChallenge(): Promise<void> {
    const id = this.state.challenge?.id;
    if (!id) return;
    const url = new URL(window.location.href);
    url.search = "";
    url.searchParams.set("challenge", id);
    try {
      await navigator.clipboard.writeText(url.toString());
      this.state.notice = tr.challengeShareCopied;
      this.sound.play("click");
    } catch {
      this.state.notice = tr.challengeCopiedFallback;
    }
    this.announce(this.state.notice);
    this.render();
  }

  private updateChallengeUrl(id: string): void {
    const url = new URL(window.location.href);
    url.search = "";
    url.searchParams.set("challenge", id);
    window.history.replaceState(null, "", url);
  }

  private updateUrl(code: string): void {
    const url = new URL(window.location.href);
    url.pathname = `/play/${encodeURIComponent(code)}`;
    url.search = "";
    window.history.replaceState(null, "", url);
  }

  private updatePlayUrl(roomId: string): void {
    if (!roomId) return;
    const url = new URL(window.location.href);
    url.pathname = `/play/${encodeURIComponent(roomId)}`;
    url.search = "";
    window.history.replaceState(null, "", url);
  }

  private clearQuery(): void {
    const url = new URL(window.location.href);
    url.search = "";
    window.history.replaceState(null, "", url);
  }

  private toggleReady(): void {
    const connection = this.multiplayer.currentConnection;
    const snapshot = this.state.snapshot;
    if (!connection || !snapshot || snapshot.phase !== "lobby" || this.state.connectionLost || this.state.assetLoad !== "ready" ||
      this.state.loadedMap?.artifact.artifactHash !== snapshot.artifactHash || (snapshot.rulesHash && this.state.loadedMap.artifact.rulesHash !== snapshot.rulesHash)) return;
    const player = snapshot.players.find((row) => row.id === this.ownPlayerId);
    if (!player?.connected) return;
    connection.send("client_ready", { ready: !player.ready, artifactHash: snapshot.artifactHash, ...(snapshot.rulesHash ? { rulesHash: snapshot.rulesHash } : {}), protocolVersion: PROTOCOL_VERSION });
    this.sound.play("ready");
  }

  private startMultiplayerMatch(): void {
    const connection = this.multiplayer.currentConnection;
    const snapshot = this.state.snapshot;
    if (!connection || !snapshot || !this.ownPlayerId || snapshot.hostId !== this.ownPlayerId || this.state.assetLoad !== "ready") return;
    const limits = snapshot.playerLimits ?? MODE_LIMITS[snapshot.mode];
    if (snapshot.players.length < limits.min || snapshot.players.length > limits.max || snapshot.players.some((player) => !player.ready || !player.connected)) return;
    connection.send("host_start", {});
  }

  private requestRematch(): void {
    const connection = this.multiplayer.currentConnection;
    const snapshot = this.state.snapshot;
    if (!connection || this.state.screen !== "match_result" || snapshot?.phase !== "match_result" ||
      this.state.connectionLost || !snapshot.players.some((player) => player.id === this.ownPlayerId && player.connected)) return;
    connection.send("request_rematch", {});
    this.state.notice = tr.rematchRequested;
    this.state.screen = "lobby";
    this.render();
  }

  private leaveRoom(): Promise<void> {
    this.clearGameActionFeedback();
    this.roomRequestId++;
    this.roomRequestAbort?.abort();
    this.roomRequestAbort = null;
    this.mapLoadRequest++;
    this.mapLoadAbort?.abort();
    this.mapLoadAbort = null;
    this.roomAssetAbort?.abort();
    this.roomAssetAbort = null;
    this.privateAssetAbort?.abort();
    this.privateAssetAbort = null;
    this.privateAssetGeneration++;
    this.state.busy = false;
    this.assetLoadKey = "";
    this.readySentHash = "";
    this.practiceReadyKey = "";
    this.practiceStartKey = "";
    const leaving = this.multiplayer.dispose();
    this.state.snapshot = null;
    this.state.latestScene = null;
    this.state.roundResult = null;
    this.state.matchResult = null;
    this.state.roomCode = "";
    this.state.inviteToken = "";
    this.ownPlayerId = "";
    this.state.connectionLost = false;
    this.state.reconnectMessage = "";
    this.state.formError = "";
    this.state.notice = "";
    this.state.practiceMode = null;
    this.state.practiceRetryHash = "";
    this.state.practiceRetryVersionId = "";
    this.clearHints();
    const url = new URL(window.location.href);
    url.searchParams.delete("room"); url.searchParams.delete("code");
    if (url.pathname.startsWith("/play/")) url.pathname = "/";
    window.history.replaceState(null, "", url);
    return leaving;
  }

  private leaveGame(): void {
    if (this.state.snapshot) {
      this.leaveRoomToSourceOrHome();
      return;
    }
    this.gameSession?.destroy();
    this.gameSession = null;
    const sourceMap = this.state.mapView;
    const sourceVersion = sourceMap?.versions.find((version) => version.id === this.state.localVersionId);
    const sourcePath = sourceMap && window.location.pathname === `/maps/${encodeURIComponent(sourceMap.id)}`;
    if (sourceMap && sourceVersion?.mapId === sourceMap.id && sourcePath) {
      this.state.selectedMode = sourceVersion.mode;
      this.state.previewVersionId = sourceVersion.id;
      this.state.previewLoad = this.state.privatePreview?.artifact.artifactHash === sourceVersion.artifactHash ? "ready" : "idle";
      this.state.previewError = "";
      this.state.localVersionId = "";
      this.state.screen = "map_detail";
    } else {
      this.state.screen = "maps";
    }
    this.render();
  }

  private leaveRoomToSourceOrHome(): void {
    const sourceTarget = this.roomSourceReturnTarget();

    void this.leaveRoom();
    if (sourceTarget) {
      const { map, version } = sourceTarget;
      this.state.selectedMode = version.mode;
      this.state.previewVersionId = version.id;
      this.state.previewLoad = this.state.privatePreview?.artifact.artifactHash === version.artifactHash ? "ready" : "idle";
      this.state.previewError = "";
      this.state.localVersionId = "";
      this.state.screen = "map_detail";
      window.history.replaceState(null, "", `/maps/${encodeURIComponent(map.id)}`);
    } else {
      this.state.screen = "home";
    }
    this.render();
  }

  private roomSourceReturnTarget(): { map: MapView; version: MapVersionView } | null {
    const snapshot = this.state.snapshot;
    const map = this.state.mapView;
    const selfId = this.ownPlayerId || snapshot?.selfId || "";
    if (!snapshot || !selfId || snapshot.hostId !== selfId || !snapshot.mapVersionId || map?.id !== snapshot.mapId ||
      snapshot.challengeVersionId || snapshot.creatorChallengeId || snapshot.challengeId) return null;
    const version = map.versions.find((candidate) => candidate.id === snapshot.mapVersionId &&
      candidate.artifactHash === snapshot.artifactHash && candidate.mode === snapshot.mode &&
      candidate.rulesHash === snapshot.rulesHash && candidate.protocolVersion === PROTOCOL_VERSION);
    return version ? { map, version } : null;
  }

  private clearHistory(): void {
    if (!window.confirm(tr.historyClearConfirm)) return;
    clearLocalScores();
    this.announce(tr.historyCleared);
    this.render();
  }
}
